// src/components/PortfolioView.js

import React, { useEffect, useState } from "react";
import {
  getPortfolioAnalytics,
  getPortfolioExposure,
  getPortfolioHistory,
  getPortfolioRiskChecks,
  getRuntimeMaintenanceStatus,
  runRuntimeMaintenance,
  getBrokerSyncStatus,
  getWhatIfRunHistory,
  getWhatIfPresets,
  getWhatIfCompareHistory,
  getWhatIfCompareHistoryPositions,
  patchWhatIfCompareHistoryNote,
  getWhatIfCompareFilterPresets,
  getWhatIfCompareFilterPresetUsage,
  getWhatIfCompareFilterPresetUsageSettings,
  getWhatIfCompareSortPresets,
  getWhatIfCompareViewPresets,
  getWhatIfCompareExportProfiles,
  exportWhatIfCompareExportProfiles,
  importWhatIfCompareExportProfiles,
  upsertWhatIfCompareFilterPreset,
  resetWhatIfCompareFilterPresetUsage,
  saveWhatIfCompareFilterPresetUsageSettings,
  trackWhatIfCompareFilterPresetUsage,
  deleteWhatIfCompareFilterPreset,
  deleteWhatIfCompareFilterPresetsUnused,
  deleteWhatIfCompareFilterPresetsUnusedSince,
  getWhatIfCompareFilterPresetArchive,
  restoreWhatIfCompareFilterPreset,
  purgeWhatIfCompareFilterPresetArchive,
  exportWhatIfCompareFilterPresets,
  getWhatIfCompareFilterExportProfiles,
  exportWhatIfCompareFilterExportProfiles,
  importWhatIfCompareFilterExportProfiles,
  getWhatIfCompareFilterExportProfileArchive,
  upsertWhatIfCompareFilterExportProfile,
  deleteWhatIfCompareFilterExportProfile,
  cloneWhatIfCompareFilterExportProfile,
  renameWhatIfCompareFilterExportProfile,
  restoreWhatIfCompareFilterExportProfile,
  purgeWhatIfCompareFilterExportProfileArchive,
  importWhatIfCompareFilterPresets,
  bulkTagWhatIfCompareFilterPresets,
  cloneWhatIfCompareFilterPreset,
  renameWhatIfCompareFilterPreset,
  getWhatIfCompareFilterPresetTags,
  upsertWhatIfCompareSortPreset,
  deleteWhatIfCompareSortPreset,
  cloneWhatIfCompareSortPreset,
  renameWhatIfCompareSortPreset,
  getWhatIfCompareSortPresetArchive,
  restoreWhatIfCompareSortPreset,
  purgeWhatIfCompareSortPresetArchive,
  upsertWhatIfCompareViewPreset,
  deleteWhatIfCompareViewPreset,
  cloneWhatIfCompareViewPreset,
  renameWhatIfCompareViewPreset,
  getWhatIfCompareViewPresetArchive,
  restoreWhatIfCompareViewPreset,
  purgeWhatIfCompareViewPresetArchive,
  exportWhatIfCompareSortPresets,
  importWhatIfCompareSortPresets,
  exportWhatIfCompareViewPresets,
  importWhatIfCompareViewPresets,
  getWhatIfCompareSortPresetUsage,
  trackWhatIfCompareSortPresetUsage,
  getWhatIfCompareSortPresetUsageSettings,
  saveWhatIfCompareSortPresetUsageSettings,
  resetWhatIfCompareSortPresetUsage,
  getWhatIfCompareViewPresetUsage,
  trackWhatIfCompareViewPresetUsage,
  getWhatIfCompareViewPresetUsageSettings,
  saveWhatIfCompareViewPresetUsageSettings,
  resetWhatIfCompareViewPresetUsage,
  upsertWhatIfCompareExportProfile,
  deleteWhatIfCompareExportProfile,
  cloneWhatIfCompareExportProfile,
  renameWhatIfCompareExportProfile,
  getWhatIfCompareExportProfileArchive,
  restoreWhatIfCompareExportProfile,
  purgeWhatIfCompareExportProfileArchive,
  appendWhatIfRunHistory,
  appendWhatIfCompareHistory,
  compareWhatIfScenarios,
  exportWhatIfCompareHistoryCsv,
  exportWhatIfRunHistoryCsv,
  saveWhatIfPresets,
  simulatePortfolioTrade,
  syncBrokerHistory,
  snapshotReadinessGate,
  getReadinessGateTrend,
  getReadinessActions,
  sendReadinessTestAlert,
  getPortfolioAnomalySignals,
  getPortfolioConcentration,
  getPortfolioPL,
  backfillPortfolioHistory,
  exportPortfolioHistoryCsv,
} from "../api/portfolio";
import { getBrokerSyncPolicy } from "../api/settings";
import { useReadiness } from "../context/ReadinessContext";
import { useCapabilities } from "../context/CapabilitiesContext";
import { FeatureGate } from "./FeatureGate";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import ResponsiveContainer from "./ResponsiveContainer";
import SymbolHoverChart from "./SymbolHoverChart";

function fmt(value, digits = 2) {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function PLChip({ value }) {
  const isPositive = value >= 0;
  return (
    <Chip
      label={`${isPositive ? "+" : ""}${fmt(value)}`}
      color={isPositive ? "success" : "error"}
      size="small"
      variant="outlined"
    />
  );
}

function KPICard({ label, value, sub, highlight }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 140,
        flex: 1,
        borderLeft: highlight ? `4px solid ${highlight}` : undefined,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function EquitySparkline({ data }) {
  if (!data || data.length < 2) return null;
  const W = 600;
  const H = 120;
  const PAD = { top: 10, right: 10, bottom: 28, left: 68 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const equities = data.map((d) => d.equity);
  const minY = Math.min(...equities);
  const maxY = Math.max(...equities);
  const rangeY = maxY - minY || 1;
  const toX = (i) => PAD.left + (i / (data.length - 1)) * innerW;
  const toY = (v) => PAD.top + innerH - ((v - minY) / rangeY) * innerH;
  const pts = data.map((d, i) => `${toX(i)},${toY(d.equity)}`).join(" ");
  const fill = `${PAD.left},${PAD.top + innerH} ${pts} ${toX(data.length - 1)},${PAD.top + innerH}`;
  const isUp = equities[equities.length - 1] >= equities[0];
  const color = isUp ? "#2e7d32" : "#c62828";
  const xIdxs = [0, Math.floor((data.length - 1) / 2), data.length - 1].filter(
    (v, i, a) => a.indexOf(v) === i
  );
  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
        <polygon points={fill} fill={color} fillOpacity={0.1} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        <line x1={PAD.left} y1={PAD.top + innerH} x2={W - PAD.right} y2={PAD.top + innerH} stroke="#ccc" strokeWidth={1} />
        {xIdxs.map((idx) => (
          <text key={idx} x={toX(idx)} y={H - 6} textAnchor="middle" fontSize={9} fill="#999">
            {data[idx].date}
          </text>
        ))}
        <text x={PAD.left - 5} y={PAD.top + innerH} textAnchor="end" fontSize={9} fill="#999">
          ${fmt(minY, 0)}
        </text>
        <text x={PAD.left - 5} y={PAD.top + 10} textAnchor="end" fontSize={9} fill="#999">
          ${fmt(maxY, 0)}
        </text>
      </svg>
    </Box>
  );
}

function normalizeBrokerSyncDetails(sync) {
  const details = sync && typeof sync === "object" ? sync : {};
  const equity = details.equity && typeof details.equity === "object" ? details.equity : {};
  const trades = details.trades && typeof details.trades === "object" ? details.trades : {};
  return {
    synced: Boolean(details.synced),
    asOf: details.asOf || details.as_of || null,
    equityPoints: Number(details.equityPoints ?? equity.points ?? 0),
    tradeRows: Number(details.tradeRows ?? trades.trades ?? 0),
    equityReason: details.equityReason || equity.reason || "unknown",
    tradeReason: details.tradeReason || trades.reason || "unknown",
    equity,
    trades,
  };
}

function formatSyncAge(asOfIso) {
  if (!asOfIso) {
    return "not available";
  }
  const asOfMs = Date.parse(asOfIso);
  if (!Number.isFinite(asOfMs)) {
    return "invalid timestamp";
  }
  const nowMs = Date.now();
  const diffMinutes = Math.floor((nowMs - asOfMs) / 60000);
  if (diffMinutes < 0) {
    return "in the future";
  }
  if (diffMinutes < 1) {
    return "just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hr ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day ago${diffDays === 1 ? "" : "s"}`;
}

const PortfolioView = () => {
  const { gate, history: readinessHistoryCtx, alerts: readinessAlertsCtx, refresh: refreshReadiness } = useReadiness();
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [exposure, setExposure] = useState(null);
  const [historySummary, setHistorySummary] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [concentration, setConcentration] = useState(null);
  const [portfolioPL, setPortfolioPL] = useState(null);
  const [readinessGate, setReadinessGate] = useState(null);
  const [readinessTrend, setReadinessTrend] = useState(null);
  const [readinessHistory, setReadinessHistory] = useState(null);
  const [portfolioAnomalySignals, setPortfolioAnomalySignals] = useState([]);
  const [readinessAlerts, setReadinessAlerts] = useState([]);
  const [readinessActions, setReadinessActions] = useState([]);
  const [readinessSnapshotLoading, setReadinessSnapshotLoading] = useState(false);
  const [readinessSnapshotMessage, setReadinessSnapshotMessage] = useState("");
  const [readinessAlertTestLoading, setReadinessAlertTestLoading] = useState(false);
  const [readinessAlertTestMessage, setReadinessAlertTestMessage] = useState("");
  const [brokerSyncLoading, setBrokerSyncLoading] = useState(false);
  const [brokerSyncMessage, setBrokerSyncMessage] = useState("");
  const [maintenanceRunLoading, setMaintenanceRunLoading] = useState(false);
  const [maintenanceRunMessage, setMaintenanceRunMessage] = useState("");
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState("");
  const [brokerSyncDetails, setBrokerSyncDetails] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [syncDays, setSyncDays] = useState(90);
  const [simSymbol, setSimSymbol] = useState("NVDA");
  const [simAction, setSimAction] = useState("buy");
  const [simQuantity, setSimQuantity] = useState(1);
  const [simPrice, setSimPrice] = useState(100);
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simPresets, setSimPresets] = useState([]);
  const [simPresetName, setSimPresetName] = useState("");
  const [simSelectedPreset, setSimSelectedPreset] = useState("");
  const [simComparePresetA, setSimComparePresetA] = useState("");
  const [simComparePresetB, setSimComparePresetB] = useState("");
  const [simCompareLoading, setSimCompareLoading] = useState(false);
  const [simCompareResult, setSimCompareResult] = useState(null);
  const [simCompareHistory, setSimCompareHistory] = useState([]);
  const [simCompareHistoryAppliedFilters, setSimCompareHistoryAppliedFilters] = useState({});
  const [simCompareHistoryCounts, setSimCompareHistoryCounts] = useState({
    totalCount: 0,
    filteredCount: 0,
    count: 0,
  });
  const [simCompareHistoryPresetAFilter, setSimCompareHistoryPresetAFilter] = useState("");
  const [simCompareHistoryPresetBFilter, setSimCompareHistoryPresetBFilter] = useState("");
  const [simCompareHistoryNoteFilter, setSimCompareHistoryNoteFilter] = useState("");
  const [simCompareHistoryMinDeltaMvFilter, setSimCompareHistoryMinDeltaMvFilter] = useState("");
  const [simCompareHistoryMinDeltaPlFilter, setSimCompareHistoryMinDeltaPlFilter] = useState("");
  const [simCompareHistoryFromAsOfFilter, setSimCompareHistoryFromAsOfFilter] = useState("");
  const [simCompareHistoryToAsOfFilter, setSimCompareHistoryToAsOfFilter] = useState("");
  const [simCompareHistoryQuickRange, setSimCompareHistoryQuickRange] = useState("custom");
  const [simCompareHistorySortBy, setSimCompareHistorySortBy] = useState("as_of");
  const [simCompareHistorySortOrder, setSimCompareHistorySortOrder] = useState("desc");
  const [simCompareHistoryExportMode, setSimCompareHistoryExportMode] = useState("all");
  const [simCompareExportProfiles, setSimCompareExportProfiles] = useState([]);
  const [simCompareExportProfileName, setSimCompareExportProfileName] = useState("");
  const [simSelectedCompareExportProfile, setSimSelectedCompareExportProfile] = useState("");
  const [simCompareExportProfileArchive, setSimCompareExportProfileArchive] = useState([]);
  const [simCompareExportProfilePurgeDeletedBefore, setSimCompareExportProfilePurgeDeletedBefore] = useState("");
  const [simCompareFilterPresets, setSimCompareFilterPresets] = useState([]);
  const [simCompareFilterPresetUsage, setSimCompareFilterPresetUsage] = useState({});
  const [simCompareFilterPresetUsageRows, setSimCompareFilterPresetUsageRows] = useState([]);
  const [simCompareFilterPresetUsageViewMode, setSimCompareFilterPresetUsageViewMode] = useState("most_used");
  const [simCompareFilterPresetUsageEnabled, setSimCompareFilterPresetUsageEnabled] = useState(true);
  const [simCompareFilterPresetUsageVisible, setSimCompareFilterPresetUsageVisible] = useState(true);
  const [simCompareFilterPresetName, setSimCompareFilterPresetName] = useState("");
  const [simSelectedCompareFilterPreset, setSimSelectedCompareFilterPreset] = useState("");
  const [simCompareFilterUnusedSince, setSimCompareFilterUnusedSince] = useState("");
  const [simCompareFilterPresetArchive, setSimCompareFilterPresetArchive] = useState([]);
  const [simCompareFilterTags, setSimCompareFilterTags] = useState([]);
  const [simCompareTagFilter, setSimCompareTagFilter] = useState("");
  const [simCompareFilterPresetTagInput, setSimCompareFilterPresetTagInput] = useState("");
  const [simCompareFilterExportScope, setSimCompareFilterExportScope] = useState("all");
  const [simCompareFilterExportProfiles, setSimCompareFilterExportProfiles] = useState([]);
  const [simCompareFilterExportProfileArchive, setSimCompareFilterExportProfileArchive] = useState([]);
  const [simCompareFilterExportArchiveDeletedBefore, setSimCompareFilterExportArchiveDeletedBefore] = useState("");
  const [simCompareFilterExportProfileName, setSimCompareFilterExportProfileName] = useState("");
  const [simSelectedCompareFilterExportProfile, setSimSelectedCompareFilterExportProfile] = useState("");
  const [simCompareImportTag, setSimCompareImportTag] = useState("");
  const [simCompareImportTagMode, setSimCompareImportTagMode] = useState("append");
  const [simCompareImportMode, setSimCompareImportMode] = useState("merge");
  const [simCompareStrictImport, setSimCompareStrictImport] = useState(false);
  const [simCompareImportValidationSummary, setSimCompareImportValidationSummary] = useState(null);
  const [simCompareDryRunImport, setSimCompareDryRunImport] = useState(false);
  const [simCompareDryRunResult, setSimCompareDryRunResult] = useState(null);
  const [simCompareImportErrors, setSimCompareImportErrors] = useState([]);
  const [simCompareBulkTagAction, setSimCompareBulkTagAction] = useState("add");
  const [simCompareBulkTagValue, setSimCompareBulkTagValue] = useState("");
  const [simCompareBulkTagReplaceWith, setSimCompareBulkTagReplaceWith] = useState("");

  useEffect(() => {
    setReadinessGate(gate || null);
  }, [gate]);

  useEffect(() => {
    setReadinessHistory({
      status: "ok",
      history: Array.isArray(readinessHistoryCtx) ? readinessHistoryCtx : [],
    });
  }, [readinessHistoryCtx]);

  useEffect(() => {
    setReadinessAlerts(Array.isArray(readinessAlertsCtx) ? readinessAlertsCtx : []);
  }, [readinessAlertsCtx]);
  const [simCompareSortPresets, setSimCompareSortPresets] = useState([]);
  const [simCompareSortPresetName, setSimCompareSortPresetName] = useState("");
  const [simCompareSortPresetArchive, setSimCompareSortPresetArchive] = useState([]);
  const [simCompareSortPresetPurgeDeletedBefore, setSimCompareSortPresetPurgeDeletedBefore] = useState("");
  const [simSelectedCompareSortPreset, setSimSelectedCompareSortPreset] = useState("");
  const [simCompareSortPresetUsageRows, setSimCompareSortPresetUsageRows] = useState([]);
  const [simCompareSortPresetUsageViewMode, setSimCompareSortPresetUsageViewMode] = useState("most_used");
  const [simCompareSortPresetUsageEnabled, setSimCompareSortPresetUsageEnabled] = useState(true);
  const [simCompareSortPresetUsageVisible, setSimCompareSortPresetUsageVisible] = useState(true);
  const [simCompareViewPresets, setSimCompareViewPresets] = useState([]);
  const [simCompareViewPresetName, setSimCompareViewPresetName] = useState("");
  const [simCompareViewPresetArchive, setSimCompareViewPresetArchive] = useState([]);
  const [simCompareViewPresetPurgeDeletedBefore, setSimCompareViewPresetPurgeDeletedBefore] = useState("");
  const [simSelectedCompareViewPreset, setSimSelectedCompareViewPreset] = useState("");
  const [simCompareViewPresetUsageRows, setSimCompareViewPresetUsageRows] = useState([]);
  const [simCompareViewPresetUsageViewMode, setSimCompareViewPresetUsageViewMode] = useState("most_used");
  const [simCompareViewPresetUsageEnabled, setSimCompareViewPresetUsageEnabled] = useState(true);
  const [simCompareViewPresetUsageVisible, setSimCompareViewPresetUsageVisible] = useState(true);
  const [simCompareSortImportMode, setSimCompareSortImportMode] = useState("merge");
  const [simCompareSortDryRunImport, setSimCompareSortDryRunImport] = useState(false);
  const [simCompareSortDryRunResult, setSimCompareSortDryRunResult] = useState(null);
  const [simCompareSortImportErrors, setSimCompareSortImportErrors] = useState([]);
  const [simCompareViewImportMode, setSimCompareViewImportMode] = useState("merge");
  const [simCompareViewDryRunImport, setSimCompareViewDryRunImport] = useState(false);
  const [simCompareViewDryRunResult, setSimCompareViewDryRunResult] = useState(null);
  const [simCompareViewImportErrors, setSimCompareViewImportErrors] = useState([]);
  const [simCompareHistoryPage, setSimCompareHistoryPage] = useState(1);
  const [simCompareHistoryPageSize, setSimCompareHistoryPageSize] = useState(10);
  const [simCompareHistoryTotalPages, setSimCompareHistoryTotalPages] = useState(1);
  const [simCompareHistoryLoading, setSimCompareHistoryLoading] = useState(false);
  const [simCompareFilterApplyBanner, setSimCompareFilterApplyBanner] = useState("");
  const [simCompareHistoryExpandedRows, setSimCompareHistoryExpandedRows] = useState({});
  const [simCompareHistoryPositions, setSimCompareHistoryPositions] = useState({});
  const [simCompareHistoryPositionsLoading, setSimCompareHistoryPositionsLoading] = useState({});
  const [simCompareHistoryNotes, setSimCompareHistoryNotes] = useState({});
  const [simCompareHistoryNoteSaving, setSimCompareHistoryNoteSaving] = useState({});
  const [simPresetMessage, setSimPresetMessage] = useState("");
  const [simRunHistory, setSimRunHistory] = useState([]);
  const [simHistorySymbolFilter, setSimHistorySymbolFilter] = useState("");
  const [simHistoryActionFilter, setSimHistoryActionFilter] = useState("all");
  const [simHistoryLoading, setSimHistoryLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const normalizeCompareFilterPresetUsage = (usageRows) => {
    if (!Array.isArray(usageRows)) {
      return {};
    }
    return usageRows.reduce((acc, row) => {
      const name = String(row?.name || "").trim();
      if (!name) {
        return acc;
      }
      const key = name.toLowerCase();
      acc[key] = {
        name,
        load_count: Number(row?.load_count ?? 0),
        load_apply_count: Number(row?.load_apply_count ?? 0),
        update_count: Number(row?.update_count ?? 0),
        total_count: Number(row?.total_count ?? 0),
        last_used_at: String(row?.last_used_at || ""),
      };
      return acc;
    }, {});
  };

  const parseCompareFilterUsageIsoTimestamp = (value) => {
    const raw = String(value || "").trim();
    if (!raw) {
      return null;
    }
    const timestamp = Date.parse(raw);
    return Number.isNaN(timestamp) ? null : timestamp;
  };

  const sortCompareFilterPresetUsageRowsByRecent = (usageRows) => {
    return [...usageRows].sort((a, b) => {
      const aTs = parseCompareFilterUsageIsoTimestamp(a?.last_used_at);
      const bTs = parseCompareFilterUsageIsoTimestamp(b?.last_used_at);
      const aSortable = aTs === null ? -1 : aTs;
      const bSortable = bTs === null ? -1 : bTs;
      if (aSortable !== bSortable) {
        return bSortable - aSortable;
      }
      const aTotal = Number(a?.total_count ?? 0);
      const bTotal = Number(b?.total_count ?? 0);
      if (aTotal !== bTotal) {
        return bTotal - aTotal;
      }
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  };

  const buildCompareFilterPresetUsageRows = (usageRows, presets, viewMode) => {
    const normalizedRows = Array.isArray(usageRows) ? usageRows : [];
    const normalizedPresets = Array.isArray(presets) ? presets : [];
    const mode = String(viewMode || "most_used").trim().toLowerCase();
    const byName = {};

    normalizedRows.forEach((row) => {
      const name = String(row?.name || "").trim();
      if (!name) {
        return;
      }
      byName[name.toLowerCase()] = {
        name,
        load_count: Number(row?.load_count ?? 0),
        load_apply_count: Number(row?.load_apply_count ?? 0),
        update_count: Number(row?.update_count ?? 0),
        total_count: Number(row?.total_count ?? 0),
        last_used_at: String(row?.last_used_at || ""),
      };
    });

    if (mode !== "never_used") {
      normalizedPresets.forEach((preset) => {
        const name = String(preset?.name || "").trim();
        if (!name) {
          return;
        }
        const key = name.toLowerCase();
        if (!byName[key]) {
          byName[key] = {
            name,
            load_count: 0,
            load_apply_count: 0,
            update_count: 0,
            total_count: 0,
            last_used_at: "",
          };
        }
      });
    }

    let rows = Object.values(byName);
    if (mode === "never_used") {
      return rows
        .filter((row) => Number(row?.total_count ?? 0) === 0)
        .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    }
    if (mode === "recently_used") {
      return sortCompareFilterPresetUsageRowsByRecent(rows);
    }
    return rows.sort((a, b) => {
      const aTotal = Number(a?.total_count ?? 0);
      const bTotal = Number(b?.total_count ?? 0);
      if (aTotal !== bTotal) {
        return bTotal - aTotal;
      }
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  };

  const summarizeCompareFilterUsage = () => {
    const rows = buildCompareFilterPresetUsageRows(
      simCompareFilterPresetUsageRows,
      simCompareFilterPresets,
      "most_used"
    );
    const topUsed = rows.find((row) => Number(row?.total_count ?? 0) > 0);
    const unusedCount = rows.filter((row) => Number(row?.total_count ?? 0) === 0).length;
    if (!topUsed && unusedCount === 0) {
      return "Usage insights unavailable.";
    }
    const topUsedText = topUsed
      ? `Top used: ${topUsed.name} (${Number(topUsed.total_count)})`
      : "Top used: none";
    return `${topUsedText} | Unused presets: ${unusedCount}`;
  };

  const countNeverUsedCompareFilterPresets = (usageRows, presets) => {
    const rows = buildCompareFilterPresetUsageRows(usageRows, presets, "most_used");
    return rows.filter((row) => Number(row?.total_count ?? 0) === 0).length;
  };

  const countCompareFilterPresetsUnusedSince = (usageRows, presets, cutoffIso) => {
    const cutoffTimestamp = parseCompareFilterUsageIsoTimestamp(cutoffIso);
    if (cutoffTimestamp === null) {
      return null;
    }
    const rows = buildCompareFilterPresetUsageRows(usageRows, presets, "most_used");
    return rows.filter((row) => {
      const totalCount = Number(row?.total_count ?? 0);
      if (totalCount <= 0) {
        return true;
      }
      const lastUsedTimestamp = parseCompareFilterUsageIsoTimestamp(row?.last_used_at);
      return lastUsedTimestamp === null || lastUsedTimestamp <= cutoffTimestamp;
    }).length;
  };

  const buildCompareSortViewPresetUsageRows = (usageRows, presets, viewMode, parseIsoTimestamp) => {
    const normalizedRows = Array.isArray(usageRows) ? usageRows : [];
    const normalizedPresets = Array.isArray(presets) ? presets : [];
    const mode = String(viewMode || "most_used").trim().toLowerCase();
    const byName = {};
    normalizedRows.forEach((row) => {
      const name = String(row?.name || "").trim();
      if (!name) return;
      byName[name.toLowerCase()] = {
        name,
        load_count: Number(row?.load_count ?? 0),
        apply_count: Number(row?.apply_count ?? 0),
        update_count: Number(row?.update_count ?? 0),
        total_count: Number(row?.total_count ?? 0),
        last_used_at: String(row?.last_used_at || ""),
      };
    });
    if (mode !== "never_used") {
      normalizedPresets.forEach((preset) => {
        const name = String(preset?.name || "").trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (!byName[key]) {
          byName[key] = { name, load_count: 0, apply_count: 0, update_count: 0, total_count: 0, last_used_at: "" };
        }
      });
    }
    let rows = Object.values(byName);
    if (mode === "never_used") {
      return rows
        .filter((row) => Number(row?.total_count ?? 0) === 0)
        .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    }
    if (mode === "recently_used") {
      return [...rows].sort((a, b) => {
        const aTs = parseIsoTimestamp(a?.last_used_at) ?? -1;
        const bTs = parseIsoTimestamp(b?.last_used_at) ?? -1;
        if (aTs !== bTs) return bTs - aTs;
        return Number(b?.total_count ?? 0) - Number(a?.total_count ?? 0);
      });
    }
    return rows.sort((a, b) => {
      const aTotal = Number(a?.total_count ?? 0);
      const bTotal = Number(b?.total_count ?? 0);
      if (aTotal !== bTotal) return bTotal - aTotal;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  };

  const buildCompareSortPresetUsageRows = (usageRows, presets, viewMode) =>
    buildCompareSortViewPresetUsageRows(
      usageRows,
      presets,
      viewMode,
      parseCompareFilterUsageIsoTimestamp
    );

  const buildCompareViewPresetUsageRows = (usageRows, presets, viewMode) =>
    buildCompareSortViewPresetUsageRows(
      usageRows,
      presets,
      viewMode,
      parseCompareFilterUsageIsoTimestamp
    );

  const deriveCompareFilterTagsFromPresets = (presets) => {
    const tags = new Set();
    (Array.isArray(presets) ? presets : []).forEach((preset) => {
      (preset?.tags || []).forEach((tag) => {
        const normalized = String(tag || "").trim().toLowerCase();
        if (normalized) {
          tags.add(normalized);
        }
      });
    });
    return [...tags].sort((a, b) => a.localeCompare(b));
  };

  const buildCompareImportValidationSummary = (rawPresets) => {
    const presets = Array.isArray(rawPresets) ? rawPresets : [];
    let tagErrorCount = 0;
    let tooLongTagCount = 0;
    let blankTagCount = 0;
    let duplicateTagCount = 0;
    let tooManyTagsCount = 0;

    presets.forEach((preset) => {
      const tags = Array.isArray(preset?.tags) ? preset.tags : [];
      const seen = new Set();
      let validTagCount = 0;

      tags.forEach((tag) => {
        const normalized = String(tag || "").trim().toLowerCase();
        if (!normalized) {
          tagErrorCount += 1;
          blankTagCount += 1;
          return;
        }
        if (normalized.length > 40) {
          tagErrorCount += 1;
          tooLongTagCount += 1;
          return;
        }
        if (seen.has(normalized)) {
          tagErrorCount += 1;
          duplicateTagCount += 1;
          return;
        }
        seen.add(normalized);
        validTagCount += 1;
      });

      if (validTagCount > 10) {
        tagErrorCount += 1;
        tooManyTagsCount += 1;
      }
    });

    return {
      presetCount: presets.length,
      tagErrorCount,
      tooLongTagCount,
      blankTagCount,
      duplicateTagCount,
      tooManyTagsCount,
    };
  };

  const getCompareFilterPresetsForUsageScope = (presets = simCompareFilterPresets) => {
    const normalizedPresets = Array.isArray(presets) ? presets : [];
    if (!simCompareTagFilter) {
      return normalizedPresets;
    }
    return normalizedPresets.filter((preset) => (preset?.tags || []).includes(simCompareTagFilter));
  };

  const refreshCompareFilterUsage = async (viewMode = simCompareFilterPresetUsageViewMode) => {
    const usageRows = await getWhatIfCompareFilterPresetUsage({
      includeZeroUsage: true,
      viewMode,
      tag: simCompareTagFilter || undefined,
    });
    const latestUsageRows = Array.isArray(usageRows) ? usageRows : [];
    const scopedPresets = getCompareFilterPresetsForUsageScope();
    setSimCompareFilterPresetUsage(normalizeCompareFilterPresetUsage(latestUsageRows));
    setSimCompareFilterPresetUsageRows(
      buildCompareFilterPresetUsageRows(
        latestUsageRows,
        scopedPresets,
        viewMode
      )
    );
    return latestUsageRows;
  };

  const applyCompareFilterPresetMaintenanceDeleteResponse = (response, fallbackPresets = simCompareFilterPresets) => {
    const nextPresets = Array.isArray(response?.presets) ? response.presets : fallbackPresets;
    setSimCompareFilterPresets(nextPresets);
    const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
    setSimCompareFilterTags(nextTags);
    if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
      setSimCompareTagFilter("");
    }

    if (
      simSelectedCompareFilterPreset
      && !nextPresets.some(
        (preset) => String(preset?.name || "").trim() === simSelectedCompareFilterPreset
      )
    ) {
      setSimSelectedCompareFilterPreset("");
      setSimCompareFilterPresetName("");
    }

    const nextUsageRowsRaw = Array.isArray(response?.usage)
      ? response.usage
      : buildCompareFilterPresetUsageRows(simCompareFilterPresetUsageRows, getCompareFilterPresetsForUsageScope(nextPresets), "most_used");
    setSimCompareFilterPresetUsage(normalizeCompareFilterPresetUsage(nextUsageRowsRaw));
    setSimCompareFilterPresetUsageRows(
      buildCompareFilterPresetUsageRows(
        nextUsageRowsRaw,
        getCompareFilterPresetsForUsageScope(nextPresets),
        simCompareFilterPresetUsageViewMode
      )
    );
    if (typeof response?.enabled === "boolean") {
      setSimCompareFilterPresetUsageEnabled(Boolean(response.enabled));
    }
  };

  const getLatestCompareFilterUsageRowsForMaintenance = async () => {
    return refreshCompareFilterUsage("most_used");
  };

  const getCompareFilterPresetUsageLabel = (presetName) => {
    if (!simCompareFilterPresetUsageVisible) {
      return String(presetName || "");
    }
    const key = String(presetName || "").trim().toLowerCase();
    const usage = simCompareFilterPresetUsage[key];
    const totalCount = Number(usage?.total_count ?? 0);
    return `${presetName} (${totalCount})`;
  };

  const trackCompareFilterPresetUsageAction = async (presetName, action) => {
    if (!simCompareFilterPresetUsageEnabled) {
      return;
    }
    try {
      const tracked = await trackWhatIfCompareFilterPresetUsage(presetName, action);
      if (typeof tracked?.enabled === "boolean") {
        setSimCompareFilterPresetUsageEnabled(Boolean(tracked.enabled));
      }
      const usageSummary = Array.isArray(tracked?.usage_summary)
        ? tracked.usage_summary
        : Array.isArray(tracked?.usage)
          ? tracked.usage
          : [];
      if (usageSummary.length > 0) {
        setSimCompareFilterPresetUsage(normalizeCompareFilterPresetUsage(usageSummary));
        setSimCompareFilterPresetUsageRows(
          buildCompareFilterPresetUsageRows(
            usageSummary,
            getCompareFilterPresetsForUsageScope(),
            simCompareFilterPresetUsageViewMode
          )
        );
      }
    } catch {
      // Telemetry should not block preset workflows.
    }
  };

  const handleSelectCompareFilterPresetUsageViewMode = async (mode) => {
    const normalizedMode = String(mode || "most_used").trim().toLowerCase();
    setSimCompareFilterPresetUsageViewMode(normalizedMode);
    try {
      await refreshCompareFilterUsage(normalizedMode);
    } catch {
      // Keep current usage view on refresh failures.
    }
  };

  const handleSelectCompareTagFilter = async (tag) => {
    const nextTag = simCompareTagFilter === tag ? "" : tag;
    setSimCompareTagFilter(nextTag);
    try {
      const usageRows = await getWhatIfCompareFilterPresetUsage({
        includeZeroUsage: true,
        viewMode: simCompareFilterPresetUsageViewMode,
        tag: nextTag || undefined,
      });
      const latestUsageRows = Array.isArray(usageRows) ? usageRows : [];
      const scopedPresets = (Array.isArray(simCompareFilterPresets) ? simCompareFilterPresets : [])
        .filter((preset) => !nextTag || (preset?.tags || []).includes(nextTag));
      setSimCompareFilterPresetUsage(normalizeCompareFilterPresetUsage(latestUsageRows));
      setSimCompareFilterPresetUsageRows(
        buildCompareFilterPresetUsageRows(
          latestUsageRows,
          scopedPresets,
          simCompareFilterPresetUsageViewMode
        )
      );
    } catch {
      // Keep current usage view on refresh failures.
    }
  };

  const loadWhatIfRunHistory = async (options = {}) => {
    setSimHistoryLoading(true);
    try {
      const loaded = await getWhatIfRunHistory({
        limit: Number(options.limit ?? 20),
        symbol: options.symbol ?? undefined,
        action: options.action ?? undefined,
      });
      setSimRunHistory(Array.isArray(loaded) ? loaded : []);
    } catch {
      setSimPresetMessage("Unable to load run history.");
    } finally {
      setSimHistoryLoading(false);
    }
  };

  const loadWhatIfCompareHistory = async (options = {}) => {
    setSimCompareHistoryLoading(true);
    try {
      const loaded = await getWhatIfCompareHistory({
        limit: Number(options.limit ?? 20),
        presetALabel: options.presetALabel ?? undefined,
        presetBLabel: options.presetBLabel ?? undefined,
        fromAsOf: options.fromAsOf ?? undefined,
        toAsOf: options.toAsOf ?? undefined,
        window: options.window ?? undefined,
        sortBy: options.sortBy ?? undefined,
        sortOrder: options.sortOrder ?? undefined,
        page: options.page ?? 1,
        pageSize: options.pageSize ?? simCompareHistoryPageSize,
        noteContains: options.noteContains ?? undefined,
        minDeltaMarketValue: options.minDeltaMarketValue ?? undefined,
        minDeltaUnrealizedPl: options.minDeltaUnrealizedPl ?? undefined,
      });
      const loadedHistory = Array.isArray(loaded)
        ? loaded
        : Array.isArray(loaded?.history)
          ? loaded.history
          : [];
      setSimCompareHistory(loadedHistory);
      setSimCompareHistoryCounts({
        totalCount: Array.isArray(loaded)
          ? loadedHistory.length
          : Number(loaded?.totalCount ?? loadedHistory.length),
        filteredCount: Array.isArray(loaded)
          ? loadedHistory.length
          : Number(loaded?.filteredCount ?? loadedHistory.length),
        count: Array.isArray(loaded)
          ? loadedHistory.length
          : Number(loaded?.count ?? loadedHistory.length),
      });
      setSimCompareHistoryAppliedFilters(
        loaded && !Array.isArray(loaded)
          ? (loaded.appliedFilters || {})
          : {
              preset_a_label: options.presetALabel ?? null,
              preset_b_label: options.presetBLabel ?? null,
              from_as_of: options.fromAsOf ?? null,
              to_as_of: options.toAsOf ?? null,
              window: options.window ?? null,
            }
      );
      if (!Array.isArray(loaded)) {
        setSimCompareHistoryPage(Number(loaded?.page ?? 1));
        setSimCompareHistoryPageSize(Number(loaded?.pageSize ?? loaded?.page_size ?? simCompareHistoryPageSize));
        setSimCompareHistoryTotalPages(Number(loaded?.totalPages ?? 1));
      }
    } catch {
      setSimPresetMessage("Unable to load compare history.");
    } finally {
      setSimCompareHistoryLoading(false);
    }
  };

  const fetchAnalytics = () => {
    setLoading(true);
    setError("");
    Promise.all([
      getPortfolioAnalytics(),
      getPortfolioExposure(),
      getPortfolioHistory(),
      getPortfolioRiskChecks(),
      getRuntimeMaintenanceStatus(),
      getBrokerSyncStatus(),
      getBrokerSyncPolicy(),
      getWhatIfPresets(),
      getWhatIfRunHistory({ limit: 20 }),
      getWhatIfCompareHistory({
        limit: 20,
        sortBy: simCompareHistorySortBy,
        sortOrder: simCompareHistorySortOrder,
      }),
      getWhatIfCompareFilterPresets(),
      getWhatIfCompareFilterPresetUsage({
        includeZeroUsage: true,
        viewMode: simCompareFilterPresetUsageViewMode,
      }),
      getWhatIfCompareFilterPresetUsageSettings(),
      getWhatIfCompareSortPresets(),
      getWhatIfCompareSortPresetArchive(),
      getWhatIfCompareSortPresetUsage({ includeZeroUsage: true, viewMode: simCompareSortPresetUsageViewMode }),
      getWhatIfCompareSortPresetUsageSettings(),
      getWhatIfCompareViewPresets(),
      getWhatIfCompareViewPresetArchive(),
      getWhatIfCompareViewPresetUsage({ includeZeroUsage: true, viewMode: simCompareViewPresetUsageViewMode }),
      getWhatIfCompareViewPresetUsageSettings(),
      getWhatIfCompareExportProfiles(),
      getWhatIfCompareExportProfileArchive(),
      getWhatIfCompareFilterPresetArchive(),
      getWhatIfCompareFilterPresetTags(),
      getWhatIfCompareFilterExportProfiles(),
      getWhatIfCompareFilterExportProfileArchive(),
      getReadinessGateTrend(),
      getPortfolioAnomalySignals(),
      getReadinessActions(),
      Promise.resolve().then(() => getPortfolioConcentration()).catch(() => ({})),
      Promise.resolve().then(() => getPortfolioPL()).catch(() => ({})),
    ])
      .then(([
        analyticsData,
        exposureData,
        historyData,
        riskData,
        maintenanceData,
        brokerSyncStatus,
        brokerPolicy,
        loadedPresets,
        loadedRunHistory,
        loadedCompareHistory,
        loadedCompareFilterPresets,
        loadedCompareFilterPresetUsage,
        loadedCompareFilterPresetUsageSettings,
        loadedCompareSortPresets,
        loadedCompareSortPresetArchive,
        loadedCompareSortPresetUsage,
        loadedCompareSortPresetUsageSettings,
        loadedCompareViewPresets,
        loadedCompareViewPresetArchive,
        loadedCompareViewPresetUsage,
        loadedCompareViewPresetUsageSettings,
        loadedCompareExportProfiles,
        loadedCompareExportProfileArchive,
        loadedCompareFilterPresetArchive,
        loadedCompareFilterPresetTags,
        loadedCompareFilterExportProfiles,
        loadedCompareFilterExportProfileArchive,
        readinessTrendData,
        anomalySignalsData,
        readinessActionsData,
        concentrationData,
        portfolioPLData,
      ]) => {
        if (brokerPolicy?.sync_days) setSyncDays(Number(brokerPolicy.sync_days));
        setAnalytics(analyticsData);
        setExposure(exposureData);
        setHistory(Array.isArray(historyData) ? historyData : []);
        setHistorySummary(analyticsData?.history_summary ?? null);
        setRiskSummary(riskData || analyticsData?.risk_summary || null);
        setMaintenanceStatus(maintenanceData || analyticsData?.runtime_summary?.last_maintenance || null);
        setConcentration(Object.keys(concentrationData ?? {}).length ? concentrationData : null);
        setPortfolioPL(Object.keys(portfolioPLData ?? {}).length ? portfolioPLData : null);
        if (readinessTrendData?.status === "ok") setReadinessTrend(readinessTrendData);
        if (anomalySignalsData?.status === "ok") {
          setPortfolioAnomalySignals(Array.isArray(anomalySignalsData?.signals) ? anomalySignalsData.signals : []);
        }
        if (readinessActionsData?.status === "ok") {
          setReadinessActions(Array.isArray(readinessActionsData?.actions) ? readinessActionsData.actions : []);
        }
        setBrokerSyncDetails(normalizeBrokerSyncDetails(brokerSyncStatus));
        setSimPresets(Array.isArray(loadedPresets) ? loadedPresets : []);
        setSimRunHistory(Array.isArray(loadedRunHistory) ? loadedRunHistory : []);
        const loadedCompareRows = Array.isArray(loadedCompareHistory)
          ? loadedCompareHistory
          : Array.isArray(loadedCompareHistory?.history)
            ? loadedCompareHistory.history
            : [];
        setSimCompareHistory(loadedCompareRows);
        setSimCompareHistoryCounts({
          totalCount: Array.isArray(loadedCompareHistory)
            ? loadedCompareRows.length
            : Number(loadedCompareHistory?.totalCount ?? loadedCompareRows.length),
          filteredCount: Array.isArray(loadedCompareHistory)
            ? loadedCompareRows.length
            : Number(loadedCompareHistory?.filteredCount ?? loadedCompareRows.length),
          count: Array.isArray(loadedCompareHistory)
            ? loadedCompareRows.length
            : Number(loadedCompareHistory?.count ?? loadedCompareRows.length),
        });
        setSimCompareHistoryAppliedFilters(
          loadedCompareHistory && !Array.isArray(loadedCompareHistory)
            ? (loadedCompareHistory.appliedFilters || {})
            : {}
        );
        if (loadedCompareHistory && !Array.isArray(loadedCompareHistory)) {
          setSimCompareHistoryPage(Number(loadedCompareHistory?.page ?? 1));
          setSimCompareHistoryPageSize(
            Number(loadedCompareHistory?.pageSize ?? loadedCompareHistory?.page_size ?? 10)
          );
          setSimCompareHistoryTotalPages(Number(loadedCompareHistory?.totalPages ?? 1));
        }
        const safeCompareFilterPresets = Array.isArray(loadedCompareFilterPresets) ? loadedCompareFilterPresets : [];
        setSimCompareFilterPresets(safeCompareFilterPresets);
        setSimCompareFilterPresetUsage(normalizeCompareFilterPresetUsage(loadedCompareFilterPresetUsage));
        setSimCompareFilterPresetUsageRows(
          buildCompareFilterPresetUsageRows(
            loadedCompareFilterPresetUsage,
            safeCompareFilterPresets,
            simCompareFilterPresetUsageViewMode
          )
        );
        setSimCompareFilterPresetUsageEnabled(Boolean(loadedCompareFilterPresetUsageSettings?.enabled ?? true));
        setSimCompareFilterPresetArchive(Array.isArray(loadedCompareFilterPresetArchive) ? loadedCompareFilterPresetArchive : []);
        setSimCompareFilterTags(Array.isArray(loadedCompareFilterPresetTags) ? loadedCompareFilterPresetTags : []);
        setSimCompareFilterExportProfiles(Array.isArray(loadedCompareFilterExportProfiles) ? loadedCompareFilterExportProfiles : []);
        setSimCompareFilterExportProfileArchive(Array.isArray(loadedCompareFilterExportProfileArchive) ? loadedCompareFilterExportProfileArchive : []);
        const safeCompareSortPresets = Array.isArray(loadedCompareSortPresets) ? loadedCompareSortPresets : [];
        setSimCompareSortPresets(safeCompareSortPresets);
        setSimCompareSortPresetArchive(Array.isArray(loadedCompareSortPresetArchive?.archive) ? loadedCompareSortPresetArchive.archive : Array.isArray(loadedCompareSortPresetArchive) ? loadedCompareSortPresetArchive : []);
        setSimCompareSortPresetUsageRows(
          buildCompareSortPresetUsageRows(loadedCompareSortPresetUsage, safeCompareSortPresets, simCompareSortPresetUsageViewMode)
        );
        setSimCompareSortPresetUsageEnabled(Boolean(loadedCompareSortPresetUsageSettings?.enabled ?? true));
        const safeCompareViewPresets = Array.isArray(loadedCompareViewPresets) ? loadedCompareViewPresets : [];
        setSimCompareViewPresets(safeCompareViewPresets);
        setSimCompareViewPresetArchive(Array.isArray(loadedCompareViewPresetArchive?.archive) ? loadedCompareViewPresetArchive.archive : Array.isArray(loadedCompareViewPresetArchive) ? loadedCompareViewPresetArchive : []);
        setSimCompareViewPresetUsageRows(
          buildCompareViewPresetUsageRows(loadedCompareViewPresetUsage, safeCompareViewPresets, simCompareViewPresetUsageViewMode)
        );
        setSimCompareViewPresetUsageEnabled(Boolean(loadedCompareViewPresetUsageSettings?.enabled ?? true));
        setSimCompareExportProfiles(Array.isArray(loadedCompareExportProfiles) ? loadedCompareExportProfiles : []);
        setSimCompareExportProfileArchive(Array.isArray(loadedCompareExportProfileArchive?.archive) ? loadedCompareExportProfileArchive.archive : Array.isArray(loadedCompareExportProfileArchive) ? loadedCompareExportProfileArchive : []);
      })
      .catch(() =>
        setError("Unable to load portfolio data. Please check your connection or try again.")
      )
      .finally(() => setLoading(false));
  };

  const handleWhatIfSimulation = async () => {
    setSimLoading(true);
    setSimPresetMessage("");
    try {
      const result = await simulatePortfolioTrade({
        symbol: simSymbol,
        quantity: simQuantity,
        price: simPrice,
        action: simAction,
      });
      setSimResult(result);
      const historyPayload = {
        preset_name: simSelectedPreset || simPresetName.trim(),
        symbol: simSymbol,
        action: simAction,
        quantity: Number(simQuantity),
        price: Number(simPrice),
        delta_market_value: Number(result?.delta?.market_value ?? 0),
        delta_unrealized_pl: Number(result?.delta?.unrealized_pl ?? 0),
      };
      try {
        const historyResponse = await appendWhatIfRunHistory(historyPayload);
        const nextHistory = Array.isArray(historyResponse?.history)
          ? historyResponse.history
          : [historyResponse?.entry, ...simRunHistory].filter(Boolean).slice(0, 20);
        setSimRunHistory(nextHistory);
      } catch {
        setSimPresetMessage("Simulation succeeded, but run history save failed.");
      }
    } catch {
      setError("What-if simulation failed. Please check symbol/quantity/price and retry.");
    } finally {
      setSimLoading(false);
    }
  };

  const handleSendReadinessTestAlert = async () => {
    setReadinessAlertTestLoading(true);
    setReadinessAlertTestMessage("");
    try {
      const result = await sendReadinessTestAlert(false);
      refreshReadiness();
      if (result?.status === "ok") {
        setReadinessAlertTestMessage("Test readiness alert generated (dry run).");
      } else {
        setReadinessAlertTestMessage("Test readiness alert failed.");
      }
    } catch {
      setReadinessAlertTestMessage("Test readiness alert failed.");
    } finally {
      setReadinessAlertTestLoading(false);
    }
  };

  const handleSavePreset = async () => {
    const name = simPresetName.trim();
    if (!name || !simSymbol.trim()) {
      setSimPresetMessage("Preset name and symbol are required.");
      return;
    }
    const nextPreset = {
      name,
      symbol: simSymbol.trim().toUpperCase(),
      action: simAction,
      quantity: Number(simQuantity),
      price: Number(simPrice),
      sleeve: "what-if",
    };
    const filtered = simPresets.filter((preset) => String(preset.name).toLowerCase() !== name.toLowerCase());
    const nextPresets = [...filtered, nextPreset];
    try {
      const saved = await saveWhatIfPresets(nextPresets);
      setSimPresets(Array.isArray(saved) ? saved : nextPresets);
      setSimSelectedPreset(name);
      setSimPresetMessage(`Preset saved: ${name}`);
    } catch {
      setSimPresetMessage("Failed to save preset.");
    }
  };

  const handleLoadPreset = (presetName) => {
    setSimSelectedPreset(presetName);
    const preset = simPresets.find((entry) => entry.name === presetName);
    if (!preset) return;
    setSimPresetName(preset.name || "");
    setSimSymbol(String(preset.symbol || "").toUpperCase());
    setSimAction(String(preset.action || "buy").toLowerCase());
    setSimQuantity(Number(preset.quantity || 0));
    setSimPrice(Number(preset.price || 0));
    setSimPresetMessage(`Preset loaded: ${preset.name}`);
  };

  const handleDeletePreset = async () => {
    if (!simSelectedPreset) {
      setSimPresetMessage("Select a preset to delete.");
      return;
    }
    const nextPresets = simPresets.filter((preset) => preset.name !== simSelectedPreset);
    try {
      const saved = await saveWhatIfPresets(nextPresets);
      setSimPresets(Array.isArray(saved) ? saved : nextPresets);
      setSimPresetMessage(`Preset deleted: ${simSelectedPreset}`);
      setSimSelectedPreset("");
    } catch {
      setSimPresetMessage("Failed to delete preset.");
    }
  };

  const handleApplyRunHistoryFilters = async () => {
    await loadWhatIfRunHistory({
      limit: 20,
      symbol: simHistorySymbolFilter.trim() || undefined,
      action: simHistoryActionFilter === "all" ? undefined : simHistoryActionFilter,
    });
  };

  const handleClearRunHistoryFilters = async () => {
    setSimHistorySymbolFilter("");
    setSimHistoryActionFilter("all");
    await loadWhatIfRunHistory({ limit: 20 });
  };

  const handleExportRunHistory = async () => {
    try {
      await exportWhatIfRunHistoryCsv({
        limit: 100,
        symbol: simHistorySymbolFilter.trim() || undefined,
        action: simHistoryActionFilter === "all" ? undefined : simHistoryActionFilter,
      });
      setSimPresetMessage("Run history CSV downloaded.");
    } catch {
      setSimPresetMessage("Run history export failed.");
    }
  };

  const handleComparePresets = async () => {
    if (!simComparePresetA || !simComparePresetB) {
      setSimPresetMessage("Select both presets to compare.");
      return;
    }
    const presetA = simPresets.find((preset) => preset.name === simComparePresetA);
    const presetB = simPresets.find((preset) => preset.name === simComparePresetB);
    if (!presetA || !presetB) {
      setSimPresetMessage("Unable to find selected presets.");
      return;
    }

    setSimCompareLoading(true);
    setSimPresetMessage("");
    try {
      const comparison = await compareWhatIfScenarios({
        scenarioA: {
          label: presetA.name,
          symbol: presetA.symbol,
          action: presetA.action,
          quantity: presetA.quantity,
          price: presetA.price,
          sleeve: presetA.sleeve || "what-if",
        },
        scenarioB: {
          label: presetB.name,
          symbol: presetB.symbol,
          action: presetB.action,
          quantity: presetB.quantity,
          price: presetB.price,
          sleeve: presetB.sleeve || "what-if",
        },
      });
      setSimCompareResult(comparison);
      try {
        // Extract position data from comparison results
        const positionsA = comparison?.scenario_a?.result?.positions ?? [];
        const positionsB = comparison?.scenario_b?.result?.positions ?? [];
        
        const compareHistoryResponse = await appendWhatIfCompareHistory({
          preset_a_label: presetA.name,
          preset_b_label: presetB.name,
          delta_market_value: Number(comparison?.delta_of_deltas?.market_value ?? 0),
          delta_unrealized_pl: Number(comparison?.delta_of_deltas?.unrealized_pl ?? 0),
          positions_a: positionsA,
          positions_b: positionsB,
        });
        const nextHistory = Array.isArray(compareHistoryResponse?.history)
          ? compareHistoryResponse.history
          : [compareHistoryResponse?.entry, ...simCompareHistory].filter(Boolean).slice(0, 20);
        setSimCompareHistory(nextHistory);
      } catch {
        setSimPresetMessage("Comparison succeeded, but compare history save failed.");
      }
    } catch {
      setSimPresetMessage("Preset comparison failed.");
    } finally {
      setSimCompareLoading(false);
    }
  };

  const handleExportCompareHistory = async () => {
    const quickWindow = simCompareHistoryQuickRange === "custom" ? undefined : simCompareHistoryQuickRange;
    const exportMode = simCompareHistoryExportMode;
    const exportLimit = Math.max(1, Math.min(Number(simCompareHistoryCounts?.filteredCount ?? 100), 1000));
    try {
      await exportWhatIfCompareHistoryCsv({
        limit: exportMode === "all" ? exportLimit : Number(simCompareHistoryPageSize ?? 20),
        exportMode,
        page: exportMode === "page" ? simCompareHistoryPage : undefined,
        pageSize: exportMode === "page" ? simCompareHistoryPageSize : undefined,
        presetALabel: simCompareHistoryPresetAFilter.trim() || undefined,
        presetBLabel: simCompareHistoryPresetBFilter.trim() || undefined,
        fromAsOf: quickWindow ? undefined : simCompareHistoryFromAsOfFilter.trim() || undefined,
        toAsOf: quickWindow ? undefined : simCompareHistoryToAsOfFilter.trim() || undefined,
        window: quickWindow,
        noteContains: simCompareHistoryNoteFilter.trim() || undefined,
        minDeltaMarketValue: simCompareHistoryMinDeltaMvFilter.trim() || undefined,
        minDeltaUnrealizedPl: simCompareHistoryMinDeltaPlFilter.trim() || undefined,
      });
      setSimPresetMessage("Compare history CSV downloaded.");
    } catch {
      setSimPresetMessage("Compare history export failed.");
    }
  };

  const handleSaveCompareHistoryFilterPreset = async () => {
    const name = simCompareFilterPresetName.trim();
    const presetALabel = simCompareHistoryPresetAFilter.trim();
    const presetBLabel = simCompareHistoryPresetBFilter.trim();
    const noteContains = simCompareHistoryNoteFilter.trim();
    const minDeltaMarketValue = simCompareHistoryMinDeltaMvFilter.trim();
    const minDeltaUnrealizedPl = simCompareHistoryMinDeltaPlFilter.trim();
    const quickRange = simCompareHistoryQuickRange;
    const fromAsOf = simCompareHistoryFromAsOfFilter.trim();
    const toAsOf = simCompareHistoryToAsOfFilter.trim();
    const sortBy = simCompareHistorySortBy;
    const sortOrder = simCompareHistorySortOrder;
    const parsedTags = simCompareFilterPresetTagInput
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const exists = simCompareFilterPresets.some(
      (preset) => String(preset?.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    if (!name) {
      setSimPresetMessage("Compare filter preset name is required.");
      return;
    }
    if (
      !presetALabel
      && !presetBLabel
      && !noteContains
      && !minDeltaMarketValue
      && !minDeltaUnrealizedPl
      && quickRange === "custom"
      && !fromAsOf
      && !toAsOf
      && sortBy === "as_of"
      && sortOrder === "desc"
      && parsedTags.length === 0
    ) {
      setSimPresetMessage("Enter at least one non-default compare history filter before saving.");
      return;
    }

    try {
      const saved = await upsertWhatIfCompareFilterPreset({
        name,
        preset_a_label: presetALabel,
        preset_b_label: presetBLabel,
        note_contains: noteContains,
        min_delta_market_value: minDeltaMarketValue ? Number(minDeltaMarketValue) : null,
        min_delta_unrealized_pl: minDeltaUnrealizedPl ? Number(minDeltaUnrealizedPl) : null,
        quick_range: quickRange,
        from_as_of: fromAsOf,
        to_as_of: toAsOf,
        sort_by: sortBy,
        sort_order: sortOrder,
        tags: parsedTags,
      });
      const nextPresets = Array.isArray(saved?.presets)
        ? saved.presets
        : [
            ...simCompareFilterPresets.filter(
              (preset) => String(preset.name).toLowerCase() !== name.toLowerCase()
            ),
            {
              name,
              preset_a_label: presetALabel,
              preset_b_label: presetBLabel,
              note_contains: noteContains,
              min_delta_market_value: minDeltaMarketValue ? Number(minDeltaMarketValue) : null,
              min_delta_unrealized_pl: minDeltaUnrealizedPl ? Number(minDeltaUnrealizedPl) : null,
              quick_range: quickRange,
              from_as_of: fromAsOf,
              to_as_of: toAsOf,
              sort_by: sortBy,
              sort_order: sortOrder,
              tags: parsedTags,
            },
          ];
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      setSimSelectedCompareFilterPreset(name);
      setSimPresetMessage(
        saved?.action === "overwritten" || exists
          ? `Compare filter preset updated: ${name}`
          : `Compare filter preset saved: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to save compare filter preset.");
    }
  };

  const handleLoadCompareHistoryFilterPreset = async () => {
    if (!simSelectedCompareFilterPreset) {
      setSimPresetMessage("Select a compare filter preset to load.");
      return;
    }
    const preset = simCompareFilterPresets.find((entry) => entry.name === simSelectedCompareFilterPreset);
    if (!preset) {
      setSimPresetMessage("Unable to find selected compare filter preset.");
      return;
    }
    setSimCompareFilterPresetName(preset.name || "");
    setSimCompareHistoryPresetAFilter(String(preset.preset_a_label || ""));
    setSimCompareHistoryPresetBFilter(String(preset.preset_b_label || ""));
    setSimCompareHistoryNoteFilter(String(preset.note_contains || ""));
    setSimCompareHistoryMinDeltaMvFilter(
      preset.min_delta_market_value === null || preset.min_delta_market_value === undefined
        ? ""
        : String(preset.min_delta_market_value)
    );
    setSimCompareHistoryMinDeltaPlFilter(
      preset.min_delta_unrealized_pl === null || preset.min_delta_unrealized_pl === undefined
        ? ""
        : String(preset.min_delta_unrealized_pl)
    );
    setSimCompareHistoryQuickRange(String(preset.quick_range || "custom"));
    setSimCompareHistoryFromAsOfFilter(String(preset.from_as_of || ""));
    setSimCompareHistoryToAsOfFilter(String(preset.to_as_of || ""));
    setSimCompareHistorySortBy(String(preset.sort_by || "as_of"));
    setSimCompareHistorySortOrder(String(preset.sort_order || "desc"));
    setSimCompareFilterPresetTagInput(Array.isArray(preset.tags) ? preset.tags.join(", ") : "");
    await trackCompareFilterPresetUsageAction(preset.name, "load");
    setSimPresetMessage(`Compare filter preset loaded: ${preset.name}`);
  };

  const handleLoadAndApplyCompareHistoryFilterPreset = async () => {
    if (!simSelectedCompareFilterPreset) {
      setSimPresetMessage("Select a compare filter preset to load.");
      return;
    }
    const preset = simCompareFilterPresets.find((entry) => entry.name === simSelectedCompareFilterPreset);
    if (!preset) {
      setSimPresetMessage("Unable to find selected compare filter preset.");
      return;
    }

    const presetALabel = String(preset.preset_a_label || "").trim();
    const presetBLabel = String(preset.preset_b_label || "").trim();
    const noteContains = String(preset.note_contains || "").trim();
    const minDeltaMarketValue =
      preset.min_delta_market_value === null || preset.min_delta_market_value === undefined
        ? ""
        : String(preset.min_delta_market_value);
    const minDeltaUnrealizedPl =
      preset.min_delta_unrealized_pl === null || preset.min_delta_unrealized_pl === undefined
        ? ""
        : String(preset.min_delta_unrealized_pl);
    const quickRange = String(preset.quick_range || "custom");
    const fromAsOf = String(preset.from_as_of || "").trim();
    const toAsOf = String(preset.to_as_of || "").trim();
    const sortBy = String(preset.sort_by || "as_of");
    const sortOrder = String(preset.sort_order || "desc");

    setSimCompareFilterPresetName(preset.name || "");
    setSimCompareHistoryPresetAFilter(presetALabel);
    setSimCompareHistoryPresetBFilter(presetBLabel);
    setSimCompareHistoryNoteFilter(noteContains);
    setSimCompareHistoryMinDeltaMvFilter(minDeltaMarketValue);
    setSimCompareHistoryMinDeltaPlFilter(minDeltaUnrealizedPl);
    setSimCompareHistoryQuickRange(quickRange);
    setSimCompareHistoryFromAsOfFilter(fromAsOf);
    setSimCompareHistoryToAsOfFilter(toAsOf);
    setSimCompareHistorySortBy(sortBy);
    setSimCompareHistorySortOrder(sortOrder);
    setSimCompareFilterPresetTagInput(Array.isArray(preset.tags) ? preset.tags.join(", ") : "");

    const quickWindow = quickRange === "custom" ? undefined : quickRange;
    await loadWhatIfCompareHistory({
      limit: 20,
      presetALabel: presetALabel || undefined,
      presetBLabel: presetBLabel || undefined,
      fromAsOf: quickWindow ? undefined : fromAsOf || undefined,
      toAsOf: quickWindow ? undefined : toAsOf || undefined,
      window: quickWindow,
      sortBy,
      sortOrder,
      page: 1,
      pageSize: simCompareHistoryPageSize,
      noteContains: noteContains || undefined,
      minDeltaMarketValue: minDeltaMarketValue || undefined,
      minDeltaUnrealizedPl: minDeltaUnrealizedPl || undefined,
    });

    const bannerParts = [
      presetALabel ? `A=${presetALabel}` : null,
      presetBLabel ? `B=${presetBLabel}` : null,
      noteContains ? `Note contains "${noteContains}"` : null,
      minDeltaMarketValue ? `|Delta MV|>=${minDeltaMarketValue}` : null,
      minDeltaUnrealizedPl ? `|Delta P/L|>=${minDeltaUnrealizedPl}` : null,
      quickWindow ? `Window=${quickWindow}` : null,
      !quickWindow && fromAsOf ? `From=${fromAsOf}` : null,
      !quickWindow && toAsOf ? `To=${toAsOf}` : null,
      `Sort=${sortBy} ${sortOrder}`,
    ].filter(Boolean);

    setSimCompareFilterApplyBanner(
      bannerParts.length > 0
        ? `Applied compare filters (${preset.name}): ${bannerParts.join(", ")}`
        : `Applied compare filters (${preset.name}).`
    );
    await trackCompareFilterPresetUsageAction(preset.name, "load_apply");
    setSimPresetMessage(`Compare filter preset loaded and applied: ${preset.name}`);
  };

  const handleUpdateLoadedCompareHistoryFilterPreset = async () => {
    const name = simSelectedCompareFilterPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a loaded compare filter preset to update.");
      return;
    }

    const presetALabel = simCompareHistoryPresetAFilter.trim();
    const presetBLabel = simCompareHistoryPresetBFilter.trim();
    const noteContains = simCompareHistoryNoteFilter.trim();
    const minDeltaMarketValue = simCompareHistoryMinDeltaMvFilter.trim();
    const minDeltaUnrealizedPl = simCompareHistoryMinDeltaPlFilter.trim();
    const quickRange = simCompareHistoryQuickRange;
    const fromAsOf = simCompareHistoryFromAsOfFilter.trim();
    const toAsOf = simCompareHistoryToAsOfFilter.trim();
    const sortBy = simCompareHistorySortBy;
    const sortOrder = simCompareHistorySortOrder;
    const parsedTags = simCompareFilterPresetTagInput
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    if (
      !presetALabel
      && !presetBLabel
      && !noteContains
      && !minDeltaMarketValue
      && !minDeltaUnrealizedPl
      && quickRange === "custom"
      && !fromAsOf
      && !toAsOf
      && sortBy === "as_of"
      && sortOrder === "desc"
      && parsedTags.length === 0
    ) {
      setSimPresetMessage("Enter at least one non-default compare history filter before updating.");
      return;
    }

    try {
      const saved = await upsertWhatIfCompareFilterPreset({
        name,
        preset_a_label: presetALabel,
        preset_b_label: presetBLabel,
        note_contains: noteContains,
        min_delta_market_value: minDeltaMarketValue ? Number(minDeltaMarketValue) : null,
        min_delta_unrealized_pl: minDeltaUnrealizedPl ? Number(minDeltaUnrealizedPl) : null,
        quick_range: quickRange,
        from_as_of: fromAsOf,
        to_as_of: toAsOf,
        sort_by: sortBy,
        sort_order: sortOrder,
        tags: parsedTags,
      });
      const nextPresets = Array.isArray(saved?.presets)
        ? saved.presets
        : simCompareFilterPresets.map((preset) => (
            String(preset?.name || "").trim().toLowerCase() === name.toLowerCase()
              ? {
                  ...preset,
                  preset_a_label: presetALabel,
                  preset_b_label: presetBLabel,
                  note_contains: noteContains,
                  min_delta_market_value: minDeltaMarketValue ? Number(minDeltaMarketValue) : null,
                  min_delta_unrealized_pl: minDeltaUnrealizedPl ? Number(minDeltaUnrealizedPl) : null,
                  quick_range: quickRange,
                  from_as_of: fromAsOf,
                  to_as_of: toAsOf,
                  sort_by: sortBy,
                  sort_order: sortOrder,
                  tags: parsedTags,
                }
              : preset
          ));
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      setSimCompareFilterPresetName(name);
      await trackCompareFilterPresetUsageAction(name, "update");
      setSimPresetMessage(`Compare filter preset updated: ${name}`);
    } catch {
      setSimPresetMessage("Failed to update compare filter preset.");
    }
  };

  const handleRenameCompareHistoryFilterPreset = async () => {
    const name = simSelectedCompareFilterPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare filter preset to rename.");
      return;
    }
    const newName = window.prompt(`Rename "${name}" — enter the new name:`, name);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Rename cancelled: no name provided.");
      return;
    }
    if (newName.trim() === name) {
      setSimPresetMessage("Rename cancelled: new name is the same as the current name.");
      return;
    }
    try {
      const response = await renameWhatIfCompareFilterPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareFilterPresets;
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      const resolvedName = response?.new_name || newName.trim();
      setSimSelectedCompareFilterPreset(resolvedName);
      setSimCompareFilterPresetName(resolvedName);
      setSimPresetMessage(`Renamed "${name}" to "${resolvedName}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Rename failed: a preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to rename compare filter preset.");
      }
    }
  };

  const handleCloneCompareHistoryFilterPreset = async () => {
    const name = simSelectedCompareFilterPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare filter preset to clone.");
      return;
    }
    const newName = window.prompt(`Clone "${name}" — enter a new name for the clone:`, `${name} (copy)`);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Clone cancelled: no name provided.");
      return;
    }
    try {
      const response = await cloneWhatIfCompareFilterPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareFilterPresets;
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      setSimSelectedCompareFilterPreset(response?.new_name || newName.trim());
      setSimCompareFilterPresetName(response?.new_name || newName.trim());
      setSimPresetMessage(`Cloned "${name}" as "${response?.new_name || newName.trim()}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Clone failed: a preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to clone compare filter preset.");
      }
    }
  };

  const handleDeleteCompareHistoryFilterPreset = async () => {
    const name = simSelectedCompareFilterPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare filter preset to delete.");
      return;
    }

    try {
      const response = await deleteWhatIfCompareFilterPreset(name);
      const nextPresets = Array.isArray(response?.presets)
        ? response.presets
        : simCompareFilterPresets.filter(
            (preset) => String(preset?.name || "").trim().toLowerCase() !== name.toLowerCase()
          );
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      setSimSelectedCompareFilterPreset("");
      setSimCompareFilterPresetName("");
      setSimCompareFilterPresetTagInput("");
      setSimPresetMessage(
        response?.deleted
          ? `Compare filter preset deleted: ${name}`
          : `Compare filter preset not found: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to delete compare filter preset.");
    }
  };

  const handleDeleteUnusedCompareHistoryFilterPresets = async () => {
    try {
      const latestUsageRows = await getLatestCompareFilterUsageRowsForMaintenance();
      const scopedPresets = getCompareFilterPresetsForUsageScope();
      const candidateCount = countNeverUsedCompareFilterPresets(latestUsageRows, scopedPresets);
      if (candidateCount <= 0) {
        setSimPresetMessage("No unused compare filter presets to delete.");
        return;
      }

      const tagSuffix = simCompareTagFilter ? ` for tag '${simCompareTagFilter}'` : "";
      const confirmed = window.confirm(
        `Delete ${candidateCount} unused compare filter preset${candidateCount === 1 ? "" : "s"}${tagSuffix}?`
      );
      if (!confirmed) {
        return;
      }

      const response = await deleteWhatIfCompareFilterPresetsUnused(simCompareTagFilter || undefined);
      applyCompareFilterPresetMaintenanceDeleteResponse(response, simCompareFilterPresets);
      setSimPresetMessage(
        `Deleted ${Number(response?.deleted_count ?? 0)} unused compare filter preset${Number(response?.deleted_count ?? 0) === 1 ? "" : "s"}${tagSuffix}.`
      );
    } catch {
      setSimPresetMessage("Failed to delete unused compare filter presets.");
    }
  };

  const handleDeleteUnusedSinceCompareHistoryFilterPresets = async () => {
    const cutoff = simCompareFilterUnusedSince.trim();
    if (!cutoff) {
      setSimPresetMessage("Enter an Unused Since (ISO) cutoff before deleting.");
      return;
    }

    const scopedPresets = getCompareFilterPresetsForUsageScope();
    const previewCount = countCompareFilterPresetsUnusedSince(simCompareFilterPresetUsageRows, scopedPresets, cutoff);
    if (previewCount === null) {
      setSimPresetMessage("Unused Since must be an ISO datetime (for example, 2026-04-01T00:00:00Z).");
      return;
    }

    try {
      const latestUsageRows = await getLatestCompareFilterUsageRowsForMaintenance();
      const candidateCount = countCompareFilterPresetsUnusedSince(latestUsageRows, scopedPresets, cutoff);
      if (!candidateCount || candidateCount <= 0) {
        setSimPresetMessage("No compare filter presets match the unused-since cutoff.");
        return;
      }

      const tagSuffix = simCompareTagFilter ? ` for tag '${simCompareTagFilter}'` : "";
      const confirmed = window.confirm(
        `Delete ${candidateCount} compare filter preset${candidateCount === 1 ? "" : "s"}${tagSuffix} unused since ${cutoff}?`
      );
      if (!confirmed) {
        return;
      }

      const response = await deleteWhatIfCompareFilterPresetsUnusedSince(cutoff, simCompareTagFilter || undefined);
      applyCompareFilterPresetMaintenanceDeleteResponse(response, simCompareFilterPresets);
      setSimPresetMessage(
        `Deleted ${Number(response?.deleted_count ?? 0)} compare filter preset${Number(response?.deleted_count ?? 0) === 1 ? "" : "s"}${tagSuffix} unused since ${cutoff}.`
      );
    } catch {
      setSimPresetMessage("Failed to delete compare filter presets for the unused-since cutoff.");
    }
  };

  const handleRestoreCompareHistoryFilterPreset = async () => {
    if (simCompareFilterPresetArchive.length === 0) {
      setSimPresetMessage("No archived compare filter presets to restore.");
      return;
    }
    const lastArchived = simCompareFilterPresetArchive[simCompareFilterPresetArchive.length - 1];
    const name = String(lastArchived?.name || "").trim();
    if (!name) {
      setSimPresetMessage("Archived preset name is missing.");
      return;
    }

    const confirmed = window.confirm(`Restore archived compare filter preset: ${name}?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await restoreWhatIfCompareFilterPreset(name);
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareFilterPresets;
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      setSimCompareFilterPresetArchive(Array.isArray(response?.archive) ? response.archive : []);
      setSimSelectedCompareFilterPreset(name);
      setSimCompareFilterPresetName(name);
      const restoredPreset = nextPresets.find((entry) => String(entry?.name || "").trim() === name);
      setSimCompareFilterPresetTagInput(Array.isArray(restoredPreset?.tags) ? restoredPreset.tags.join(", ") : "");
      setSimPresetMessage(`Compare filter preset restored: ${name}`);
    } catch {
      setSimPresetMessage("Failed to restore compare filter preset.");
    }
  };

  const handlePurgeCompareHistoryFilterPresetArchive = async () => {
    const archiveCount = simCompareFilterPresetArchive.length;
    if (archiveCount === 0) {
      setSimPresetMessage("Archive is already empty.");
      return;
    }

    const confirmed = window.confirm(
      `Permanently purge all ${archiveCount} archived compare filter preset${archiveCount === 1 ? "" : "s"}? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    try {
      const response = await purgeWhatIfCompareFilterPresetArchive();
      setSimCompareFilterPresetArchive([]);
      setSimPresetMessage(
        `Purged ${Number(response?.purged_count ?? 0)} archived compare filter preset${Number(response?.purged_count ?? 0) === 1 ? "" : "s"}.`
      );
    } catch {
      setSimPresetMessage("Failed to purge compare filter preset archive.");
    }
  };

  const handleExportCompareHistoryFilterPresets = async () => {
    try {
      const exportTag = simCompareFilterExportScope === "selected_tag" ? simCompareTagFilter : undefined;
      if (simCompareFilterExportScope === "selected_tag" && !exportTag) {
        setSimPresetMessage("Select a tag filter before exporting by selected tag.");
        return;
      }
      const blob = await exportWhatIfCompareFilterPresets(exportTag);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportTag
        ? `compare_filter_presets_${String(exportTag).replace(/[^a-zA-Z0-9_-]/g, "_")}.json`
        : "compare_filter_presets.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (exportTag) {
        setSimPresetMessage(`Exported compare filter presets for tag '${exportTag}'.`);
      } else {
        setSimPresetMessage(`Exported ${simCompareFilterPresets.length} compare filter preset${simCompareFilterPresets.length === 1 ? "" : "s"}.`);
      }
    } catch {
      setSimPresetMessage("Failed to export compare filter presets.");
    }
  };

  const handleSaveCompareHistoryFilterExportProfile = async () => {
    const name = String(simCompareFilterExportProfileName || "").trim();
    if (!name) {
      setSimPresetMessage("Compare filter export profile name is required.");
      return;
    }
    if (name.length > 120) {
      setSimPresetMessage("Compare filter export profile name must be <= 120 characters.");
      return;
    }

    const includeTags = simCompareFilterExportScope === "selected_tag" && simCompareTagFilter
      ? [simCompareTagFilter]
      : [];

    if (simCompareFilterExportScope === "selected_tag" && includeTags.length === 0) {
      setSimPresetMessage("Select a tag filter before saving a selected-tag export profile.");
      return;
    }

    try {
      const response = await upsertWhatIfCompareFilterExportProfile({
        name,
        tag_scope: simCompareFilterExportScope,
        include_tags: includeTags,
      });
      const nextProfiles = Array.isArray(response?.profiles)
        ? response.profiles
        : simCompareFilterExportProfiles;
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimSelectedCompareFilterExportProfile(name);
      setSimPresetMessage(`Compare filter export profile saved: ${name}`);
    } catch {
      setSimPresetMessage("Failed to save compare filter export profile.");
    }
  };

  const handleLoadCompareHistoryFilterExportProfile = () => {
    const selectedName = String(simSelectedCompareFilterExportProfile || "").trim();
    if (!selectedName) {
      setSimPresetMessage("Select a compare filter export profile to load.");
      return;
    }

    const profile = simCompareFilterExportProfiles.find(
      (entry) => String(entry?.name || "").trim().toLowerCase() === selectedName.toLowerCase()
    );
    if (!profile) {
      setSimPresetMessage(`Compare filter export profile not found: ${selectedName}`);
      return;
    }

    const scope = String(profile?.tag_scope || "all").trim().toLowerCase();
    const includeTags = Array.isArray(profile?.include_tags) ? profile.include_tags : [];
    setSimCompareFilterExportScope(scope === "selected_tag" ? "selected_tag" : "all");
    if (scope === "selected_tag") {
      setSimCompareTagFilter(String(includeTags[0] || "").trim());
    }
    setSimCompareFilterExportProfileName(profile.name || selectedName);
    setSimPresetMessage(`Compare filter export profile loaded: ${profile.name || selectedName}`);
  };

  const handleDeleteCompareHistoryFilterExportProfile = async () => {
    const selectedName = String(simSelectedCompareFilterExportProfile || "").trim();
    if (!selectedName) {
      setSimPresetMessage("Select a compare filter export profile to delete.");
      return;
    }

    const confirmed = window.confirm(`Delete compare filter export profile: ${selectedName}?`);
    if (!confirmed) return;

    try {
      const response = await deleteWhatIfCompareFilterExportProfile(selectedName);
      const nextProfiles = Array.isArray(response?.profiles)
        ? response.profiles
        : simCompareFilterExportProfiles.filter(
            (entry) => String(entry?.name || "").trim().toLowerCase() !== selectedName.toLowerCase()
          );
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimCompareFilterExportProfileArchive(Array.isArray(response?.archive) ? response.archive : simCompareFilterExportProfileArchive);
      setSimSelectedCompareFilterExportProfile("");
      setSimCompareFilterExportProfileName("");
      setSimPresetMessage(`Compare filter export profile deleted: ${selectedName}`);
    } catch {
      setSimPresetMessage("Failed to delete compare filter export profile.");
    }
  };

  const handleCloneCompareHistoryFilterExportProfile = async () => {
    const selectedName = String(simSelectedCompareFilterExportProfile || "").trim();
    if (!selectedName) {
      setSimPresetMessage("Select a compare filter export profile to clone.");
      return;
    }

    const candidate = window.prompt("Clone compare filter export profile as:", `${selectedName} (copy)`);
    const newName = String(candidate || "").trim();
    if (!newName) return;

    try {
      const response = await cloneWhatIfCompareFilterExportProfile(selectedName, newName);
      const nextProfiles = Array.isArray(response?.profiles)
        ? response.profiles
        : simCompareFilterExportProfiles;
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimSelectedCompareFilterExportProfile(response?.new_name || newName);
      setSimCompareFilterExportProfileName(response?.new_name || newName);
      setSimPresetMessage(`Cloned compare filter export profile \"${selectedName}\" as \"${response?.new_name || newName}\".`);
    } catch {
      setSimPresetMessage("Failed to clone compare filter export profile.");
    }
  };

  const handleRenameCompareHistoryFilterExportProfile = async () => {
    const selectedName = String(simSelectedCompareFilterExportProfile || "").trim();
    if (!selectedName) {
      setSimPresetMessage("Select a compare filter export profile to rename.");
      return;
    }

    const candidate = window.prompt("Rename compare filter export profile to:", selectedName);
    const newName = String(candidate || "").trim();
    if (!newName) return;

    try {
      const response = await renameWhatIfCompareFilterExportProfile(selectedName, newName);
      const nextProfiles = Array.isArray(response?.profiles)
        ? response.profiles
        : simCompareFilterExportProfiles;
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimSelectedCompareFilterExportProfile(response?.new_name || newName);
      setSimCompareFilterExportProfileName(response?.new_name || newName);
      setSimPresetMessage(`Renamed compare filter export profile \"${selectedName}\" to \"${response?.new_name || newName}\".`);
    } catch {
      setSimPresetMessage("Failed to rename compare filter export profile.");
    }
  };

  const handleRestoreCompareHistoryFilterExportProfile = async () => {
    if (simCompareFilterExportProfileArchive.length === 0) {
      setSimPresetMessage("No archived compare filter export profiles to restore.");
      return;
    }

    const lastArchived = simCompareFilterExportProfileArchive[simCompareFilterExportProfileArchive.length - 1] || {};
    const name = String(lastArchived?.name || "").trim();
    if (!name) {
      setSimPresetMessage("Archived compare filter export profile name is missing.");
      return;
    }

    const confirmed = window.confirm(`Restore archived compare filter export profile: ${name}?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await restoreWhatIfCompareFilterExportProfile(name);
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareFilterExportProfiles;
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimCompareFilterExportProfileArchive(Array.isArray(response?.archive) ? response.archive : []);
      setSimSelectedCompareFilterExportProfile(name);
      setSimCompareFilterExportProfileName(name);
      setSimPresetMessage(`Compare filter export profile restored: ${name}`);
    } catch {
      setSimPresetMessage("Failed to restore compare filter export profile.");
    }
  };

  const handlePurgeCompareHistoryFilterExportProfileArchive = async () => {
    const archiveCount = simCompareFilterExportProfileArchive.length;
    if (archiveCount === 0) {
      setSimPresetMessage("Export scope archive is already empty.");
      return;
    }

    const cutoff = simCompareFilterExportArchiveDeletedBefore.trim();
    if (cutoff && Number.isNaN(Date.parse(cutoff))) {
      setSimPresetMessage("Export archive purge cutoff must be an ISO datetime (for example, 2026-04-01T00:00:00Z).");
      return;
    }

    const confirmed = window.confirm(
      cutoff
        ? `Permanently purge archived compare filter export scope profile entries deleted on/before ${cutoff}?`
        : `Permanently purge all ${archiveCount} archived compare filter export scope profile${archiveCount === 1 ? "" : "s"}? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    try {
      const response = await purgeWhatIfCompareFilterExportProfileArchive(cutoff || undefined);
      const nextArchive = Array.isArray(response?.archive) ? response.archive : [];
      setSimCompareFilterExportProfileArchive(nextArchive);
      setSimPresetMessage(
        cutoff
          ? `Purged ${Number(response?.purged_count ?? 0)} archived compare filter export scope profile${Number(response?.purged_count ?? 0) === 1 ? "" : "s"} deleted on/before ${cutoff}.`
          : `Purged ${Number(response?.purged_count ?? 0)} archived compare filter export scope profile${Number(response?.purged_count ?? 0) === 1 ? "" : "s"}.`
      );
    } catch {
      setSimPresetMessage("Failed to purge compare filter export scope archive.");
    }
  };

  const handleExportCompareHistoryFilterExportProfiles = async () => {
    try {
      const blob = await exportWhatIfCompareFilterExportProfiles();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compare_filter_export_profiles.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSimPresetMessage(`Exported ${simCompareFilterExportProfiles.length} compare filter export scope profile${simCompareFilterExportProfiles.length === 1 ? "" : "s"}.`);
    } catch {
      setSimPresetMessage("Failed to export compare filter export scope profiles.");
    }
  };

  const handleImportCompareHistoryFilterExportProfilesFile = async (event) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    try { event.target.value = ""; } catch (_) { /* file input reset not supported in all envs */ }
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setSimPresetMessage("Export scope import failed: file is not valid JSON.");
      return;
    }

    const rawProfiles = Array.isArray(parsed) ? parsed : parsed?.profiles;
    if (!Array.isArray(rawProfiles)) {
      setSimPresetMessage("Export scope import failed: JSON must contain a 'profiles' array.");
      return;
    }

    const replaceMode = window.confirm("Use replace mode for export scope profile import? Click Cancel for merge mode.");
    const mode = replaceMode ? "replace" : "merge";

    try {
      const response = await importWhatIfCompareFilterExportProfiles(rawProfiles, mode);
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareFilterExportProfiles;
      setSimCompareFilterExportProfiles(nextProfiles);
      setSimPresetMessage(
        `Imported ${Number(response?.imported_count ?? 0)} compare filter export scope profile${Number(response?.imported_count ?? 0) === 1 ? "" : "s"} (${mode}).`
      );
    } catch {
      setSimPresetMessage("Failed to import compare filter export scope profiles.");
    }
  };

  const handleImportCompareHistoryFilterPresetsFile = async (event) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    // Reset input so the same file can be re-imported after clearing.
    try { event.target.value = ""; } catch (_) { /* file input reset not supported in all envs */ }
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setSimPresetMessage("Import failed: file is not valid JSON.");
      return;
    }

    const rawPresets = Array.isArray(parsed) ? parsed : parsed?.presets;
    if (!Array.isArray(rawPresets)) {
      setSimPresetMessage("Import failed: JSON must contain a 'presets' array.");
      return;
    }

    const validationSummary = buildCompareImportValidationSummary(rawPresets);
    setSimCompareImportValidationSummary(validationSummary);
    setSimCompareDryRunResult(null);
    setSimCompareImportErrors([]);

    const selectedImportMode = simCompareImportMode === "replace" ? "replace" : "merge";
    const modeLabel = selectedImportMode === "replace" ? "REPLACE" : "MERGE";
    // If dry-run is enabled, preflight without writing.
    if (simCompareDryRunImport) {
      try {
        const importTag = simCompareImportTag.trim();
        const dryResult = await importWhatIfCompareFilterPresets(
          rawPresets,
          selectedImportMode,
          {
            importTag: importTag || undefined,
            importTagMode: simCompareImportTagMode,
            ...(simCompareStrictImport ? { strictImport: true } : {}),
            dryRun: true,
          }
        );
        setSimCompareDryRunResult(dryResult);
        const dryTagNote = dryResult?.import_tag
          ? ` (tag: '${dryResult.import_tag}', mode: ${dryResult.import_tag_mode || "append"})`
          : "";
        setSimPresetMessage(
          `Dry-run preview (${modeLabel})${dryTagNote}: would import ${Number(dryResult?.would_import_count ?? 0)} (create ${Number(dryResult?.would_create_count ?? 0)}, update ${Number(dryResult?.would_update_count ?? 0)}, skip ${Number(dryResult?.would_skip_count ?? 0)}). Toggle off 'Dry-Run' to commit.`
        );
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.strict) {
          setSimPresetMessage(`Dry-run blocked by strict tag validation (${Number(detail?.tag_error_count ?? 0)} issue${Number(detail?.tag_error_count ?? 0) === 1 ? "" : "s"}).`);
        } else {
          setSimPresetMessage("Dry-run preflight failed.");
        }
      }
      return;
    }

    const strictWarning = simCompareStrictImport && validationSummary.tagErrorCount > 0
      ? `\n\nStrict mode will reject this import (${validationSummary.tagErrorCount} tag validation issue${validationSummary.tagErrorCount === 1 ? "" : "s"} detected).`
      : "";
    const modeWarning = selectedImportMode === "replace"
      ? "\n\nReplace mode will overwrite all existing compare filter presets."
      : "\n\nMerge mode will add/update imported presets without removing others.";

    if (selectedImportMode === "replace") {
      const replacePhrase = window.prompt("Type REPLACE to confirm destructive replace import.", "");
      if (String(replacePhrase || "").trim().toUpperCase() !== "REPLACE") {
        setSimPresetMessage("Replace import cancelled: confirmation phrase was not provided.");
        return;
      }
    }

    const shouldImport = window.confirm(
      `Import ${rawPresets.length} preset${rawPresets.length === 1 ? "" : "s"} using ${modeLabel}?${modeWarning}${strictWarning}\n\nClick OK to continue.\nClick Cancel to abort.`
    );
    if (!shouldImport) return;

    try {
      const importTag = simCompareImportTag.trim();
      const response = await importWhatIfCompareFilterPresets(
        rawPresets,
        selectedImportMode,
        {
          importTag: importTag || undefined,
          importTagMode: simCompareImportTagMode,
          ...(simCompareStrictImport ? { strictImport: true } : {}),
          ...(selectedImportMode === "replace" ? { replaceConfirmed: true } : {}),
        }
      );
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareFilterPresets;
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      setSimCompareImportErrors(Array.isArray(response?.errors) ? response.errors : []);
      const errNote = response?.error_count > 0 ? ` (${response.error_count} skipped due to errors)` : "";
      const tagNote = response?.import_tag
        ? ` with tag '${response.import_tag}' (${response?.import_tag_mode || "append"})`
        : "";
      const strictNote = response?.strict ? " [strict mode]" : "";
      const modeNote = response?.mode ? ` (${String(response.mode).toLowerCase()})` : "";
      setSimPresetMessage(
        `Imported ${Number(response?.imported_count ?? 0)} compare filter preset${Number(response?.imported_count ?? 0) === 1 ? "" : "s"}${modeNote}${tagNote}${errNote}${strictNote}.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.strict) {
        const strictCount = Number(detail?.tag_error_count ?? 0);
        const firstError = Array.isArray(detail?.errors) && detail.errors.length > 0
          ? detail.errors[0]
          : null;
        const firstPreset = String(firstError?.name || "").trim();
        setSimPresetMessage(
          `Import blocked by strict tag validation (${strictCount} issue${strictCount === 1 ? "" : "s"}).${firstPreset ? ` First preset: ${firstPreset}.` : ""}`
        );
      } else {
        setSimPresetMessage("Failed to import compare filter presets.");
      }
    }
  };

  const handleBulkTagCompareFilterPresets = async () => {
    const tag = simCompareBulkTagValue.trim().toLowerCase();
    if (!tag) {
      setSimPresetMessage("Bulk tag: tag value is required.");
      return;
    }
    const replaceWith = simCompareBulkTagReplaceWith.trim().toLowerCase();
    if (simCompareBulkTagAction === "replace" && !replaceWith) {
      setSimPresetMessage("Bulk tag: 'Replace With' is required for replace action.");
      return;
    }
    const filterTag = simCompareTagFilter || undefined;
    try {
      const response = await bulkTagWhatIfCompareFilterPresets(
        simCompareBulkTagAction,
        tag,
        {
          ...(simCompareBulkTagAction === "replace" ? { replaceWith } : {}),
          ...(filterTag ? { filterTag } : {}),
        },
      );
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareFilterPresets;
      setSimCompareFilterPresets(nextPresets);
      const nextTags = deriveCompareFilterTagsFromPresets(nextPresets);
      setSimCompareFilterTags(nextTags);
      if (simCompareTagFilter && !nextTags.includes(simCompareTagFilter)) {
        setSimCompareTagFilter("");
      }
      const scopeNote = filterTag ? ` (scoped to tag: '${filterTag}')` : "";
      const actionLabel = simCompareBulkTagAction === "replace"
        ? `replaced '${tag}' → '${replaceWith}'`
        : `${simCompareBulkTagAction}ed tag '${tag}'`;
      setSimPresetMessage(
        `Bulk tag: ${actionLabel} on ${Number(response?.updated_count ?? 0)} preset${Number(response?.updated_count ?? 0) === 1 ? "" : "s"}${scopeNote}.`
      );
    } catch {
      setSimPresetMessage("Bulk tag operation failed.");
    }
  };

  const handleResetCompareHistoryFilterPresetUsage = async () => {
    const selectedName = simSelectedCompareFilterPreset.trim();
    try {
      const response = await resetWhatIfCompareFilterPresetUsage(selectedName || undefined);
      const nextUsage = normalizeCompareFilterPresetUsage(response?.usage);
      setSimCompareFilterPresetUsage(nextUsage);
      setSimCompareFilterPresetUsageRows(
        buildCompareFilterPresetUsageRows(
          response?.usage,
          simCompareFilterPresets,
          simCompareFilterPresetUsageViewMode
        )
      );
      if (typeof response?.enabled === "boolean") {
        setSimCompareFilterPresetUsageEnabled(Boolean(response.enabled));
      }
      setSimPresetMessage(
        selectedName
          ? `Usage reset for compare filter preset: ${selectedName}`
          : "Usage reset for all compare filter presets."
      );
    } catch {
      setSimPresetMessage("Failed to reset compare filter usage.");
    }
  };

  const _makeTogglePresetUsageEnabled = (saveFn, setEnabled, label) => async (checked) => {
    try {
      const saved = await saveFn(checked);
      const enabled = Boolean(saved?.enabled ?? checked);
      setEnabled(enabled);
      setSimPresetMessage(
        enabled ? `Compare ${label} usage telemetry enabled.` : `Compare ${label} usage telemetry disabled.`
      );
    } catch {
      setSimPresetMessage(`Failed to update compare ${label} usage telemetry setting.`);
    }
  };

  const handleToggleCompareFilterPresetUsageEnabled = _makeTogglePresetUsageEnabled(
    saveWhatIfCompareFilterPresetUsageSettings, setSimCompareFilterPresetUsageEnabled, "filter"
  );

  const handleToggleCompareSortPresetUsageEnabled = _makeTogglePresetUsageEnabled(
    saveWhatIfCompareSortPresetUsageSettings, setSimCompareSortPresetUsageEnabled, "sort"
  );

  const handleResetCompareSortPresetUsage = async (name) => {
    try {
      const result = await resetWhatIfCompareSortPresetUsage(name || "");
      const fresh = await getWhatIfCompareSortPresetUsage({
        includeZeroUsage: true,
        viewMode: simCompareSortPresetUsageViewMode,
      });
      setSimCompareSortPresetUsageRows(
        buildCompareSortPresetUsageRows(fresh, simCompareSortPresets, simCompareSortPresetUsageViewMode)
      );
      setSimPresetMessage(
        name ? `Sort preset usage reset: ${name}` : `All sort preset usage reset (${result?.deleted_count ?? 0} cleared).`
      );
    } catch {
      setSimPresetMessage("Failed to reset compare sort preset usage.");
    }
  };

  const handleToggleCompareViewPresetUsageEnabled = _makeTogglePresetUsageEnabled(
    saveWhatIfCompareViewPresetUsageSettings, setSimCompareViewPresetUsageEnabled, "view"
  );

  const handleResetCompareViewPresetUsage = async (name) => {
    try {
      const result = await resetWhatIfCompareViewPresetUsage(name || "");
      const fresh = await getWhatIfCompareViewPresetUsage({
        includeZeroUsage: true,
        viewMode: simCompareViewPresetUsageViewMode,
      });
      setSimCompareViewPresetUsageRows(
        buildCompareViewPresetUsageRows(fresh, simCompareViewPresets, simCompareViewPresetUsageViewMode)
      );
      setSimPresetMessage(
        name ? `View preset usage reset: ${name}` : `All view preset usage reset (${result?.deleted_count ?? 0} cleared).`
      );
    } catch {
      setSimPresetMessage("Failed to reset compare view preset usage.");
    }
  };

  const handleSaveCompareHistorySortPreset = async () => {
    const name = simCompareSortPresetName.trim();
    const sortBy = simCompareHistorySortBy;
    const sortOrder = simCompareHistorySortOrder;
    const exists = simCompareSortPresets.some(
      (preset) => String(preset?.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    if (!name) {
      setSimPresetMessage("Compare sort preset name is required.");
      return;
    }

    try {
      const saved = await upsertWhatIfCompareSortPreset({
        name,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const nextPresets = Array.isArray(saved?.presets)
        ? saved.presets
        : [
            ...simCompareSortPresets.filter(
              (preset) => String(preset.name).toLowerCase() !== name.toLowerCase()
            ),
            {
              name,
              sort_by: sortBy,
              sort_order: sortOrder,
            },
          ];
      setSimCompareSortPresets(nextPresets);
      setSimSelectedCompareSortPreset(name);
      setSimPresetMessage(
        saved?.action === "overwritten" || exists
          ? `Compare sort preset updated: ${name}`
          : `Compare sort preset saved: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to save compare sort preset.");
    }
  };

  const handleLoadCompareHistorySortPreset = () => {
    if (!simSelectedCompareSortPreset) {
      setSimPresetMessage("Select a compare sort preset to load.");
      return;
    }
    const preset = simCompareSortPresets.find((entry) => entry.name === simSelectedCompareSortPreset);
    if (!preset) {
      setSimPresetMessage("Unable to find selected compare sort preset.");
      return;
    }
    const nextSortBy = String(preset.sort_by || "as_of");
    const nextSortOrder = String(preset.sort_order || "desc");
    setSimCompareSortPresetName(preset.name || "");
    setSimCompareHistorySortBy(nextSortBy);
    setSimCompareHistorySortOrder(nextSortOrder);
    setSimPresetMessage(`Compare sort preset loaded: ${preset.name}`);
    // Fire-and-forget usage tracking
    if (simCompareSortPresetUsageEnabled) {
      Promise.resolve(trackWhatIfCompareSortPresetUsage(preset.name, "load")).then((result) => {
        if (Array.isArray(result?.usage_summary)) {
          setSimCompareSortPresetUsageRows(
            buildCompareSortPresetUsageRows(result.usage_summary, simCompareSortPresets, simCompareSortPresetUsageViewMode)
          );
        }
      }).catch(() => {});
    }
  };

  const handleDeleteCompareHistorySortPreset = async () => {
    const name = simSelectedCompareSortPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare sort preset to delete.");
      return;
    }

    try {
      const response = await deleteWhatIfCompareSortPreset(name);
      const nextPresets = Array.isArray(response?.presets)
        ? response.presets
        : simCompareSortPresets.filter(
            (preset) => String(preset?.name || "").trim().toLowerCase() !== name.toLowerCase()
          );
      setSimCompareSortPresets(nextPresets);
      setSimCompareSortPresetArchive(Array.isArray(response?.archive) ? response.archive : simCompareSortPresetArchive);
      setSimSelectedCompareSortPreset("");
      setSimCompareSortPresetName("");
      setSimPresetMessage(
        response?.deleted
          ? `Compare sort preset deleted: ${name}`
          : `Compare sort preset not found: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to delete compare sort preset.");
    }
  };

  const handleRestoreCompareHistorySortPreset = async () => {
    const archive = simCompareSortPresetArchive;
    if (!archive || archive.length === 0) {
      setSimPresetMessage("No archived compare sort presets to restore.");
      return;
    }
    const last = archive[archive.length - 1];
    const name = last?.name || "";
    if (!name) {
      setSimPresetMessage("Cannot restore: archived sort preset has no name.");
      return;
    }
    try {
      const response = await restoreWhatIfCompareSortPreset(name);
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareSortPresets;
      setSimCompareSortPresets(nextPresets);
      setSimCompareSortPresetArchive(Array.isArray(response?.archive) ? response.archive : []);
      setSimSelectedCompareSortPreset(name);
      setSimCompareSortPresetName(name);
      setSimPresetMessage(`Restored sort preset "${name}" from archive.`);
    } catch {
      setSimPresetMessage("Failed to restore compare sort preset.");
    }
  };

  const handlePurgeCompareHistorySortPresetArchive = async () => {
    try {
      const cutoff = simCompareSortPresetPurgeDeletedBefore.trim() || undefined;
      const response = await purgeWhatIfCompareSortPresetArchive(cutoff);
      setSimCompareSortPresetArchive(Array.isArray(response?.archive) ? response.archive : []);
      const count = response?.purged_count ?? 0;
      setSimPresetMessage(`Purged ${count} compare sort preset(s) from archive.`);
    } catch {
      setSimPresetMessage("Failed to purge compare sort preset archive.");
    }
  };

  const handleSaveCompareHistoryViewPreset = async () => {
    const name = simCompareViewPresetName.trim();
    const quickRange = simCompareHistoryQuickRange;
    const sortBy = simCompareHistorySortBy;
    const sortOrder = simCompareHistorySortOrder;
    const presetALabel = simCompareHistoryPresetAFilter.trim();
    const presetBLabel = simCompareHistoryPresetBFilter.trim();
    const fromAsOf = simCompareHistoryFromAsOfFilter.trim();
    const toAsOf = simCompareHistoryToAsOfFilter.trim();
    const exists = simCompareViewPresets.some(
      (preset) => String(preset?.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    if (!name) {
      setSimPresetMessage("Compare view preset name is required.");
      return;
    }

    try {
      const saved = await upsertWhatIfCompareViewPreset({
        name,
        preset_a_label: presetALabel,
        preset_b_label: presetBLabel,
        quick_range: quickRange,
        from_as_of: fromAsOf,
        to_as_of: toAsOf,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const nextPresets = Array.isArray(saved?.presets)
        ? saved.presets
        : [
            ...simCompareViewPresets.filter(
              (preset) => String(preset.name).toLowerCase() !== name.toLowerCase()
            ),
            {
              name,
              preset_a_label: presetALabel,
              preset_b_label: presetBLabel,
              quick_range: quickRange,
              from_as_of: fromAsOf,
              to_as_of: toAsOf,
              sort_by: sortBy,
              sort_order: sortOrder,
            },
          ];
      setSimCompareViewPresets(nextPresets);
      setSimSelectedCompareViewPreset(name);
      setSimPresetMessage(
        saved?.action === "overwritten" || exists
          ? `Compare view preset updated: ${name}`
          : `Compare view preset saved: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to save compare view preset.");
    }
  };

  const handleLoadCompareHistoryViewPreset = async () => {
    if (!simSelectedCompareViewPreset) {
      setSimPresetMessage("Select a compare view preset to load.");
      return;
    }
    const preset = simCompareViewPresets.find((entry) => entry.name === simSelectedCompareViewPreset);
    if (!preset) {
      setSimPresetMessage("Unable to find selected compare view preset.");
      return;
    }

    const quickRange = String(preset.quick_range || "custom");
    const sortBy = String(preset.sort_by || "as_of");
    const sortOrder = String(preset.sort_order || "desc");
    const fromAsOf = String(preset.from_as_of || "");
    const toAsOf = String(preset.to_as_of || "");

    setSimCompareViewPresetName(preset.name || "");
    setSimCompareHistoryPresetAFilter(String(preset.preset_a_label || ""));
    setSimCompareHistoryPresetBFilter(String(preset.preset_b_label || ""));
    setSimCompareHistoryQuickRange(quickRange);
    setSimCompareHistoryFromAsOfFilter(quickRange === "custom" ? fromAsOf : "");
    setSimCompareHistoryToAsOfFilter(quickRange === "custom" ? toAsOf : "");
    setSimCompareHistorySortBy(sortBy);
    setSimCompareHistorySortOrder(sortOrder);

    await loadWhatIfCompareHistory({
      limit: 20,
      presetALabel: String(preset.preset_a_label || "") || undefined,
      presetBLabel: String(preset.preset_b_label || "") || undefined,
      fromAsOf: quickRange === "custom" ? (fromAsOf || undefined) : undefined,
      toAsOf: quickRange === "custom" ? (toAsOf || undefined) : undefined,
      window: quickRange === "custom" ? undefined : quickRange,
      sortBy,
      sortOrder,
      page: 1,
      pageSize: simCompareHistoryPageSize,
    });

    setSimPresetMessage(`Compare view preset loaded: ${preset.name}`);
    // Fire-and-forget usage tracking
    if (simCompareViewPresetUsageEnabled) {
      Promise.resolve(trackWhatIfCompareViewPresetUsage(preset.name, "load")).then((result) => {
        if (Array.isArray(result?.usage_summary)) {
          setSimCompareViewPresetUsageRows(
            buildCompareViewPresetUsageRows(result.usage_summary, simCompareViewPresets, simCompareViewPresetUsageViewMode)
          );
        }
      }).catch(() => {});
    }
  };

  const handleDeleteCompareHistoryViewPreset = async () => {
    const name = simSelectedCompareViewPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare view preset to delete.");
      return;
    }

    try {
      const response = await deleteWhatIfCompareViewPreset(name);
      const nextPresets = Array.isArray(response?.presets)
        ? response.presets
        : simCompareViewPresets.filter(
            (preset) => String(preset?.name || "").trim().toLowerCase() !== name.toLowerCase()
          );
      setSimCompareViewPresets(nextPresets);
      setSimCompareViewPresetArchive(Array.isArray(response?.archive) ? response.archive : simCompareViewPresetArchive);
      setSimSelectedCompareViewPreset("");
      setSimCompareViewPresetName("");
      setSimPresetMessage(
        response?.deleted
          ? `Compare view preset deleted: ${name}`
          : `Compare view preset not found: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to delete compare view preset.");
    }
  };

  const handleRestoreCompareHistoryViewPreset = async () => {
    const archive = simCompareViewPresetArchive;
    if (!archive || archive.length === 0) {
      setSimPresetMessage("No archived compare view presets to restore.");
      return;
    }
    const last = archive[archive.length - 1];
    const name = last?.name || "";
    if (!name) {
      setSimPresetMessage("Cannot restore: archived view preset has no name.");
      return;
    }
    try {
      const response = await restoreWhatIfCompareViewPreset(name);
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareViewPresets;
      setSimCompareViewPresets(nextPresets);
      setSimCompareViewPresetArchive(Array.isArray(response?.archive) ? response.archive : []);
      setSimSelectedCompareViewPreset(name);
      setSimCompareViewPresetName(name);
      setSimPresetMessage(`Restored view preset "${name}" from archive.`);
    } catch {
      setSimPresetMessage("Failed to restore compare view preset.");
    }
  };

  const handlePurgeCompareHistoryViewPresetArchive = async () => {
    try {
      const cutoff = simCompareViewPresetPurgeDeletedBefore.trim() || undefined;
      const response = await purgeWhatIfCompareViewPresetArchive(cutoff);
      setSimCompareViewPresetArchive(Array.isArray(response?.archive) ? response.archive : []);
      const count = response?.purged_count ?? 0;
      setSimPresetMessage(`Purged ${count} compare view preset(s) from archive.`);
    } catch {
      setSimPresetMessage("Failed to purge compare view preset archive.");
    }
  };

  const handleExportCompareSortPresets = async () => {
    try {
      const blob = await exportWhatIfCompareSortPresets();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compare_sort_presets.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSimPresetMessage(`Exported ${simCompareSortPresets.length} compare sort preset${simCompareSortPresets.length === 1 ? "" : "s"}.`);
    } catch {
      setSimPresetMessage("Failed to export compare sort presets.");
    }
  };

  const handleImportCompareSortPresetsFile = async (event) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    try { event.target.value = ""; } catch (_) { /* file input reset not supported in all envs */ }
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setSimPresetMessage("Sort preset import failed: file is not valid JSON.");
      return;
    }

    const rawPresets = Array.isArray(parsed) ? parsed : parsed?.presets;
    if (!Array.isArray(rawPresets)) {
      setSimPresetMessage("Sort preset import failed: JSON must contain a 'presets' array.");
      return;
    }

    setSimCompareSortDryRunResult(null);
    setSimCompareSortImportErrors([]);

    const selectedMode = simCompareSortImportMode === "replace" ? "replace" : "merge";
    const modeLabel = selectedMode === "replace" ? "REPLACE" : "MERGE";

    if (simCompareSortDryRunImport) {
      try {
        const dryResult = await importWhatIfCompareSortPresets(rawPresets, selectedMode, { dryRun: true });
        setSimCompareSortDryRunResult(dryResult);
        setSimPresetMessage(
          `Sort dry-run (${modeLabel}): would import ${Number(dryResult?.would_import_count ?? 0)} (create ${Number(dryResult?.would_create_count ?? 0)}, update ${Number(dryResult?.would_update_count ?? 0)}, skip ${Number(dryResult?.would_skip_count ?? 0)}). Toggle off 'Dry-Run' to commit.`
        );
      } catch {
        setSimPresetMessage("Sort preset dry-run preflight failed.");
      }
      return;
    }

    if (selectedMode === "replace") {
      const replacePhrase = window.prompt("Type REPLACE to confirm destructive replace import.", "");
      if (String(replacePhrase || "").trim().toUpperCase() !== "REPLACE") {
        setSimPresetMessage("Sort preset replace import cancelled: confirmation phrase not provided.");
        return;
      }
    }

    const confirmed = window.confirm(
      `Import ${rawPresets.length} sort preset${rawPresets.length === 1 ? "" : "s"} using ${modeLabel}?${selectedMode === "replace" ? "\n\nReplace mode will overwrite all existing sort presets." : "\n\nMerge mode will add/update imported presets without removing others."}\n\nClick OK to continue.`
    );
    if (!confirmed) return;

    try {
      const response = await importWhatIfCompareSortPresets(
        rawPresets,
        selectedMode,
        { ...(selectedMode === "replace" ? { replaceConfirmed: true } : {}) },
      );
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareSortPresets;
      setSimCompareSortPresets(nextPresets);
      setSimCompareSortImportErrors(Array.isArray(response?.errors) ? response.errors : []);
      const errNote = response?.error_count > 0 ? ` (${response.error_count} skipped due to errors)` : "";
      setSimPresetMessage(
        `Imported ${Number(response?.imported_count ?? 0)} sort preset${Number(response?.imported_count ?? 0) === 1 ? "" : "s"} (${String(response?.mode || selectedMode)})${errNote}.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.replace_confirmed_required) {
        setSimPresetMessage("Sort preset replace import requires explicit confirmation.");
      } else {
        setSimPresetMessage("Failed to import sort presets.");
      }
    }
  };

  const handleExportCompareViewPresets = async () => {
    try {
      const blob = await exportWhatIfCompareViewPresets();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compare_view_presets.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSimPresetMessage(`Exported ${simCompareViewPresets.length} compare view preset${simCompareViewPresets.length === 1 ? "" : "s"}.`);
    } catch {
      setSimPresetMessage("Failed to export compare view presets.");
    }
  };

  const handleImportCompareViewPresetsFile = async (event) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    try { event.target.value = ""; } catch (_) { /* file input reset not supported in all envs */ }
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setSimPresetMessage("View preset import failed: file is not valid JSON.");
      return;
    }

    const rawPresets = Array.isArray(parsed) ? parsed : parsed?.presets;
    if (!Array.isArray(rawPresets)) {
      setSimPresetMessage("View preset import failed: JSON must contain a 'presets' array.");
      return;
    }

    setSimCompareViewDryRunResult(null);
    setSimCompareViewImportErrors([]);

    const selectedMode = simCompareViewImportMode === "replace" ? "replace" : "merge";
    const modeLabel = selectedMode === "replace" ? "REPLACE" : "MERGE";

    if (simCompareViewDryRunImport) {
      try {
        const dryResult = await importWhatIfCompareViewPresets(rawPresets, selectedMode, { dryRun: true });
        setSimCompareViewDryRunResult(dryResult);
        setSimPresetMessage(
          `View dry-run (${modeLabel}): would import ${Number(dryResult?.would_import_count ?? 0)} (create ${Number(dryResult?.would_create_count ?? 0)}, update ${Number(dryResult?.would_update_count ?? 0)}, skip ${Number(dryResult?.would_skip_count ?? 0)}). Toggle off 'Dry-Run' to commit.`
        );
      } catch {
        setSimPresetMessage("View preset dry-run preflight failed.");
      }
      return;
    }

    if (selectedMode === "replace") {
      const replacePhrase = window.prompt("Type REPLACE to confirm destructive replace import.", "");
      if (String(replacePhrase || "").trim().toUpperCase() !== "REPLACE") {
        setSimPresetMessage("View preset replace import cancelled: confirmation phrase not provided.");
        return;
      }
    }

    const confirmed = window.confirm(
      `Import ${rawPresets.length} view preset${rawPresets.length === 1 ? "" : "s"} using ${modeLabel}?${selectedMode === "replace" ? "\n\nReplace mode will overwrite all existing view presets." : "\n\nMerge mode will add/update imported presets without removing others."}\n\nClick OK to continue.`
    );
    if (!confirmed) return;

    try {
      const response = await importWhatIfCompareViewPresets(
        rawPresets,
        selectedMode,
        { ...(selectedMode === "replace" ? { replaceConfirmed: true } : {}) },
      );
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareViewPresets;
      setSimCompareViewPresets(nextPresets);
      setSimCompareViewImportErrors(Array.isArray(response?.errors) ? response.errors : []);
      const errNote = response?.error_count > 0 ? ` (${response.error_count} skipped due to errors)` : "";
      setSimPresetMessage(
        `Imported ${Number(response?.imported_count ?? 0)} view preset${Number(response?.imported_count ?? 0) === 1 ? "" : "s"} (${String(response?.mode || selectedMode)})${errNote}.`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.replace_confirmed_required) {
        setSimPresetMessage("View preset replace import requires explicit confirmation.");
      } else {
        setSimPresetMessage("Failed to import view presets.");
      }
    }
  };

  const handleSaveCompareHistoryExportProfile = async () => {
    const name = simCompareExportProfileName.trim();
    const presetALabel = simCompareHistoryPresetAFilter.trim();
    const presetBLabel = simCompareHistoryPresetBFilter.trim();
    const quickRange = simCompareHistoryQuickRange;
    const fromAsOf = simCompareHistoryFromAsOfFilter.trim();
    const toAsOf = simCompareHistoryToAsOfFilter.trim();
    const exportMode = simCompareHistoryExportMode;
    const exists = simCompareExportProfiles.some(
      (profile) => String(profile?.name || "").trim().toLowerCase() === name.toLowerCase()
    );

    if (!name) {
      setSimPresetMessage("Compare export profile name is required.");
      return;
    }

    try {
      const saved = await upsertWhatIfCompareExportProfile({
        name,
        export_mode: exportMode,
        preset_a_label: presetALabel,
        preset_b_label: presetBLabel,
        quick_range: quickRange,
        from_as_of: fromAsOf,
        to_as_of: toAsOf,
      });
      const nextProfiles = Array.isArray(saved?.profiles)
        ? saved.profiles
        : [
            ...simCompareExportProfiles.filter(
              (profile) => String(profile.name).toLowerCase() !== name.toLowerCase()
            ),
            {
              name,
              export_mode: exportMode,
              preset_a_label: presetALabel,
              preset_b_label: presetBLabel,
              quick_range: quickRange,
              from_as_of: fromAsOf,
              to_as_of: toAsOf,
            },
          ];
      setSimCompareExportProfiles(nextProfiles);
      setSimSelectedCompareExportProfile(name);
      setSimPresetMessage(
        saved?.action === "overwritten" || exists
          ? `Compare export profile updated: ${name}`
          : `Compare export profile saved: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to save compare export profile.");
    }
  };

  const handleLoadCompareHistoryExportProfile = () => {
    if (!simSelectedCompareExportProfile) {
      setSimPresetMessage("Select a compare export profile to load.");
      return;
    }
    const profile = simCompareExportProfiles.find((entry) => entry.name === simSelectedCompareExportProfile);
    if (!profile) {
      setSimPresetMessage("Unable to find selected compare export profile.");
      return;
    }

    const quickRange = String(profile.quick_range || "custom");
    const fromAsOf = String(profile.from_as_of || "");
    const toAsOf = String(profile.to_as_of || "");
    const exportMode = String(profile.export_mode || "all");

    setSimCompareExportProfileName(profile.name || "");
    setSimCompareHistoryExportMode(exportMode === "page" ? "page" : "all");
    setSimCompareHistoryPresetAFilter(String(profile.preset_a_label || ""));
    setSimCompareHistoryPresetBFilter(String(profile.preset_b_label || ""));
    setSimCompareHistoryQuickRange(quickRange);
    setSimCompareHistoryFromAsOfFilter(quickRange === "custom" ? fromAsOf : "");
    setSimCompareHistoryToAsOfFilter(quickRange === "custom" ? toAsOf : "");
    setSimPresetMessage(`Compare export profile loaded: ${profile.name}`);
  };

  const handleDeleteCompareHistoryExportProfile = async () => {
    const name = simSelectedCompareExportProfile.trim();
    if (!name) {
      setSimPresetMessage("Select a compare export profile to delete.");
      return;
    }

    try {
      const response = await deleteWhatIfCompareExportProfile(name);
      const nextProfiles = Array.isArray(response?.profiles)
        ? response.profiles
        : simCompareExportProfiles.filter(
            (profile) => String(profile?.name || "").trim().toLowerCase() !== name.toLowerCase()
          );
      setSimCompareExportProfiles(nextProfiles);
      setSimCompareExportProfileArchive(Array.isArray(response?.archive) ? response.archive : simCompareExportProfileArchive);
      setSimSelectedCompareExportProfile("");
      setSimCompareExportProfileName("");
      setSimPresetMessage(
        response?.deleted
          ? `Compare export profile deleted: ${name}`
          : `Compare export profile not found: ${name}`
      );
    } catch {
      setSimPresetMessage("Failed to delete compare export profile.");
    }
  };

  const handleCloneCompareHistorySortPreset = async () => {
    const name = simSelectedCompareSortPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare sort preset to clone.");
      return;
    }
    const newName = window.prompt(`Clone "${name}" — enter a new name for the clone:`, `${name} (copy)`);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Clone cancelled: no name provided.");
      return;
    }
    try {
      const response = await cloneWhatIfCompareSortPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareSortPresets;
      setSimCompareSortPresets(nextPresets);
      setSimSelectedCompareSortPreset(response?.new_name || newName.trim());
      setSimCompareSortPresetName(response?.new_name || newName.trim());
      setSimPresetMessage(`Cloned sort preset "${name}" as "${response?.new_name || newName.trim()}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Clone failed: a sort preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to clone compare sort preset.");
      }
    }
  };

  const handleRenameCompareHistorySortPreset = async () => {
    const name = simSelectedCompareSortPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare sort preset to rename.");
      return;
    }
    const newName = window.prompt(`Rename "${name}" — enter the new name:`, name);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Rename cancelled: no name provided.");
      return;
    }
    if (newName.trim() === name) {
      setSimPresetMessage("Rename cancelled: new name is the same as the current name.");
      return;
    }
    try {
      const response = await renameWhatIfCompareSortPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareSortPresets;
      setSimCompareSortPresets(nextPresets);
      const resolvedName = response?.new_name || newName.trim();
      setSimSelectedCompareSortPreset(resolvedName);
      setSimCompareSortPresetName(resolvedName);
      setSimPresetMessage(`Renamed sort preset "${name}" to "${resolvedName}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Rename failed: a sort preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to rename compare sort preset.");
      }
    }
  };

  const handleCloneCompareHistoryViewPreset = async () => {
    const name = simSelectedCompareViewPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare view preset to clone.");
      return;
    }
    const newName = window.prompt(`Clone "${name}" — enter a new name for the clone:`, `${name} (copy)`);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Clone cancelled: no name provided.");
      return;
    }
    try {
      const response = await cloneWhatIfCompareViewPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareViewPresets;
      setSimCompareViewPresets(nextPresets);
      setSimSelectedCompareViewPreset(response?.new_name || newName.trim());
      setSimCompareViewPresetName(response?.new_name || newName.trim());
      setSimPresetMessage(`Cloned view preset "${name}" as "${response?.new_name || newName.trim()}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Clone failed: a view preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to clone compare view preset.");
      }
    }
  };

  const handleRenameCompareHistoryViewPreset = async () => {
    const name = simSelectedCompareViewPreset.trim();
    if (!name) {
      setSimPresetMessage("Select a compare view preset to rename.");
      return;
    }
    const newName = window.prompt(`Rename "${name}" — enter the new name:`, name);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Rename cancelled: no name provided.");
      return;
    }
    if (newName.trim() === name) {
      setSimPresetMessage("Rename cancelled: new name is the same as the current name.");
      return;
    }
    try {
      const response = await renameWhatIfCompareViewPreset(name, newName.trim());
      const nextPresets = Array.isArray(response?.presets) ? response.presets : simCompareViewPresets;
      setSimCompareViewPresets(nextPresets);
      const resolvedName = response?.new_name || newName.trim();
      setSimSelectedCompareViewPreset(resolvedName);
      setSimCompareViewPresetName(resolvedName);
      setSimPresetMessage(`Renamed view preset "${name}" to "${resolvedName}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Rename failed: a view preset named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to rename compare view preset.");
      }
    }
  };

  const handleCloneCompareHistoryExportProfile = async () => {
    const name = simSelectedCompareExportProfile.trim();
    if (!name) {
      setSimPresetMessage("Select a compare export profile to clone.");
      return;
    }
    const newName = window.prompt(`Clone "${name}" — enter a new name for the clone:`, `${name} (copy)`);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Clone cancelled: no name provided.");
      return;
    }
    try {
      const response = await cloneWhatIfCompareExportProfile(name, newName.trim());
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareExportProfiles;
      setSimCompareExportProfiles(nextProfiles);
      setSimSelectedCompareExportProfile(response?.new_name || newName.trim());
      setSimCompareExportProfileName(response?.new_name || newName.trim());
      setSimPresetMessage(`Cloned export profile "${name}" as "${response?.new_name || newName.trim()}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Clone failed: an export profile named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to clone compare export profile.");
      }
    }
  };

  const handleRenameCompareHistoryExportProfile = async () => {
    const name = simSelectedCompareExportProfile.trim();
    if (!name) {
      setSimPresetMessage("Select a compare export profile to rename.");
      return;
    }
    const newName = window.prompt(`Rename "${name}" — enter the new name:`, name);
    if (!newName || !newName.trim()) {
      setSimPresetMessage("Rename cancelled: no name provided.");
      return;
    }
    if (newName.trim() === name) {
      setSimPresetMessage("Rename cancelled: new name is the same as the current name.");
      return;
    }
    try {
      const response = await renameWhatIfCompareExportProfile(name, newName.trim());
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareExportProfiles;
      setSimCompareExportProfiles(nextProfiles);
      const resolvedName = response?.new_name || newName.trim();
      setSimSelectedCompareExportProfile(resolvedName);
      setSimCompareExportProfileName(resolvedName);
      setSimPresetMessage(`Renamed export profile "${name}" to "${resolvedName}".`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("already exists")) {
        setSimPresetMessage(`Rename failed: an export profile named "${newName.trim()}" already exists.`);
      } else {
        setSimPresetMessage("Failed to rename compare export profile.");
      }
    }
  };

  const handleRestoreCompareHistoryExportProfile = async () => {
    const archive = simCompareExportProfileArchive;
    if (!archive || archive.length === 0) {
      setSimPresetMessage("No archived compare export profiles to restore.");
      return;
    }
    const last = archive[archive.length - 1];
    const name = last?.name || "";
    if (!name) {
      setSimPresetMessage("Cannot restore: archived profile has no name.");
      return;
    }
    try {
      const response = await restoreWhatIfCompareExportProfile(name);
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareExportProfiles;
      setSimCompareExportProfiles(nextProfiles);
      setSimCompareExportProfileArchive(Array.isArray(response?.archive) ? response.archive : []);
      setSimSelectedCompareExportProfile(name);
      setSimCompareExportProfileName(name);
      setSimPresetMessage(`Restored export profile "${name}" from archive.`);
    } catch (err) {
      setSimPresetMessage("Failed to restore compare export profile.");
    }
  };

  const handlePurgeCompareHistoryExportProfileArchive = async () => {
    try {
      const cutoff = simCompareExportProfilePurgeDeletedBefore.trim() || undefined;
      const response = await purgeWhatIfCompareExportProfileArchive(cutoff);
      setSimCompareExportProfileArchive(Array.isArray(response?.archive) ? response.archive : []);
      const count = response?.purged_count ?? 0;
      setSimPresetMessage(`Purged ${count} compare export profile(s) from archive.`);
    } catch (err) {
      setSimPresetMessage("Failed to purge compare export profile archive.");
    }
  };

  const handleExportCompareHistoryExportProfiles = async () => {
    try {
      const blob = await exportWhatIfCompareExportProfiles();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "compare_export_profiles.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSimPresetMessage(`Exported ${simCompareExportProfiles.length} compare export profile${simCompareExportProfiles.length === 1 ? "" : "s"}.`);
    } catch {
      setSimPresetMessage("Failed to export compare export profiles.");
    }
  };

  const handleImportCompareHistoryExportProfilesFile = async (event) => {
    const file = event.target.files?.[0];
    if (!event.target) return;
    try { event.target.value = ""; } catch (_) { /* file input reset not supported in all envs */ }
    if (!file) return;

    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
    } catch {
      setSimPresetMessage("Export profile import failed: file is not valid JSON.");
      return;
    }

    const rawProfiles = Array.isArray(parsed) ? parsed : parsed?.profiles;
    if (!Array.isArray(rawProfiles)) {
      setSimPresetMessage("Export profile import failed: JSON must contain a 'profiles' array.");
      return;
    }

    const replaceMode = window.confirm("Use replace mode for compare export profile import? Click Cancel for merge mode.");
    const mode = replaceMode ? "replace" : "merge";

    if (mode === "replace") {
      const replacePhrase = window.prompt("Type REPLACE to confirm destructive replace import.", "");
      if (String(replacePhrase || "").trim().toUpperCase() !== "REPLACE") {
        setSimPresetMessage("Replace import cancelled: confirmation phrase was not provided.");
        return;
      }
    }

    try {
      const response = mode === "replace"
        ? await importWhatIfCompareExportProfiles(rawProfiles, mode, { replaceConfirmed: true })
        : await importWhatIfCompareExportProfiles(rawProfiles, mode);
      const nextProfiles = Array.isArray(response?.profiles) ? response.profiles : simCompareExportProfiles;
      setSimCompareExportProfiles(nextProfiles);
      setSimPresetMessage(
        `Imported ${Number(response?.imported_count ?? 0)} compare export profile${Number(response?.imported_count ?? 0) === 1 ? "" : "s"} (${mode}).`
      );
    } catch {
      setSimPresetMessage("Failed to import compare export profiles.");
    }
  };

  const handleToggleCompareHistoryRowExpand = async (index) => {
    const isCurrentlyExpanded = simCompareHistoryExpandedRows[index] || false;
    
    if (isCurrentlyExpanded) {
      // Collapse the row
      setSimCompareHistoryExpandedRows((prev) => ({
        ...prev,
        [index]: false,
      }));
    } else {
      // Seed note state from row data if not already in state
      if (simCompareHistoryNotes[index] === undefined) {
        const existingNote = simCompareHistory[index]?.note ?? "";
        setSimCompareHistoryNotes((prev) => ({ ...prev, [index]: existingNote }));
      }

      // Expand and load positions if not already loaded
      if (!simCompareHistoryPositions[index]) {
        setSimCompareHistoryPositionsLoading((prev) => ({
          ...prev,
          [index]: true,
        }));

        try {
          const positions = await getWhatIfCompareHistoryPositions(index);
          setSimCompareHistoryPositions((prev) => ({
            ...prev,
            [index]: positions,
          }));
        } catch (error) {
          console.error("Failed to load position details:", error);
        } finally {
          setSimCompareHistoryPositionsLoading((prev) => ({
            ...prev,
            [index]: false,
          }));
        }
      }

      setSimCompareHistoryExpandedRows((prev) => ({
        ...prev,
        [index]: true,
      }));
    }
  };

  const handleSaveCompareHistoryNote = async (index) => {
    const note = simCompareHistoryNotes[index] ?? "";
    setSimCompareHistoryNoteSaving((prev) => ({ ...prev, [index]: true }));
    try {
      const result = await patchWhatIfCompareHistoryNote(index, note);
      const savedNote = result?.note ?? note;
      setSimCompareHistoryNotes((prev) => ({ ...prev, [index]: savedNote }));
      setSimCompareHistory((prev) =>
        prev.map((entry, entryIndex) =>
          entryIndex === index
            ? { ...entry, note: savedNote }
            : entry
        )
      );
    } catch {
      // Silently fail; note stays editable
    } finally {
      setSimCompareHistoryNoteSaving((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleApplyCompareHistoryFilters = async () => {
    const quickWindow = simCompareHistoryQuickRange === "custom" ? undefined : simCompareHistoryQuickRange;
    await loadWhatIfCompareHistory({
      limit: 20,
      presetALabel: simCompareHistoryPresetAFilter.trim() || undefined,
      presetBLabel: simCompareHistoryPresetBFilter.trim() || undefined,
      fromAsOf: quickWindow ? undefined : simCompareHistoryFromAsOfFilter.trim() || undefined,
      toAsOf: quickWindow ? undefined : simCompareHistoryToAsOfFilter.trim() || undefined,
      window: quickWindow,
      sortBy: simCompareHistorySortBy,
      sortOrder: simCompareHistorySortOrder,
      page: 1,
      pageSize: simCompareHistoryPageSize,
      noteContains: simCompareHistoryNoteFilter.trim() || undefined,
      minDeltaMarketValue: simCompareHistoryMinDeltaMvFilter.trim() || undefined,
      minDeltaUnrealizedPl: simCompareHistoryMinDeltaPlFilter.trim() || undefined,
    });

    const presetContext = simSelectedCompareFilterPreset || "custom filters";
    const bannerParts = [
      simCompareHistoryPresetAFilter.trim() ? `A=${simCompareHistoryPresetAFilter.trim()}` : null,
      simCompareHistoryPresetBFilter.trim() ? `B=${simCompareHistoryPresetBFilter.trim()}` : null,
      simCompareHistoryNoteFilter.trim() ? `Note contains "${simCompareHistoryNoteFilter.trim()}"` : null,
      simCompareHistoryMinDeltaMvFilter.trim() ? `|Delta MV|>=${simCompareHistoryMinDeltaMvFilter.trim()}` : null,
      simCompareHistoryMinDeltaPlFilter.trim() ? `|Delta P/L|>=${simCompareHistoryMinDeltaPlFilter.trim()}` : null,
      quickWindow ? `Window=${quickWindow}` : null,
      !quickWindow && simCompareHistoryFromAsOfFilter.trim() ? `From=${simCompareHistoryFromAsOfFilter.trim()}` : null,
      !quickWindow && simCompareHistoryToAsOfFilter.trim() ? `To=${simCompareHistoryToAsOfFilter.trim()}` : null,
      `Sort=${simCompareHistorySortBy} ${simCompareHistorySortOrder}`,
    ].filter(Boolean);

    setSimCompareFilterApplyBanner(
      bannerParts.length > 0
        ? `Applied compare filters (${presetContext}): ${bannerParts.join(", ")}`
        : "Applied compare filters."
    );
  };

  const handleClearCompareHistoryFilters = async () => {
    setSimCompareHistoryPresetAFilter("");
    setSimCompareHistoryPresetBFilter("");
    setSimCompareHistoryNoteFilter("");
    setSimCompareHistoryMinDeltaMvFilter("");
    setSimCompareHistoryMinDeltaPlFilter("");
    setSimCompareHistoryFromAsOfFilter("");
    setSimCompareHistoryToAsOfFilter("");
    setSimCompareHistoryQuickRange("custom");
    setSimCompareFilterApplyBanner("");
    await loadWhatIfCompareHistory({
      limit: 20,
      sortBy: simCompareHistorySortBy,
      sortOrder: simCompareHistorySortOrder,
      page: 1,
      pageSize: simCompareHistoryPageSize,
    });
  };

  const handleBrokerSync = async () => {
    setBrokerSyncLoading(true);
    setError("");
    setBrokerSyncMessage("");
    setBrokerSyncDetails(null);
    try {
      const payload = await syncBrokerHistory(syncDays);
      const sync = payload?.sync ?? {};
      const synced = Boolean(sync?.synced);
      const equity = sync?.equity ?? {};
      const trades = sync?.trades ?? {};
      const equityPoints = Number(equity?.points ?? 0);
      const tradeRows = Number(trades?.trades ?? 0);
      const equityReason = equity?.reason || "unknown";
      const tradeReason = trades?.reason || "unknown";
      setBrokerSyncMessage(
        synced
          ? "Broker history sync completed."
          : "Broker sync fallback used."
      );
      setBrokerSyncDetails(normalizeBrokerSyncDetails({
        synced,
        asOf: sync?.as_of || null,
        equityPoints,
        tradeRows,
        equityReason,
        tradeReason,
      }));
      await fetchAnalytics();
    } catch {
      setError("Broker history sync failed. Please verify provider key settings and retry.");
    } finally {
      setBrokerSyncLoading(false);
    }
  };

  const handleRunMaintenance = async () => {
    setMaintenanceRunLoading(true);
    setMaintenanceRunMessage("");
    try {
      const result = await runRuntimeMaintenance(false);
      if (result?.maintenance) {
        setMaintenanceStatus(result.maintenance);
      }
      setMaintenanceRunMessage("Runtime maintenance completed.");
      await fetchAnalytics();
    } catch {
      setMaintenanceRunMessage("Runtime maintenance failed.");
    } finally {
      setMaintenanceRunLoading(false);
    }
  };

  const handleBackfillHistory = async () => {
    setBackfillLoading(true);
    setBackfillMessage("");
    try {
      const result = await backfillPortfolioHistory(syncDays);
      const inserted = Number(result?.inserted ?? 0);
      setBackfillMessage(`History backfill completed (${inserted} rows processed).`);
      await fetchAnalytics();
    } catch {
      setBackfillMessage("History backfill failed.");
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [retryCount]);

  const plColor =
    analytics && analytics.total_unrealized_pl >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  const runtimeSummary = analytics?.runtime_summary ?? null;
  const historyRows = Number(runtimeSummary?.equity_history_rows ?? 0);
  const historyMaxRows = Math.max(1, Number(runtimeSummary?.equity_history_max_rows ?? 1));
  const historyUtilizationPct = (historyRows / historyMaxRows) * 100;
  const dbSizeBytes = Number(runtimeSummary?.equity_history_db_size_bytes ?? 0);
  const dbSizeMb = dbSizeBytes / (1024 * 1024);
  const runtimeHealthy = historyUtilizationPct < 90 && dbSizeMb < 100;
  const normalizeCompareFilterState = (presetLike) => ({
    preset_a_label: String(presetLike?.preset_a_label || "").trim(),
    preset_b_label: String(presetLike?.preset_b_label || "").trim(),
    note_contains: String(presetLike?.note_contains || "").trim(),
    min_delta_market_value:
      presetLike?.min_delta_market_value === null || presetLike?.min_delta_market_value === undefined || presetLike?.min_delta_market_value === ""
        ? null
        : Number(presetLike?.min_delta_market_value),
    min_delta_unrealized_pl:
      presetLike?.min_delta_unrealized_pl === null || presetLike?.min_delta_unrealized_pl === undefined || presetLike?.min_delta_unrealized_pl === ""
        ? null
        : Number(presetLike?.min_delta_unrealized_pl),
    quick_range: String(presetLike?.quick_range || "custom"),
    from_as_of: String(presetLike?.from_as_of || "").trim(),
    to_as_of: String(presetLike?.to_as_of || "").trim(),
    sort_by: String(presetLike?.sort_by || "as_of"),
    sort_order: String(presetLike?.sort_order || "desc"),
  });
  const currentCompareFilterState = normalizeCompareFilterState({
    preset_a_label: simCompareHistoryPresetAFilter,
    preset_b_label: simCompareHistoryPresetBFilter,
    note_contains: simCompareHistoryNoteFilter,
    min_delta_market_value: simCompareHistoryMinDeltaMvFilter,
    min_delta_unrealized_pl: simCompareHistoryMinDeltaPlFilter,
    quick_range: simCompareHistoryQuickRange,
    from_as_of: simCompareHistoryFromAsOfFilter,
    to_as_of: simCompareHistoryToAsOfFilter,
    sort_by: simCompareHistorySortBy,
    sort_order: simCompareHistorySortOrder,
  });
  const loadedCompareFilterPreset = simCompareFilterPresets.find(
    (preset) => String(preset?.name || "").trim() === simSelectedCompareFilterPreset
  ) || null;
  const loadedCompareFilterState = normalizeCompareFilterState(loadedCompareFilterPreset || {});
  const hasLoadedCompareFilterPreset = Boolean(loadedCompareFilterPreset);
  const hasUnsavedCompareFilterChanges = hasLoadedCompareFilterPreset
    && JSON.stringify(currentCompareFilterState) !== JSON.stringify(loadedCompareFilterState);
  const compareApplied = simCompareHistoryAppliedFilters || {};
  const compareHistoryFilterChips = [
    compareApplied.preset_a_label ? `Preset A: ${compareApplied.preset_a_label}` : null,
    compareApplied.preset_b_label ? `Preset B: ${compareApplied.preset_b_label}` : null,
    compareApplied.window ? `Window: ${compareApplied.window}` : null,
    compareApplied.from_as_of ? `From: ${compareApplied.from_as_of}` : null,
    compareApplied.to_as_of ? `To: ${compareApplied.to_as_of}` : null,
    compareApplied.note_contains ? `Note: ${compareApplied.note_contains}` : null,
    compareApplied.min_delta_market_value !== null && compareApplied.min_delta_market_value !== undefined
      ? `Min |Delta MV|: ${compareApplied.min_delta_market_value}`
      : null,
    compareApplied.min_delta_unrealized_pl !== null && compareApplied.min_delta_unrealized_pl !== undefined
      ? `Min |Delta P/L|: ${compareApplied.min_delta_unrealized_pl}`
      : null,
  ].filter(Boolean);
  const compareHistoryPresetStateChips = [
    hasLoadedCompareFilterPreset ? `Loaded Preset: ${simSelectedCompareFilterPreset}` : null,
    hasUnsavedCompareFilterChanges ? "Preset Changes: Unsaved" : null,
  ].filter(Boolean);
  const hasActiveCompareHistoryFilters = compareHistoryFilterChips.length > 0;
  const compareHistoryTotalCount = Number(simCompareHistoryCounts?.totalCount ?? simCompareHistory.length);
  const compareHistoryFilteredCount = Number(simCompareHistoryCounts?.filteredCount ?? simCompareHistory.length);
  const compareHistoryLoadedCount = Number(simCompareHistoryCounts?.count ?? simCompareHistory.length);
  const compareHistoryIsLimited = compareHistoryLoadedCount < compareHistoryFilteredCount;
  const compareHistorySummaryMessage = hasActiveCompareHistoryFilters
    ? compareHistoryIsLimited
      ? `Showing ${compareHistoryLoadedCount} loaded rows of ${compareHistoryFilteredCount} matching comparison rows (${compareHistoryTotalCount} total).`
      : `Showing ${compareHistoryFilteredCount} of ${compareHistoryTotalCount} comparison rows.`
    : compareHistoryIsLimited
      ? `Showing ${compareHistoryLoadedCount} of ${compareHistoryTotalCount} comparison rows.`
      : `Showing ${compareHistoryTotalCount} comparison rows.`;
  const compareFilterUsageSummaryMessage = summarizeCompareFilterUsage();
  const usageRowByName = simCompareFilterPresetUsageRows.reduce((acc, row, index) => {
    const key = String(row?.name || "").trim().toLowerCase();
    if (key) {
      acc[key] = { row, index };
    }
    return acc;
  }, {});
  let compareFilterPresetOptions = simCompareFilterPresets.filter((preset) => {
    if (simCompareFilterPresetUsageViewMode !== "never_used") {
      if (simCompareTagFilter) {
        return (preset?.tags || []).includes(simCompareTagFilter);
      }
      return true;
    }
    const key = String(preset?.name || "").trim().toLowerCase();
    if (!Boolean(usageRowByName[key])) return false;
    if (simCompareTagFilter) {
      return (preset?.tags || []).includes(simCompareTagFilter);
    }
    return true;
  });
  compareFilterPresetOptions = compareFilterPresetOptions.sort((a, b) => {
    const aKey = String(a?.name || "").trim().toLowerCase();
    const bKey = String(b?.name || "").trim().toLowerCase();
    const aOrder = usageRowByName[aKey]?.index;
    const bOrder = usageRowByName[bKey]?.index;
    if (aOrder === undefined && bOrder === undefined) {
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    }
    if (aOrder === undefined) {
      return 1;
    }
    if (bOrder === undefined) {
      return -1;
    }
    return aOrder - bOrder;
  });
  if (
    simSelectedCompareFilterPreset
    && !compareFilterPresetOptions.some(
      (preset) => String(preset?.name || "").trim() === simSelectedCompareFilterPreset
    )
  ) {
    const selectedPreset = simCompareFilterPresets.find(
      (preset) => String(preset?.name || "").trim() === simSelectedCompareFilterPreset
    );
    if (selectedPreset) {
      compareFilterPresetOptions = [...compareFilterPresetOptions, selectedPreset];
    }
  }

  const selectNamedValueOrEmpty = (selectedValue, options) => {
    const normalized = String(selectedValue || "").trim();
    if (!normalized) return "";
    const exists = (Array.isArray(options) ? options : []).some(
      (entry) => String(entry?.name || "").trim() === normalized
    );
    return exists ? normalized : "";
  };

  const safeSelectedCompareSortPreset = selectNamedValueOrEmpty(simSelectedCompareSortPreset, simCompareSortPresets);
  const safeSelectedCompareViewPreset = selectNamedValueOrEmpty(simSelectedCompareViewPreset, simCompareViewPresets);
  const safeSelectedCompareExportProfile = selectNamedValueOrEmpty(simSelectedCompareExportProfile, simCompareExportProfiles);
  const safeSelectedCompareFilterPreset = selectNamedValueOrEmpty(
    simSelectedCompareFilterPreset,
    compareFilterPresetOptions
  );
  const safeSelectedCompareFilterExportProfile = selectNamedValueOrEmpty(
    simSelectedCompareFilterExportProfile,
    simCompareFilterExportProfiles
  );
  const compareHistoryPageSizeOptions = [5, 10, 20, 50];
  const safeCompareHistoryPageSize = compareHistoryPageSizeOptions.includes(Number(simCompareHistoryPageSize))
    ? Number(simCompareHistoryPageSize)
    : 10;

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          {/* Header */}
          <Stack
            direction={isMobile ? "column" : "row"}
            sx={{
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
            }}
            mb={2}
            spacing={isMobile ? 2 : 0}
          >
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: 22, sm: 28 } }}>
                Portfolio
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: 14, sm: 16 } }}>
                Positions, exposure, and P/L analytics.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setRetryCount((c) => c + 1)}
              disabled={loading}
              sx={{ width: isMobile ? "100%" : "auto", minHeight: 40 }}
            >
              Refresh
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          {loading && !analytics && (
            <Stack spacing={2} mt={2}>
              <Skeleton variant="rectangular" height={80} />
              <Skeleton variant="rectangular" height={200} />
            </Stack>
          )}

          {!loading && !error && analytics && (
            <>
              {/* KPI Cards */}
              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={2}
                sx={{ mt: 2, flexWrap: "wrap" }}
              >
                <KPICard
                  label="Market Value"
                  value={`$${fmt(analytics.total_market_value)}`}
                  sub={`Cost basis: $${fmt(analytics.total_cost_basis)}`}
                />
                <KPICard
                  label="Unrealized P/L"
                  value={`${analytics.total_unrealized_pl >= 0 ? "+" : ""}$${fmt(analytics.total_unrealized_pl)}`}
                  sub={`${analytics.total_return_pct >= 0 ? "+" : ""}${fmt(analytics.total_return_pct)}% total return`}
                  highlight={plColor}
                />
                <KPICard
                  label="Realized P/L"
                  value={`$${fmt(analytics.realized_pl)}`}
                  sub="Closed positions"
                />
                <KPICard
                  label="Positions"
                  value={analytics.positions.length}
                  sub={`${analytics.sleeve_breakdown.length} sleeve(s)`}
                />
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Positions Table */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Positions
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Hover or tap a ticker to preview its 6-month trend chart.
              </Typography>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell>Symbol</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Original cost of acquiring the position" arrow>
                            <span>Cost Basis</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Current market value" arrow>
                            <span>Mkt Value</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Unrealized profit/loss" arrow>
                            <span>Unreal. P/L</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">Return %</TableCell>
                        <TableCell align="right">Weight %</TableCell>
                        <TableCell>Sleeve</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.positions.map((pos) => (
                        <TableRow key={pos.symbol} hover>
                          <TableCell>
                            <SymbolHoverChart symbol={pos.symbol} />
                          </TableCell>
                          <TableCell align="right">{pos.quantity}</TableCell>
                          <TableCell align="right">${fmt(pos.cost_basis)}</TableCell>
                          <TableCell align="right">${fmt(pos.market_value)}</TableCell>
                          <TableCell align="right">
                            <PLChip value={pos.unrealized_pl} />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color:
                                pos.return_pct >= 0
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                              fontWeight: 600,
                            }}
                          >
                            {pos.return_pct >= 0 ? "+" : ""}
                            {fmt(pos.return_pct)}%
                          </TableCell>
                          <TableCell align="right">{fmt(pos.weight_pct)}%</TableCell>
                          <TableCell>
                            <Chip label={pos.sleeve} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Risk Summary */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Risk Governance
              </Typography>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2}>
                <KPICard
                  label="Largest Position"
                  value={`${fmt(riskSummary?.largest_position_weight_pct ?? 0)}%`}
                  sub={`Limit ${fmt(riskSummary?.policy?.max_single_position_weight_pct ?? 0)}%`}
                />
                <KPICard
                  label="Largest Sleeve"
                  value={`${fmt(riskSummary?.largest_sleeve_weight_pct ?? 0)}%`}
                  sub={`Limit ${fmt(riskSummary?.policy?.max_sleeve_weight_pct ?? 0)}%`}
                />
                <KPICard
                  label="Largest Sector"
                  value={`${fmt(riskSummary?.largest_sector_weight_pct ?? 0)}%`}
                  sub={`${riskSummary?.largest_sector_name || "Unknown"} vs limit ${fmt(riskSummary?.policy?.max_sector_weight_pct ?? 0)}%`}
                />
                <KPICard
                  label="Drawdown vs Limit"
                  value={`${fmt(riskSummary?.max_drawdown_pct ?? 0)}%`}
                  sub={`Limit ${fmt(riskSummary?.policy?.max_drawdown_pct ?? 0)}%`}
                />
              </Stack>
              <Alert
                severity={riskSummary?.within_limits ? "success" : "warning"}
                sx={{ mb: 1 }}
              >
                {riskSummary?.within_limits
                  ? "Portfolio risk checks are currently within policy limits."
                  : `Risk policy breaches: ${(riskSummary?.breaches || []).join(", ")}`}
              </Alert>

              <Divider sx={{ my: 3 }} />

              {/* Runtime Health */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Autonomous Runtime Health
              </Typography>
              {brokerSyncDetails && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Last Broker Sync Status
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Outcome: {brokerSyncDetails.synced ? "success" : "fallback"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last reason: {brokerSyncDetails.equityReason || "n/a"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Last sync: {brokerSyncDetails.asOf || "not yet run"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Sync age: {formatSyncAge(brokerSyncDetails.asOf)}
                  </Typography>
                </Paper>
              )}
              <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={2}>
                <Button
                  variant="outlined"
                  onClick={handleBrokerSync}
                  disabled={loading || brokerSyncLoading}
                >
                  {brokerSyncLoading ? "Syncing Broker History..." : "Sync Broker History"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBackfillHistory}
                  disabled={loading || backfillLoading}
                >
                  {backfillLoading ? "Backfilling History..." : "Backfill History"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRunMaintenance}
                  disabled={loading || maintenanceRunLoading}
                >
                  {maintenanceRunLoading ? "Running Maintenance..." : "Run Maintenance"}
                </Button>
              </Stack>
              {brokerSyncMessage && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {brokerSyncMessage}
                </Alert>
              )}
              {backfillMessage && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {backfillMessage}
                </Alert>
              )}
              {maintenanceRunMessage && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {maintenanceRunMessage}
                </Alert>
              )}
              {brokerSyncDetails && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Equity points synced: {brokerSyncDetails.equityPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Trade rows synced: {brokerSyncDetails.tradeRows}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Equity sync reason: {brokerSyncDetails.equityReason}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Trade sync reason: {brokerSyncDetails.tradeReason}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Sync as of: {brokerSyncDetails.asOf || "n/a"}
                  </Typography>
                </Paper>
              )}
              <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2}>
                <KPICard
                  label="History Rows"
                  value={`${historyRows}`}
                  sub={`Cap ${historyMaxRows}`}
                />
                <KPICard
                  label="History Utilization"
                  value={`${fmt(historyUtilizationPct)}%`}
                  sub="Rows used vs cap"
                />
                <KPICard
                  label="History DB Size"
                  value={`${fmt(dbSizeMb)} MB`}
                  sub="SQLite footprint"
                />
              </Stack>
              <Alert severity={runtimeHealthy ? "success" : "warning"} sx={{ mb: 1 }}>
                {runtimeHealthy
                  ? "Runtime retention is healthy for unattended operation."
                  : "Runtime retention is nearing capacity. Consider tightening history caps in runtime limits."}
              </Alert>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Last maintenance: {maintenanceStatus?.last_run_at || "not yet run"}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Readiness Gate */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Readiness Gate
              </Typography>
              {readinessGate ? (
                <>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={readinessGate.ready ? "READY" : "NOT READY"}
                      color={readinessGate.ready ? "success" : "error"}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Score: <strong>{readinessGate.score_pct}%</strong>
                    </Typography>
                    {Array.isArray(readinessGate.blockers) && readinessGate.blockers.length > 0 && (
                      <Typography variant="body2" color="error.main">
                        Blockers: {readinessGate.blockers.join(", ")}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} mb={1} sx={{ flexWrap: "wrap" }}>
                    {readinessGate.checks && Object.entries(readinessGate.checks).map(([key, passed]) => (
                      <Chip
                        key={key}
                        label={key.replace(/_/g, " ")}
                        color={passed ? "success" : "error"}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Stack>
                  {readinessGate.metrics && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Shadow pass rate: {readinessGate.metrics.shadow_pass_rate_pct}%
                      &nbsp;·&nbsp; History: {readinessGate.metrics.history_utilization_pct}%
                      &nbsp;·&nbsp; DB: {readinessGate.metrics.history_db_size_mb} MB
                      &nbsp;·&nbsp; Providers OK: {readinessGate.metrics.provider_ok}/{readinessGate.metrics.provider_total}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    As of: {readinessGate.as_of || "unknown"}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Readiness gate data unavailable.
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={readinessSnapshotLoading}
                onClick={async () => {
                  setReadinessSnapshotLoading(true);
                  setReadinessSnapshotMessage("");
                  try {
                    const result = await snapshotReadinessGate();
                    if (result?.snapshot) setReadinessGate(result.snapshot);
                    refreshReadiness();
                    setReadinessSnapshotMessage("Snapshot saved.");
                  } catch {
                    setReadinessSnapshotMessage("Snapshot failed.");
                  } finally {
                    setReadinessSnapshotLoading(false);
                  }
                }}
                sx={{ mb: 1 }}
              >
                Take Snapshot
              </Button>
              {readinessSnapshotMessage && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {readinessSnapshotMessage}
                </Typography>
              )}

              {/* Readiness Gate Trend */}
              {readinessTrend && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} mt={2} mb={0.5}>
                    Score Trend
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                    <Chip
                      label={readinessTrend.trend_direction === "up" ? "↑ Improving" : readinessTrend.trend_direction === "down" ? "↓ Declining" : "→ Flat"}
                      color={readinessTrend.trend_direction === "up" ? "success" : readinessTrend.trend_direction === "down" ? "error" : "default"}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {readinessTrend.count} snapshot{readinessTrend.count !== 1 ? "s" : ""} · {readinessTrend.blocker_transitions?.length ?? 0} blocker transition{(readinessTrend.blocker_transitions?.length ?? 0) !== 1 ? "s" : ""}
                    </Typography>
                  </Stack>
                  {Array.isArray(readinessTrend.score_series) && readinessTrend.score_series.length >= 2 && (
                    <Box sx={{ mb: 1, overflow: "hidden" }}>
                      <svg
                        aria-label="Readiness score sparkline"
                        width="100%"
                        viewBox="0 0 200 40"
                        preserveAspectRatio="none"
                        style={{ display: "block", height: 40 }}
                      >
                        <polyline
                          fill="none"
                          stroke={readinessTrend.trend_direction === "up" ? "#2e7d32" : readinessTrend.trend_direction === "down" ? "#c62828" : "#1565c0"}
                          strokeWidth="2"
                          points={readinessTrend.score_series.map((pt, i) => {
                            const x = (i / (readinessTrend.score_series.length - 1)) * 200;
                            const y = 40 - (pt.score_pct / 100) * 38 - 1;
                            return `${x},${y}`;
                          }).join(" ")}
                        />
                      </svg>
                    </Box>
                  )}
                </>
              )}

              {/* Readiness Gate History */}
              {readinessHistory && Array.isArray(readinessHistory.history) && readinessHistory.history.length > 0 && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} mt={2} mb={0.5}>
                    Readiness History
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 220, overflow: "auto" }}>
                    <Table size="small" aria-label="Readiness history table">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell>As Of</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Score %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...readinessHistory.history].reverse().map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{row.as_of ?? "—"}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.ready ? "READY" : "NOT READY"}
                                color={row.ready ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{row.score_pct ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Typography variant="subtitle2" fontWeight={600} mt={2} mb={0.5}>
                Anomaly Signals
              </Typography>
              {portfolioAnomalySignals.length === 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  No anomaly signals at this time.
                </Typography>
              ) : (
                <Stack spacing={0.75} mb={1}>
                  {portfolioAnomalySignals.slice(0, 8).map((signal, idx) => (
                    <Stack
                      key={`${signal?.id || "anomaly"}-${idx}`}
                      direction={isMobile ? "column" : "row"}
                      spacing={1}
                      sx={{ alignItems: isMobile ? "flex-start" : "center" }}
                    >
                      <Chip
                        size="small"
                        color={signal?.severity === "high" ? "error" : signal?.severity === "medium" ? "warning" : "default"}
                        label={String(signal?.severity || "info").toUpperCase()}
                      />
                      <Typography variant="caption" color="text.primary">
                        {signal?.title || "Unnamed signal"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {signal?.detail || ""}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Typography variant="subtitle2" fontWeight={600} mt={2} mb={0.5}>
                Readiness Alerts
              </Typography>
              {readinessAlerts.length === 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  No readiness alerts emitted yet.
                </Typography>
              ) : (
                <Stack spacing={0.75} mb={1}>
                  {readinessAlerts.slice().reverse().slice(0, 6).map((row, idx) => (
                    <Stack
                      key={`${row?.timestamp || "alert"}-${idx}`}
                      direction={isMobile ? "column" : "row"}
                      spacing={1}
                      sx={{ alignItems: isMobile ? "flex-start" : "center" }}
                    >
                      <Chip
                        size="small"
                        color={row?.event_type === "readiness_recovered" ? "success" : "warning"}
                        label={row?.event_type || "readiness_event"}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {row?.timestamp || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        score {Number(row?.score_pct ?? 0).toFixed(1)}%
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={readinessAlertTestLoading}
                  onClick={handleSendReadinessTestAlert}
                >
                  Send Test Alert
                </Button>
                {readinessAlertTestMessage && (
                  <Typography variant="caption" color="text.secondary">
                    {readinessAlertTestMessage}
                  </Typography>
                )}
              </Stack>

              <Typography variant="subtitle2" fontWeight={600} mt={2} mb={0.5}>
                Readiness Actions
              </Typography>
              {readinessActions.length === 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  No readiness actions recorded yet.
                </Typography>
              ) : (
                <Stack spacing={0.75} mb={1}>
                  {readinessActions.slice().reverse().slice(0, 6).map((row, idx) => (
                    <Stack
                      key={`${row?.as_of || "action"}-${idx}`}
                      direction={isMobile ? "column" : "row"}
                      spacing={1}
                      sx={{ alignItems: isMobile ? "flex-start" : "center" }}
                    >
                      <Chip
                        size="small"
                        color={row?.status === "executed" ? "success" : row?.status === "dry_run" ? "warning" : "default"}
                        label={row?.status || "unknown"}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {row?.event_type || row?.reason || "n/a"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row?.as_of || ""}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Equity Curve */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Equity Curve (30D)
              </Typography>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2}>
                <KPICard
                  label="Period Return"
                  value={`${historySummary?.period_return_pct >= 0 ? "+" : ""}${fmt(historySummary?.period_return_pct ?? 0)}%`}
                  sub="From first to last point"
                />
                <KPICard
                  label="Max Drawdown"
                  value={`${fmt(historySummary?.max_drawdown_pct ?? 0)}%`}
                  sub="Peak-to-trough decline"
                />
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                {history.length >= 2 ? (
                  <EquitySparkline data={history} />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Not enough data points to render chart.
                  </Typography>
                )}
              </Paper>
              <Stack direction="row" sx={{ justifyContent: "flex-end" }} mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      await exportPortfolioHistoryCsv(syncDays);
                    } catch {
                      setError("Failed to export equity history CSV.");
                    }
                  }}
                >
                  Export CSV
                </Button>
              </Stack>

              {/* Sleeve Breakdown */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Sleeve Breakdown
              </Typography>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell>Sleeve</TableCell>
                        <TableCell align="right">Mkt Value</TableCell>
                        <TableCell align="right">Cost Basis</TableCell>
                        <TableCell align="right">Unreal. P/L</TableCell>
                        <TableCell align="right">Weight %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.sleeve_breakdown.map((row) => (
                        <TableRow key={row.sleeve} hover>
                          <TableCell>
                            <Chip label={row.sleeve} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">${fmt(row.market_value)}</TableCell>
                          <TableCell align="right">${fmt(row.cost_basis)}</TableCell>
                          <TableCell align="right">
                            <PLChip value={row.unrealized_pl} />
                          </TableCell>
                          <TableCell align="right">{fmt(row.weight_pct)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Divider sx={{ my: 3 }} />

              {/* Exposure & Contribution */}
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Exposure & Contribution
              </Typography>
              {analytics.sleeve_breakdown && analytics.sleeve_breakdown.length > 0 && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Sleeve Exposure
                  </Typography>
                  {analytics.sleeve_breakdown.map((row) => (
                    <Box key={row.sleeve} mb={1}>
                      <Stack direction="row" sx={{ justifyContent: "space-between", mb: 0.25 }}>
                        <Typography variant="caption">{row.sleeve}</Typography>
                        <Typography variant="caption" fontWeight={600}>{fmt(row.weight_pct)}%</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(row.weight_pct, 100)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              {exposure?.by_sector && exposure.by_sector.length > 0 && (
                <Box sx={{ width: "100%", overflowX: "auto" }} mb={2}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Sector Exposure
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell>Sector</TableCell>
                          <TableCell align="right">Mkt Value</TableCell>
                          <TableCell align="right">Weight %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exposure.by_sector.map((row) => (
                          <TableRow key={row.sector} hover>
                            <TableCell>{row.sector}</TableCell>
                            <TableCell align="right">${fmt(row.market_value)}</TableCell>
                            <TableCell align="right">{fmt(row.weight_pct)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {analytics.positions && analytics.positions.length > 0 && (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    P/L Contribution by Position
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell>Symbol</TableCell>
                          <TableCell align="right">Unreal. P/L</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Position unrealized P/L as % of total portfolio cost basis" arrow>
                              <span>Contribution %</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">Weight %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...analytics.positions]
                          .sort((a, b) => Math.abs(b.pl_contribution_pct ?? 0) - Math.abs(a.pl_contribution_pct ?? 0))
                          .map((pos) => (
                            <TableRow key={pos.symbol} hover>
                              <TableCell>
                                <SymbolHoverChart symbol={pos.symbol} />
                              </TableCell>
                              <TableCell align="right">
                                <PLChip value={pos.unrealized_pl} />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color:
                                    (pos.pl_contribution_pct ?? 0) >= 0
                                      ? theme.palette.success.main
                                      : theme.palette.error.main,
                                  fontWeight: 600,
                                }}
                              >
                                {(pos.pl_contribution_pct ?? 0) >= 0 ? "+" : ""}
                                {fmt(pos.pl_contribution_pct ?? 0)}%
                              </TableCell>
                              <TableCell align="right">{fmt(pos.weight_pct)}%</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  What-If Trade Simulator
                </Typography>
                <Stack direction={isMobile ? "column" : "row"} spacing={1.5} mb={1.5}>
                  <TextField
                    label="Preset Name"
                    size="small"
                    value={simPresetName}
                    onChange={(e) => setSimPresetName(e.target.value)}
                    sx={{ minWidth: 170 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="sim-preset-select-label">Load Preset</InputLabel>
                    <Select
                      labelId="sim-preset-select-label"
                      value={simSelectedPreset}
                      label="Load Preset"
                      onChange={(e) => handleLoadPreset(e.target.value)}
                    >
                      {simPresets.map((preset) => (
                        <MenuItem key={preset.name} value={preset.name}>{preset.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={handleSavePreset} disabled={!simPresetName.trim() || !simSymbol.trim()}>
                    Save Preset
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleDeletePreset} disabled={!simSelectedPreset}>
                    Delete Preset
                  </Button>
                </Stack>
                <Stack direction={isMobile ? "column" : "row"} spacing={1.5} mb={1.5}>
                  <TextField
                    label="Symbol"
                    size="small"
                    value={simSymbol}
                    onChange={(e) => setSimSymbol(e.target.value.toUpperCase())}
                    sx={{ minWidth: 110 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="sim-action-label">Action</InputLabel>
                    <Select
                      labelId="sim-action-label"
                      value={simAction}
                      label="Action"
                      onChange={(e) => setSimAction(e.target.value)}
                    >
                      <MenuItem value="buy">Buy</MenuItem>
                      <MenuItem value="sell">Sell</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Quantity"
                    size="small"
                    type="number"
                    value={simQuantity}
                    onChange={(e) => setSimQuantity(Number(e.target.value))}
                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                    sx={{ minWidth: 130 }}
                  />
                  <TextField
                    label="Price"
                    size="small"
                    type="number"
                    value={simPrice}
                    onChange={(e) => setSimPrice(Number(e.target.value))}
                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                    sx={{ minWidth: 130 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleWhatIfSimulation}
                    disabled={simLoading || !simSymbol.trim()}
                  >
                    {simLoading ? "Simulating..." : "Run What-If"}
                  </Button>
                </Stack>
                {simPresetMessage && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {simPresetMessage}
                  </Alert>
                )}
                {simResult && (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Projected Market Value: ${fmt(simResult?.projected?.total_market_value ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Projected Unrealized P/L: {simResult?.projected?.total_unrealized_pl >= 0 ? "+" : ""}${fmt(simResult?.projected?.total_unrealized_pl ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Portfolio Delta (MV): {simResult?.delta?.market_value >= 0 ? "+" : ""}${fmt(simResult?.delta?.market_value ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Portfolio Delta (P/L): {simResult?.delta?.unrealized_pl >= 0 ? "+" : ""}${fmt(simResult?.delta?.unrealized_pl ?? 0)}
                    </Typography>
                  </Paper>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Preset Comparison (A/B)
                  </Typography>
                  <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="sim-compare-a-label">Compare Preset A</InputLabel>
                      <Select
                        labelId="sim-compare-a-label"
                        value={simComparePresetA}
                        label="Compare Preset A"
                        onChange={(e) => setSimComparePresetA(e.target.value)}
                      >
                        {simPresets.map((preset) => (
                          <MenuItem key={`a-${preset.name}`} value={preset.name}>{preset.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id="sim-compare-b-label">Compare Preset B</InputLabel>
                      <Select
                        labelId="sim-compare-b-label"
                        value={simComparePresetB}
                        label="Compare Preset B"
                        onChange={(e) => setSimComparePresetB(e.target.value)}
                      >
                        {simPresets.map((preset) => (
                          <MenuItem key={`b-${preset.name}`} value={preset.name}>{preset.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleComparePresets} disabled={simCompareLoading}>
                      {simCompareLoading ? "Comparing..." : "Compare Presets"}
                    </Button>
                  </Stack>
                  {simCompareResult && (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {simCompareResult?.scenario_a?.label || "Scenario A"}: Delta MV {Number(simCompareResult?.scenario_a?.result?.delta?.market_value ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.scenario_a?.result?.delta?.market_value ?? 0)} | Delta P/L {Number(simCompareResult?.scenario_a?.result?.delta?.unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.scenario_a?.result?.delta?.unrealized_pl ?? 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {simCompareResult?.scenario_b?.label || "Scenario B"}: Delta MV {Number(simCompareResult?.scenario_b?.result?.delta?.market_value ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.scenario_b?.result?.delta?.market_value ?? 0)} | Delta P/L {Number(simCompareResult?.scenario_b?.result?.delta?.unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.scenario_b?.result?.delta?.unrealized_pl ?? 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Delta-of-Deltas (B-A) MV: {Number(simCompareResult?.delta_of_deltas?.market_value ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.delta_of_deltas?.market_value ?? 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Delta-of-Deltas (B-A) P/L: {Number(simCompareResult?.delta_of_deltas?.unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmt(simCompareResult?.delta_of_deltas?.unrealized_pl ?? 0)}
                      </Typography>
                    </Paper>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Compare View Preset Name"
                        size="small"
                        value={simCompareViewPresetName}
                        onChange={(e) => setSimCompareViewPresetName(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sim-compare-view-preset-label">Saved Compare Views</InputLabel>
                        <Select
                          labelId="sim-compare-view-preset-label"
                          value={safeSelectedCompareViewPreset}
                          label="Saved Compare Views"
                          onChange={(e) => setSimSelectedCompareViewPreset(e.target.value)}
                        >
                          {simCompareViewPresets.map((preset) => (
                            <MenuItem key={`compare-view-${preset.name}`} value={preset.name}>{preset.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" size="small" onClick={handleSaveCompareHistoryViewPreset}>
                        Save Compare View
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleLoadCompareHistoryViewPreset}>
                        Load Compare View
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleDeleteCompareHistoryViewPreset}>
                        Delete Compare View
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleCloneCompareHistoryViewPreset}>
                        Clone Compare View
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRenameCompareHistoryViewPreset}>
                        Rename Compare View
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRestoreCompareHistoryViewPreset} disabled={simCompareViewPresetArchive.length === 0}>
                        Restore Last View Preset
                      </Button>
                      {simCompareViewPresetArchive.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          {simCompareViewPresetArchive.length} archived
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Purge View Archive Before (ISO date)"
                        size="small"
                        value={simCompareViewPresetPurgeDeletedBefore}
                        onChange={(e) => setSimCompareViewPresetPurgeDeletedBefore(e.target.value)}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="outlined" size="small" color="warning" onClick={handlePurgeCompareHistoryViewPresetArchive}>
                        Purge View Preset Archive
                      </Button>
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareViewPresets.length === 0}
                        onClick={handleExportCompareViewPresets}
                      >
                        Export View Presets
                      </Button>
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                      >
                        Import View Presets
                        <input
                          type="file"
                          accept="application/json,.json"
                          hidden
                          data-testid="compare-view-presets-import-input"
                          onChange={handleImportCompareViewPresetsFile}
                        />
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="sim-compare-view-import-mode-label">View Import Mode</InputLabel>
                        <Select
                          labelId="sim-compare-view-import-mode-label"
                          value={simCompareViewImportMode}
                          label="View Import Mode"
                          onChange={(e) => setSimCompareViewImportMode(e.target.value)}
                        >
                          <MenuItem value="merge">Merge</MenuItem>
                          <MenuItem value="replace">Replace</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={simCompareViewDryRunImport}
                            onChange={(e) => { setSimCompareViewDryRunImport(e.target.checked); setSimCompareViewDryRunResult(null); }}
                          />
                        }
                        label="Dry-Run View Import"
                      />
                    </Stack>
                    {simCompareViewDryRunResult && (
                      <Typography variant="caption" color="info.main" display="block" sx={{ mb: 1 }}>
                        {`View dry-run result (${simCompareViewImportMode === "replace" ? "REPLACE" : "MERGE"}): would import ${Number(simCompareViewDryRunResult.would_import_count ?? 0)} (create ${Number(simCompareViewDryRunResult.would_create_count ?? 0)}, update ${Number(simCompareViewDryRunResult.would_update_count ?? 0)}, skip ${Number(simCompareViewDryRunResult.would_skip_count ?? 0)}). No changes were written.`}
                      </Typography>
                    )}
                    {simCompareViewImportErrors.length > 0 && (
                      <Box sx={{ mb: 1 }} data-testid="view-import-error-detail">
                        <Typography variant="caption" color="error" display="block">
                          {`View import errors (${simCompareViewImportErrors.length}):`}
                        </Typography>
                        {simCompareViewImportErrors.map((err, i) => (
                          <Typography key={i} variant="caption" color="error" display="block">
                            {`[${err.index ?? i}] ${err.name || "(unnamed)"}: ${err.error}${err.field ? ` [${err.field}]` : ""}${err.value ? ` = ${err.value}` : ""}`}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {/* View preset usage telemetry */}
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1} sx={{ flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareViewPresetUsageEnabled}
                            onChange={(e) => handleToggleCompareViewPresetUsageEnabled(e.target.checked)}
                          />
                        )}
                        label="Track View Usage"
                      />
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareViewPresetUsageVisible}
                            onChange={(e) => setSimCompareViewPresetUsageVisible(e.target.checked)}
                          />
                        )}
                        label="Show View Usage"
                      />
                      {["most_used", "recently_used", "never_used"].map((mode) => (
                        <Chip
                          key={mode}
                          label={mode.replace("_", " ")}
                          size="small"
                          variant={simCompareViewPresetUsageViewMode === mode ? "filled" : "outlined"}
                          color={simCompareViewPresetUsageViewMode === mode ? "primary" : "default"}
                          onClick={() => {
                            setSimCompareViewPresetUsageViewMode(mode);
                            getWhatIfCompareViewPresetUsage({ includeZeroUsage: true, viewMode: mode })
                              .then((rows) => setSimCompareViewPresetUsageRows(buildCompareViewPresetUsageRows(rows, simCompareViewPresets, mode)))
                              .catch(() => {});
                          }}
                        />
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleResetCompareViewPresetUsage("")}
                        disabled={simCompareViewPresetUsageRows.filter((r) => Number(r?.total_count ?? 0) > 0).length === 0}
                        data-testid="reset-all-view-usage-btn"
                      >
                        Reset All View Usage
                      </Button>
                    </Stack>
                    {simCompareViewPresetUsageVisible && simCompareViewPresetUsageRows.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {buildCompareViewPresetUsageRows(simCompareViewPresetUsageRows, simCompareViewPresets, simCompareViewPresetUsageViewMode).map((row) => (
                          <Stack key={row.name} direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                            <Typography variant="caption" sx={{ minWidth: 120 }}>{row.name}</Typography>
                            <Chip label={`${row.total_count} uses`} size="small" color={row.total_count > 0 ? "primary" : "default"} />
                            {row.last_used_at && (
                              <Typography variant="caption" color="text.secondary">
                                {`Last: ${new Date(row.last_used_at).toLocaleDateString()}`}
                              </Typography>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              color="warning"
                              sx={{ minWidth: 0, p: 0.5 }}
                              onClick={() => handleResetCompareViewPresetUsage(row.name)}
                              disabled={row.total_count === 0}
                            >
                              Reset
                            </Button>
                          </Stack>
                        ))}
                      </Box>
                    )}
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Compare Export Profile Name"
                        size="small"
                        value={simCompareExportProfileName}
                        onChange={(e) => setSimCompareExportProfileName(e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="sim-compare-export-profile-label">Saved Export Profiles</InputLabel>
                        <Select
                          labelId="sim-compare-export-profile-label"
                          value={safeSelectedCompareExportProfile}
                          label="Saved Export Profiles"
                          onChange={(e) => setSimSelectedCompareExportProfile(e.target.value)}
                        >
                          {simCompareExportProfiles.map((profile) => (
                            <MenuItem key={`compare-export-${profile.name}`} value={profile.name}>{profile.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" size="small" onClick={handleSaveCompareHistoryExportProfile}>
                        Save Export Profile
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleLoadCompareHistoryExportProfile}>
                        Load Export Profile
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleDeleteCompareHistoryExportProfile}>
                        Delete Export Profile
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleCloneCompareHistoryExportProfile}>
                        Clone Export Profile
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRenameCompareHistoryExportProfile}>
                        Rename Export Profile
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRestoreCompareHistoryExportProfile} disabled={simCompareExportProfileArchive.length === 0}>
                        Restore Last Export Profile
                      </Button>
                      {simCompareExportProfileArchive.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          {simCompareExportProfileArchive.length} archived
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Purge Archive Before (ISO date)"
                        size="small"
                        value={simCompareExportProfilePurgeDeletedBefore}
                        onChange={(e) => setSimCompareExportProfilePurgeDeletedBefore(e.target.value)}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="outlined" size="small" color="warning" onClick={handlePurgeCompareHistoryExportProfileArchive}>
                        Purge Export Profile Archive
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareExportProfiles.length === 0}
                        onClick={handleExportCompareHistoryExportProfiles}
                      >
                        Export Profiles
                      </Button>
                      <FeatureGate capability="compare_export_profiles" mode="hide">
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                        >
                          Import Profiles
                          <input
                            type="file"
                            accept="application/json,.json"
                            hidden
                            data-testid="compare-export-profiles-import-input"
                            onChange={handleImportCompareHistoryExportProfilesFile}
                          />
                        </Button>
                      </FeatureGate>
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Compare Filter Preset Name"
                        size="small"
                        value={simCompareFilterPresetName}
                        onChange={(e) => setSimCompareFilterPresetName(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <TextField
                        label="Tags (comma-separated)"
                        size="small"
                        value={simCompareFilterPresetTagInput}
                        onChange={(e) => setSimCompareFilterPresetTagInput(e.target.value)}
                        placeholder="e.g. desk, risk, eod"
                        sx={{ minWidth: 200 }}
                      />
                      {simCompareFilterTags.length > 0 && (
                        <>
                          <Chip
                            label="All"
                            size="small"
                            color={!simCompareTagFilter ? "primary" : "default"}
                            variant={!simCompareTagFilter ? "filled" : "outlined"}
                            onClick={() => handleSelectCompareTagFilter("")}
                          />
                          {simCompareFilterTags.map((tag) => (
                            <Chip
                              key={`tag-filter-${tag}`}
                              label={tag}
                              size="small"
                              color={simCompareTagFilter === tag ? "primary" : "default"}
                              variant={simCompareTagFilter === tag ? "filled" : "outlined"}
                              onClick={() => handleSelectCompareTagFilter(tag)}
                            />
                          ))}
                        </>
                      )}
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sim-compare-filter-preset-label">Saved Compare Filters</InputLabel>
                        <Select
                          labelId="sim-compare-filter-preset-label"
                          value={safeSelectedCompareFilterPreset}
                          label="Saved Compare Filters"
                          onChange={(e) => setSimSelectedCompareFilterPreset(e.target.value)}
                        >
                          {compareFilterPresetOptions.map((preset) => (
                            <MenuItem key={`compare-filter-${preset.name}`} value={preset.name}>
                              {getCompareFilterPresetUsageLabel(preset.name)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Chip
                        label="Most Used"
                        color={simCompareFilterPresetUsageViewMode === "most_used" ? "primary" : "default"}
                        variant={simCompareFilterPresetUsageViewMode === "most_used" ? "filled" : "outlined"}
                        onClick={() => handleSelectCompareFilterPresetUsageViewMode("most_used")}
                      />
                      <Chip
                        label="Recent"
                        color={simCompareFilterPresetUsageViewMode === "recently_used" ? "primary" : "default"}
                        variant={simCompareFilterPresetUsageViewMode === "recently_used" ? "filled" : "outlined"}
                        onClick={() => handleSelectCompareFilterPresetUsageViewMode("recently_used")}
                      />
                      <Chip
                        label="Unused"
                        color={simCompareFilterPresetUsageViewMode === "never_used" ? "primary" : "default"}
                        variant={simCompareFilterPresetUsageViewMode === "never_used" ? "filled" : "outlined"}
                        onClick={() => handleSelectCompareFilterPresetUsageViewMode("never_used")}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                        {compareFilterUsageSummaryMessage}
                      </Typography>
                      <Button variant="outlined" size="small" onClick={handleSaveCompareHistoryFilterPreset}>
                        Save Compare Filter
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleLoadCompareHistoryFilterPreset}>
                        Load Compare Filter
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleLoadAndApplyCompareHistoryFilterPreset}>
                        Load + Apply Compare Filter
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleUpdateLoadedCompareHistoryFilterPreset}
                        disabled={!hasLoadedCompareFilterPreset || !hasUnsavedCompareFilterChanges}
                      >
                        Update Loaded Preset
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleResetCompareHistoryFilterPresetUsage}>
                        Reset Usage
                      </Button>
                      <TextField
                        label="Unused Since (ISO)"
                        size="small"
                        value={simCompareFilterUnusedSince}
                        onChange={(e) => setSimCompareFilterUnusedSince(e.target.value)}
                        placeholder="2026-04-01T00:00:00Z"
                        sx={{ minWidth: 210 }}
                      />
                      <Button variant="outlined" size="small" onClick={handleDeleteUnusedCompareHistoryFilterPresets}>
                        Delete Unused
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDeleteUnusedSinceCompareHistoryFilterPresets}
                      >
                        Delete Unused Since...
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareFilterPresetArchive.length === 0}
                        onClick={handleRestoreCompareHistoryFilterPreset}
                      >
                        Restore Last Deleted
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        disabled={simCompareFilterPresetArchive.length === 0}
                        onClick={handlePurgeCompareHistoryFilterPresetArchive}
                      >
                        Purge Archive
                      </Button>
                      {simCompareFilterPresetArchive.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          {`${simCompareFilterPresetArchive.length} in archive`}
                        </Typography>
                      )}
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="sim-compare-export-scope-label">Export Scope</InputLabel>
                        <Select
                          labelId="sim-compare-export-scope-label"
                          value={simCompareFilterExportScope}
                          label="Export Scope"
                          onChange={(e) => setSimCompareFilterExportScope(e.target.value)}
                        >
                          <MenuItem value="all">All Presets</MenuItem>
                          <MenuItem value="selected_tag">Selected Tag</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Filter Export Profile Name"
                        size="small"
                        value={simCompareFilterExportProfileName}
                        onChange={(e) => setSimCompareFilterExportProfileName(e.target.value)}
                        placeholder="Desk Export"
                        sx={{ minWidth: 200 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="sim-compare-filter-export-profile-select-label">Saved Filter Export Profiles</InputLabel>
                        <Select
                          labelId="sim-compare-filter-export-profile-select-label"
                          value={safeSelectedCompareFilterExportProfile}
                          label="Saved Filter Export Profiles"
                          onChange={(e) => {
                            setSimSelectedCompareFilterExportProfile(e.target.value);
                            if (e.target.value) {
                              setSimCompareFilterExportProfileName(e.target.value);
                            }
                          }}
                        >
                          {simCompareFilterExportProfiles.map((profile) => (
                            <MenuItem key={profile.name} value={profile.name}>
                              {profile.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" size="small" onClick={handleSaveCompareHistoryFilterExportProfile}>
                        Save Export Scope
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!simSelectedCompareFilterExportProfile}
                        onClick={handleLoadCompareHistoryFilterExportProfile}
                      >
                        Load Export Scope
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        disabled={!simSelectedCompareFilterExportProfile}
                        onClick={handleDeleteCompareHistoryFilterExportProfile}
                      >
                        Delete Export Scope
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!simSelectedCompareFilterExportProfile}
                        onClick={handleCloneCompareHistoryFilterExportProfile}
                      >
                        Clone Export Scope
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!simSelectedCompareFilterExportProfile}
                        onClick={handleRenameCompareHistoryFilterExportProfile}
                      >
                        Rename Export Scope
                      </Button>
                      <TextField
                        label="Export Archive Purge Before (ISO)"
                        size="small"
                        value={simCompareFilterExportArchiveDeletedBefore}
                        onChange={(e) => setSimCompareFilterExportArchiveDeletedBefore(e.target.value)}
                        placeholder="2026-04-01T00:00:00Z"
                        sx={{ minWidth: 235 }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareFilterExportProfileArchive.length === 0}
                        onClick={handleRestoreCompareHistoryFilterExportProfile}
                      >
                        Restore Last Export Scope
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        disabled={simCompareFilterExportProfileArchive.length === 0}
                        onClick={handlePurgeCompareHistoryFilterExportProfileArchive}
                      >
                        Purge Export Scope Archive
                      </Button>
                      {simCompareFilterExportProfileArchive.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          {`${simCompareFilterExportProfileArchive.length} export scope${simCompareFilterExportProfileArchive.length === 1 ? "" : "s"} in archive`}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareFilterExportProfiles.length === 0}
                        onClick={handleExportCompareHistoryFilterExportProfiles}
                      >
                        Export Scope Profiles
                      </Button>
                      <FeatureGate capability="compare_filter_export_profiles" mode="hide">
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                        >
                          Import Scope Profiles
                          <input
                            type="file"
                            accept="application/json,.json"
                            hidden
                            onChange={handleImportCompareHistoryFilterExportProfilesFile}
                          />
                        </Button>
                      </FeatureGate>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareFilterPresets.length === 0}
                        onClick={handleExportCompareHistoryFilterPresets}
                      >
                        Export Presets
                      </Button>
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                      >
                        Import Presets
                        <input
                          type="file"
                          accept="application/json,.json"
                          hidden
                          onChange={handleImportCompareHistoryFilterPresetsFile}
                        />
                      </Button>
                      <TextField
                        label="Import Tag"
                        size="small"
                        value={simCompareImportTag}
                        onChange={(e) => setSimCompareImportTag(e.target.value)}
                        placeholder="optional"
                        sx={{ minWidth: 140 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="sim-compare-import-mode-label">Import Mode</InputLabel>
                        <Select
                          labelId="sim-compare-import-mode-label"
                          value={simCompareImportMode}
                          label="Import Mode"
                          onChange={(e) => setSimCompareImportMode(e.target.value)}
                        >
                          <MenuItem value="merge">Merge</MenuItem>
                          <MenuItem value="replace">Replace</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="sim-compare-import-tag-mode-label">Import Tag Mode</InputLabel>
                        <Select
                          labelId="sim-compare-import-tag-mode-label"
                          value={simCompareImportTagMode}
                          label="Import Tag Mode"
                          onChange={(e) => setSimCompareImportTagMode(e.target.value)}
                        >
                          <MenuItem value="append">Append Tag</MenuItem>
                          <MenuItem value="replace">Replace Tags</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareStrictImport}
                            onChange={(e) => setSimCompareStrictImport(e.target.checked)}
                          />
                        )}
                        label="Strict Tag Validation"
                      />
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareDryRunImport}
                            onChange={(e) => {
                              setSimCompareDryRunImport(e.target.checked);
                              if (!e.target.checked) setSimCompareDryRunResult(null);
                            }}
                          />
                        )}
                        label="Dry-Run Import"
                      />
                      <Button variant="outlined" size="small" onClick={handleDeleteCompareHistoryFilterPreset}>
                        Delete Compare Filter
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleCloneCompareHistoryFilterPreset}>
                        Clone Compare Filter
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRenameCompareHistoryFilterPreset}>
                        Rename Compare Filter
                      </Button>
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareFilterPresetUsageVisible}
                            onChange={(e) => setSimCompareFilterPresetUsageVisible(e.target.checked)}
                          />
                        )}
                        label="Show Usage Counts"
                      />
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareFilterPresetUsageEnabled}
                            onChange={(e) => handleToggleCompareFilterPresetUsageEnabled(e.target.checked)}
                          />
                        )}
                        label="Track Usage"
                      />
                    </Stack>
                    {simCompareImportValidationSummary && (
                      <Alert
                        severity={simCompareImportValidationSummary.tagErrorCount > 0 ? "warning" : "info"}
                        sx={{ mb: 1 }}
                      >
                        {`Import preview: ${simCompareImportValidationSummary.presetCount} preset${simCompareImportValidationSummary.presetCount === 1 ? "" : "s"}, ${simCompareImportValidationSummary.tagErrorCount} tag issue${simCompareImportValidationSummary.tagErrorCount === 1 ? "" : "s"}`}
                        {simCompareImportValidationSummary.tagErrorCount > 0
                          ? ` (blank: ${simCompareImportValidationSummary.blankTagCount}, duplicates: ${simCompareImportValidationSummary.duplicateTagCount}, too long: ${simCompareImportValidationSummary.tooLongTagCount}, too many tags: ${simCompareImportValidationSummary.tooManyTagsCount}).`
                          : "."}
                      </Alert>
                    )}
                    {simCompareDryRunResult && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        {`Dry-run result (${simCompareImportMode === "replace" ? "REPLACE" : "MERGE"}): would import ${Number(simCompareDryRunResult.would_import_count ?? 0)} (create ${Number(simCompareDryRunResult.would_create_count ?? 0)}, update ${Number(simCompareDryRunResult.would_update_count ?? 0)}, skip ${Number(simCompareDryRunResult.would_skip_count ?? 0)}). No changes were written.`}
                      </Alert>
                    )}
                    {simCompareImportErrors.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }} data-testid="compare-import-error-detail">
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          {`${simCompareImportErrors.length} preset${simCompareImportErrors.length === 1 ? "" : "s"} skipped due to errors:`}
                        </Typography>
                        {simCompareImportErrors.map((e, i) => (
                          <Box key={i} sx={{ mt: 0.5, pl: 1, borderLeft: "2px solid", borderColor: "warning.main" }}>
                            <Typography variant="caption" display="block">
                              {`#${(e.index ?? i) + 1}${e.name ? ` "${e.name}"` : ""}: ${e.error}`}
                              {e.field ? ` [field: ${e.field}${e.value != null ? `, value: "${e.value}"` : ""}]` : ""}
                              {e.code ? ` (${e.code})` : ""}
                            </Typography>
                          </Box>
                        ))}
                      </Alert>
                    )}
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ mb: 1, alignItems: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="bulk-tag-action-label">Bulk Tag Action</InputLabel>
                        <Select
                          labelId="bulk-tag-action-label"
                          value={simCompareBulkTagAction}
                          label="Bulk Tag Action"
                          onChange={(e) => setSimCompareBulkTagAction(e.target.value)}
                        >
                          <MenuItem value="add">Add Tag</MenuItem>
                          <MenuItem value="remove">Remove Tag</MenuItem>
                          <MenuItem value="replace">Replace Tag</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Bulk Tag"
                        size="small"
                        value={simCompareBulkTagValue}
                        onChange={(e) => setSimCompareBulkTagValue(e.target.value)}
                        sx={{ minWidth: 130 }}
                      />
                      {simCompareBulkTagAction === "replace" && (
                        <TextField
                          label="Replace Tag With"
                          size="small"
                          value={simCompareBulkTagReplaceWith}
                          onChange={(e) => setSimCompareBulkTagReplaceWith(e.target.value)}
                          sx={{ minWidth: 130 }}
                        />
                      )}
                      {simCompareTagFilter && (
                        <Typography variant="caption" color="text.secondary">
                          {`Scope: tag '${simCompareTagFilter}'`}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleBulkTagCompareFilterPresets}
                      >
                        Apply Bulk Tag
                      </Button>
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Compare Sort Preset Name"
                        size="small"
                        value={simCompareSortPresetName}
                        onChange={(e) => setSimCompareSortPresetName(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sim-compare-sort-preset-label">Saved Compare Sorts</InputLabel>
                        <Select
                          labelId="sim-compare-sort-preset-label"
                          value={safeSelectedCompareSortPreset}
                          label="Saved Compare Sorts"
                          onChange={(e) => setSimSelectedCompareSortPreset(e.target.value)}
                        >
                          {simCompareSortPresets.map((preset) => (
                            <MenuItem key={`compare-sort-${preset.name}`} value={preset.name}>{preset.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" size="small" onClick={handleSaveCompareHistorySortPreset}>
                        Save Compare Sort
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleLoadCompareHistorySortPreset}>
                        Load Compare Sort
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleDeleteCompareHistorySortPreset}>
                        Delete Compare Sort
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleCloneCompareHistorySortPreset}>
                        Clone Compare Sort
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRenameCompareHistorySortPreset}>
                        Rename Compare Sort
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleRestoreCompareHistorySortPreset} disabled={simCompareSortPresetArchive.length === 0}>
                        Restore Last Sort Preset
                      </Button>
                      {simCompareSortPresetArchive.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                          {simCompareSortPresetArchive.length} archived
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <TextField
                        label="Purge Sort Archive Before (ISO date)"
                        size="small"
                        value={simCompareSortPresetPurgeDeletedBefore}
                        onChange={(e) => setSimCompareSortPresetPurgeDeletedBefore(e.target.value)}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="outlined" size="small" color="warning" onClick={handlePurgeCompareHistorySortPresetArchive}>
                        Purge Sort Preset Archive
                      </Button>
                    </Stack>
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={simCompareSortPresets.length === 0}
                        onClick={handleExportCompareSortPresets}
                      >
                        Export Sort Presets
                      </Button>
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                      >
                        Import Sort Presets
                        <input
                          type="file"
                          accept="application/json,.json"
                          hidden
                          data-testid="compare-sort-presets-import-input"
                          onChange={handleImportCompareSortPresetsFile}
                        />
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel id="sim-compare-sort-import-mode-label">Sort Import Mode</InputLabel>
                        <Select
                          labelId="sim-compare-sort-import-mode-label"
                          value={simCompareSortImportMode}
                          label="Sort Import Mode"
                          onChange={(e) => setSimCompareSortImportMode(e.target.value)}
                        >
                          <MenuItem value="merge">Merge</MenuItem>
                          <MenuItem value="replace">Replace</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={simCompareSortDryRunImport}
                            onChange={(e) => { setSimCompareSortDryRunImport(e.target.checked); setSimCompareSortDryRunResult(null); }}
                          />
                        }
                        label="Dry-Run Sort Import"
                      />
                    </Stack>
                    {simCompareSortDryRunResult && (
                      <Typography variant="caption" color="info.main" display="block" sx={{ mb: 1 }}>
                        {`Sort dry-run result (${simCompareSortImportMode === "replace" ? "REPLACE" : "MERGE"}): would import ${Number(simCompareSortDryRunResult.would_import_count ?? 0)} (create ${Number(simCompareSortDryRunResult.would_create_count ?? 0)}, update ${Number(simCompareSortDryRunResult.would_update_count ?? 0)}, skip ${Number(simCompareSortDryRunResult.would_skip_count ?? 0)}). No changes were written.`}
                      </Typography>
                    )}
                    {simCompareSortImportErrors.length > 0 && (
                      <Box sx={{ mb: 1 }} data-testid="sort-import-error-detail">
                        <Typography variant="caption" color="error" display="block">
                          {`Sort import errors (${simCompareSortImportErrors.length}):`}
                        </Typography>
                        {simCompareSortImportErrors.map((err, i) => (
                          <Typography key={i} variant="caption" color="error" display="block">
                            {`[${err.index ?? i}] ${err.name || "(unnamed)"}: ${err.error}${err.field ? ` [${err.field}]` : ""}${err.value ? ` = ${err.value}` : ""}`}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {/* Sort preset usage telemetry */}
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1} sx={{ flexWrap: "wrap" }}>
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareSortPresetUsageEnabled}
                            onChange={(e) => handleToggleCompareSortPresetUsageEnabled(e.target.checked)}
                          />
                        )}
                        label="Track Sort Usage"
                      />
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={simCompareSortPresetUsageVisible}
                            onChange={(e) => setSimCompareSortPresetUsageVisible(e.target.checked)}
                          />
                        )}
                        label="Show Sort Usage"
                      />
                      {["most_used", "recently_used", "never_used"].map((mode) => (
                        <Chip
                          key={mode}
                          label={mode.replace("_", " ")}
                          size="small"
                          variant={simCompareSortPresetUsageViewMode === mode ? "filled" : "outlined"}
                          color={simCompareSortPresetUsageViewMode === mode ? "primary" : "default"}
                          onClick={() => {
                            setSimCompareSortPresetUsageViewMode(mode);
                            getWhatIfCompareSortPresetUsage({ includeZeroUsage: true, viewMode: mode })
                              .then((rows) => setSimCompareSortPresetUsageRows(buildCompareSortPresetUsageRows(rows, simCompareSortPresets, mode)))
                              .catch(() => {});
                          }}
                        />
                      ))}
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleResetCompareSortPresetUsage("")}
                        disabled={simCompareSortPresetUsageRows.filter((r) => Number(r?.total_count ?? 0) > 0).length === 0}
                        data-testid="reset-all-sort-usage-btn"
                      >
                        Reset All Sort Usage
                      </Button>
                    </Stack>
                    {simCompareSortPresetUsageVisible && simCompareSortPresetUsageRows.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {buildCompareSortPresetUsageRows(simCompareSortPresetUsageRows, simCompareSortPresets, simCompareSortPresetUsageViewMode).map((row) => (
                          <Stack key={row.name} direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                            <Typography variant="caption" sx={{ minWidth: 120 }}>{row.name}</Typography>
                            <Chip label={`${row.total_count} uses`} size="small" color={row.total_count > 0 ? "primary" : "default"} />
                            {row.last_used_at && (
                              <Typography variant="caption" color="text.secondary">
                                {`Last: ${new Date(row.last_used_at).toLocaleDateString()}`}
                              </Typography>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              color="warning"
                              sx={{ minWidth: 0, p: 0.5 }}
                              onClick={() => handleResetCompareSortPresetUsage(row.name)}
                              disabled={row.total_count === 0}
                            >
                              Reset
                            </Button>
                          </Stack>
                        ))}
                      </Box>
                    )}
                    <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sim-compare-quick-range-label">Compare Range</InputLabel>
                        <Select
                          labelId="sim-compare-quick-range-label"
                          value={simCompareHistoryQuickRange}
                          label="Compare Range"
                          onChange={(e) => setSimCompareHistoryQuickRange(e.target.value)}
                        >
                          <MenuItem value="custom">Custom</MenuItem>
                          <MenuItem value="24h">Last 24 Hours</MenuItem>
                          <MenuItem value="7d">Last 7 Days</MenuItem>
                          <MenuItem value="30d">Last 30 Days</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="sim-compare-sort-by-label">Compare Sort By</InputLabel>
                        <Select
                          labelId="sim-compare-sort-by-label"
                          value={simCompareHistorySortBy}
                          label="Compare Sort By"
                          onChange={(e) => setSimCompareHistorySortBy(e.target.value)}
                        >
                          <MenuItem value="as_of">As Of</MenuItem>
                          <MenuItem value="delta_market_value">Delta MV</MenuItem>
                          <MenuItem value="delta_unrealized_pl">Delta P/L</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 190 }}>
                        <InputLabel id="sim-compare-sort-order-label">Compare Sort Order</InputLabel>
                        <Select
                          labelId="sim-compare-sort-order-label"
                          value={simCompareHistorySortOrder}
                          label="Compare Sort Order"
                          onChange={(e) => setSimCompareHistorySortOrder(e.target.value)}
                        >
                          <MenuItem value="desc">Descending</MenuItem>
                          <MenuItem value="asc">Ascending</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Compare History Preset A"
                        size="small"
                        value={simCompareHistoryPresetAFilter}
                        onChange={(e) => setSimCompareHistoryPresetAFilter(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <TextField
                        label="Compare History Preset B"
                        size="small"
                        value={simCompareHistoryPresetBFilter}
                        onChange={(e) => setSimCompareHistoryPresetBFilter(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <TextField
                        label="Filter by Note"
                        size="small"
                        value={simCompareHistoryNoteFilter}
                        onChange={(e) => setSimCompareHistoryNoteFilter(e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <TextField
                        label="Min |Delta MV|"
                        size="small"
                        type="number"
                        value={simCompareHistoryMinDeltaMvFilter}
                        onChange={(e) => setSimCompareHistoryMinDeltaMvFilter(e.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: "any" } }}
                        sx={{ minWidth: 160 }}
                      />
                      <TextField
                        label="Min |Delta P/L|"
                        size="small"
                        type="number"
                        value={simCompareHistoryMinDeltaPlFilter}
                        onChange={(e) => setSimCompareHistoryMinDeltaPlFilter(e.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: "any" } }}
                        sx={{ minWidth: 160 }}
                      />
                      <TextField
                        label="Compare History From"
                        size="small"
                        type="datetime-local"
                        value={simCompareHistoryFromAsOfFilter}
                        onChange={(e) => setSimCompareHistoryFromAsOfFilter(e.target.value)}
                        disabled={simCompareHistoryQuickRange !== "custom"}
                        sx={{ minWidth: 220 }}
                      />
                      <TextField
                        label="Compare History To"
                        size="small"
                        type="datetime-local"
                        value={simCompareHistoryToAsOfFilter}
                        onChange={(e) => setSimCompareHistoryToAsOfFilter(e.target.value)}
                        disabled={simCompareHistoryQuickRange !== "custom"}
                        sx={{ minWidth: 220 }}
                      />
                      <Button variant="outlined" size="small" onClick={handleApplyCompareHistoryFilters} disabled={simCompareHistoryLoading}>
                        Apply Compare Filters
                      </Button>
                      <Button variant="outlined" size="small" onClick={handleClearCompareHistoryFilters} disabled={simCompareHistoryLoading}>
                        Clear Compare Filters
                      </Button>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="sim-compare-export-mode-label">Compare Export Mode</InputLabel>
                        <Select
                          labelId="sim-compare-export-mode-label"
                          value={simCompareHistoryExportMode}
                          label="Compare Export Mode"
                          onChange={(e) => setSimCompareHistoryExportMode(e.target.value)}
                        >
                          <MenuItem value="all">All Filtered</MenuItem>
                          <MenuItem value="page">Current Page</MenuItem>
                        </Select>
                      </FormControl>
                      <Button variant="outlined" size="small" onClick={handleExportCompareHistory}>
                        Export Compare CSV
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Recent Comparison History
                    </Typography>
                    {simCompareFilterApplyBanner && (
                      <Alert severity="success" sx={{ mb: 1 }}>
                        {simCompareFilterApplyBanner}
                      </Alert>
                    )}
                    <Alert severity={hasActiveCompareHistoryFilters ? "info" : "success"} sx={{ mb: 1 }}>
                      {compareHistorySummaryMessage}
                    </Alert>
                    {(hasActiveCompareHistoryFilters || compareHistoryPresetStateChips.length > 0) && (
                      <Stack direction={isMobile ? "column" : "row"} spacing={0.75} sx={{ mb: 1 }}>
                        {compareHistoryFilterChips.map((chipLabel) => (
                          <Chip key={chipLabel} size="small" variant="outlined" label={chipLabel} />
                        ))}
                        {compareHistoryPresetStateChips.map((chipLabel) => (
                          <Chip key={chipLabel} size="small" variant="outlined" label={chipLabel} />
                        ))}
                      </Stack>
                    )}
                    {simCompareHistory.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        {hasActiveCompareHistoryFilters
                          ? "No comparison rows match current filters. Use Clear Compare Filters to reset and try again."
                          : "No comparison history yet."}
                      </Typography>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: "action.hover" }}>
                              <TableCell padding="checkbox" style={{ width: "40px" }} />
                              <TableCell>As Of</TableCell>
                              <TableCell>Preset A</TableCell>
                              <TableCell>Preset B</TableCell>
                              <TableCell align="right">Delta MV</TableCell>
                              <TableCell align="right">Delta P/L</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {simCompareHistory.map((row, idx) => {
                              const isExpanded = simCompareHistoryExpandedRows[idx] || false;
                              const posData = simCompareHistoryPositions[idx];
                              const isLoading = simCompareHistoryPositionsLoading[idx] || false;
                              const noteValue = simCompareHistoryNotes[idx] ?? row.note ?? "";
                              const isNoteSaving = simCompareHistoryNoteSaving[idx] || false;
                              return (
                                <React.Fragment key={`${row.as_of || "compare"}-${idx}`}>
                                  <TableRow hover>
                                    <TableCell padding="checkbox">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleToggleCompareHistoryRowExpand(idx)}
                                      >
                                        {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>{formatSyncAge(row.as_of)}</TableCell>
                                    <TableCell>{row.preset_a_label}</TableCell>
                                    <TableCell>{row.preset_b_label}</TableCell>
                                    <TableCell align="right">
                                      {(row.delta_market_value ?? 0) >= 0 ? "+" : ""}${fmt(row.delta_market_value ?? 0)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {(row.delta_unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmt(row.delta_unrealized_pl ?? 0)}
                                    </TableCell>
                                  </TableRow>
                                  {isExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={6} sx={{ py: 2 }}>
                                        {isLoading ? (
                                          <Stack spacing={1}>
                                            <Typography variant="body2">Loading position details...</Typography>
                                            <LinearProgress />
                                          </Stack>
                                        ) : (
                                          <Stack spacing={2}>
                                            {/* Inline Note */}
                                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                              <TextField
                                                label="Note"
                                                size="small"
                                                value={noteValue}
                                                onChange={(e) =>
                                                  setSimCompareHistoryNotes((prev) => ({
                                                    ...prev,
                                                    [idx]: e.target.value,
                                                  }))
                                                }
                                                slotProps={{ htmlInput: { maxLength: 500 } }}
                                                sx={{ flex: 1 }}
                                                placeholder="Add a note to this comparison…"
                                              />
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={isNoteSaving}
                                                onClick={() => handleSaveCompareHistoryNote(idx)}
                                              >
                                                {isNoteSaving ? "Saving…" : "Save Note"}
                                              </Button>
                                            </Stack>
                                            {/* Position Tables */}
                                            {posData ? (
                                              <>
                                                <Typography variant="subtitle2">
                                                  {posData.presetALabel} vs {posData.presetBLabel}
                                                </Typography>
                                                <Stack direction="row" spacing={4}>
                                                  {/* Preset A Positions */}
                                                  <Box flex={1}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                      {posData.presetALabel} Positions
                                                    </Typography>
                                                    <Table size="small" sx={{ mt: 1 }}>
                                                      <TableHead>
                                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                                          <TableCell>Symbol</TableCell>
                                                          <TableCell align="right">Qty</TableCell>
                                                          <TableCell align="right">MV</TableCell>
                                                          <TableCell align="right">P/L</TableCell>
                                                        </TableRow>
                                                      </TableHead>
                                                      <TableBody>
                                                        {posData.positionsA?.map((pos, pidx) => (
                                                          <TableRow key={pidx}>
                                                            <TableCell>{pos.symbol || "—"}</TableCell>
                                                            <TableCell align="right">{pos.quantity ?? 0}</TableCell>
                                                            <TableCell align="right">
                                                              ${fmt(pos.market_value ?? pos.marketValue ?? 0)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              ${fmt(pos.unrealized_pl ?? pos.unrealizedPl ?? 0)}
                                                            </TableCell>
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                  </Box>
                                                  {/* Preset B Positions */}
                                                  <Box flex={1}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                      {posData.presetBLabel} Positions
                                                    </Typography>
                                                    <Table size="small" sx={{ mt: 1 }}>
                                                      <TableHead>
                                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                                          <TableCell>Symbol</TableCell>
                                                          <TableCell align="right">Qty</TableCell>
                                                          <TableCell align="right">MV</TableCell>
                                                          <TableCell align="right">P/L</TableCell>
                                                        </TableRow>
                                                      </TableHead>
                                                      <TableBody>
                                                        {posData.positionsB?.map((pos, pidx) => (
                                                          <TableRow key={pidx}>
                                                            <TableCell>{pos.symbol || "—"}</TableCell>
                                                            <TableCell align="right">{pos.quantity ?? 0}</TableCell>
                                                            <TableCell align="right">
                                                              ${fmt(pos.market_value ?? pos.marketValue ?? 0)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              ${fmt(pos.unrealized_pl ?? pos.unrealizedPl ?? 0)}
                                                            </TableCell>
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                  </Box>
                                                </Stack>
                                              </>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">
                                                No position data available.
                                              </Typography>
                                            )}
                                          </Stack>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    {simCompareHistory.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={simCompareHistoryPage <= 1 || simCompareHistoryLoading}
                          onClick={() => {
                            const prevPage = simCompareHistoryPage - 1;
                            const quickWindow = simCompareHistoryQuickRange === "custom" ? undefined : simCompareHistoryQuickRange;
                            loadWhatIfCompareHistory({
                              limit: 20,
                              presetALabel: simCompareHistoryPresetAFilter.trim() || undefined,
                              presetBLabel: simCompareHistoryPresetBFilter.trim() || undefined,
                              fromAsOf: quickWindow ? undefined : simCompareHistoryFromAsOfFilter.trim() || undefined,
                              toAsOf: quickWindow ? undefined : simCompareHistoryToAsOfFilter.trim() || undefined,
                              window: quickWindow,
                              sortBy: simCompareHistorySortBy,
                              sortOrder: simCompareHistorySortOrder,
                              page: prevPage,
                              pageSize: simCompareHistoryPageSize,
                            });
                          }}
                        >
                          Prev
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Page {simCompareHistoryPage} of {simCompareHistoryTotalPages}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={simCompareHistoryPage >= simCompareHistoryTotalPages || simCompareHistoryLoading}
                          onClick={() => {
                            const nextPage = simCompareHistoryPage + 1;
                            const quickWindow = simCompareHistoryQuickRange === "custom" ? undefined : simCompareHistoryQuickRange;
                            loadWhatIfCompareHistory({
                              limit: 20,
                              presetALabel: simCompareHistoryPresetAFilter.trim() || undefined,
                              presetBLabel: simCompareHistoryPresetBFilter.trim() || undefined,
                              fromAsOf: quickWindow ? undefined : simCompareHistoryFromAsOfFilter.trim() || undefined,
                              toAsOf: quickWindow ? undefined : simCompareHistoryToAsOfFilter.trim() || undefined,
                              window: quickWindow,
                              sortBy: simCompareHistorySortBy,
                              sortOrder: simCompareHistorySortOrder,
                              page: nextPage,
                              pageSize: simCompareHistoryPageSize,
                            });
                          }}
                        >
                          Next
                        </Button>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel id="compare-history-page-size-label">Per page</InputLabel>
                          <Select
                            labelId="compare-history-page-size-label"
                            value={safeCompareHistoryPageSize}
                            label="Per page"
                            onChange={(e) => {
                              const newSize = Number(e.target.value);
                              setSimCompareHistoryPageSize(newSize);
                              const quickWindow = simCompareHistoryQuickRange === "custom" ? undefined : simCompareHistoryQuickRange;
                              loadWhatIfCompareHistory({
                                limit: 20,
                                presetALabel: simCompareHistoryPresetAFilter.trim() || undefined,
                                presetBLabel: simCompareHistoryPresetBFilter.trim() || undefined,
                                fromAsOf: quickWindow ? undefined : simCompareHistoryFromAsOfFilter.trim() || undefined,
                                toAsOf: quickWindow ? undefined : simCompareHistoryToAsOfFilter.trim() || undefined,
                                window: quickWindow,
                                sortBy: simCompareHistorySortBy,
                                sortOrder: simCompareHistorySortOrder,
                                page: 1,
                                pageSize: newSize,
                              });
                            }}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Stack>
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Recent What-If Runs
                  </Typography>
                  <Stack direction={isMobile ? "column" : "row"} spacing={1} mb={1}>
                    <TextField
                      label="History Symbol"
                      size="small"
                      value={simHistorySymbolFilter}
                      onChange={(e) => setSimHistorySymbolFilter(e.target.value.toUpperCase())}
                      sx={{ minWidth: 150 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="sim-history-action-filter-label">History Action</InputLabel>
                      <Select
                        labelId="sim-history-action-filter-label"
                        value={simHistoryActionFilter}
                        label="History Action"
                        onChange={(e) => setSimHistoryActionFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="buy">Buy</MenuItem>
                        <MenuItem value="sell">Sell</MenuItem>
                      </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleApplyRunHistoryFilters} disabled={simHistoryLoading}>
                      Apply Filters
                    </Button>
                    <Button variant="outlined" onClick={handleClearRunHistoryFilters} disabled={simHistoryLoading}>
                      Clear Filters
                    </Button>
                    <Button variant="outlined" onClick={handleExportRunHistory}>
                      Export CSV
                    </Button>
                  </Stack>
                  {simRunHistory.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No run history yet.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell>As Of</TableCell>
                            <TableCell>Preset</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Delta MV</TableCell>
                            <TableCell align="right">Delta P/L</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {simRunHistory.slice().reverse().map((row, idx) => (
                            <TableRow key={`${row.as_of || "run"}-${row.symbol || ""}-${idx}`} hover>
                              <TableCell>{formatSyncAge(row.as_of)}</TableCell>
                              <TableCell>{row.preset_name || "-"}</TableCell>
                              <TableCell>{String(row.action || "buy").toUpperCase()}</TableCell>
                              <TableCell>{row.symbol}</TableCell>
                              <TableCell align="right">{fmt(row.quantity ?? 0)}</TableCell>
                              <TableCell align="right">${fmt(row.price ?? 0)}</TableCell>
                              <TableCell align="right">
                                {(row.delta_market_value ?? 0) >= 0 ? "+" : ""}${fmt(row.delta_market_value ?? 0)}
                              </TableCell>
                              <TableCell align="right">
                                {(row.delta_unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmt(row.delta_unrealized_pl ?? 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            </>
          )}

          {!loading && !error && !analytics && (
            <Box mt={2} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No portfolio data available. Add positions to get started.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
};

export default PortfolioView;